import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FolderTree   from './components/FolderTree';
import MessageList, { isNoiseAttachment }  from './components/MessageList';
import ReadingPane  from './components/ReadingPane';
import ConfirmDialog from './components/ConfirmDialog';
import ContextMenu  from './components/ContextMenu';
import Icon         from './components/Icon';
import Login        from './components/Login';
import { ViewAccountButton, UploadInvoiceButton, BuildOrderButton } from './components/ReadingPane';
import ErrorBoundary     from './components/ErrorBoundary';
import { getImpersonate, exitImpersonation, withImpersonate, MAILBOX_SWITCHED_EVENT } from './lib/impersonate';
import { buildReplyOpener } from './lib/replyOpener';
import { signatureMemoryOfSend } from './lib/composeSignature';
import * as mailCache from './lib/mailCache';
import { planFreshLabelRetry } from './lib/labelRetry';
import { resolveCidImages, extractDataUriImages, oneShotCidFactory, deliverQuoteCidMap, MAX_INLINE_IMAGE_BYTES } from './lib/inlineImages';
import { lazyModal } from './lib/lazyModal';
import { LOCAL_INDEX_DAYS } from './lib/localIndex';
import { createBodyCache, withCachedBody } from './lib/bodyCache';
import { pageWindow, isPruneCandidate as pollPruneCandidate } from './lib/pollPrune';
import { reportError, apiRoute } from './lib/reportError';
import useFlaggedMail from './lib/useFlaggedMail';
import { isV2 } from './lib/uiVersion';
import styles from './App.module.css';

// Section 6 #1: code-split the heavy, conditionally-opened drawers/modals so
// they're not downloaded/parsed in the initial bundle. lazyModal wraps each in
// its own Suspense (fallback: nothing — the overlay just appears when its chunk
// lands, a one-time fetch on first open, cached thereafter). RENDER SITES ARE
// UNCHANGED — only these imports became lazy. Small, frequently-used components
// (ConfirmDialog, ContextMenu, Icon, Login, ReadingPane, MessageList) stay eager.
// lazyModal (code-split + REQUIRED ref-forwarding) lives in ./lib/lazyModal so
// its "must forwardRef or composeRef.current is null" contract is unit-tested.
const ComposeModal          = lazyModal(() => import('./components/ComposeModal'));
const OrphanDraftsModal     = lazyModal(() => import('./components/OrphanDraftsModal'));
const OrderDrawer           = lazyModal(() => import('./components/OrderDrawer'));
const LabelCorrectionDialog = lazyModal(() => import('./components/LabelCorrectionDialog'));
const AddLabelDialog        = lazyModal(() => import('./components/AddLabelDialog'));
const ReplyOpenersDialog    = lazyModal(() => import('./components/ReplyOpenersDialog'));
const AddSampleModal        = lazyModal(() => import('./components/AddSampleModal'));
const SendErrorsModal       = lazyModal(() => import('./components/SendErrorsModal'));
const AppErrorsModal        = lazyModal(() => import('./components/AppErrorsModal'));
const BodyLoadDebugModal    = lazyModal(() => import('./components/BodyLoadDebugModal'));
const AccountDrawer         = lazyModal(() => import('./components/AccountDrawer'));
const InvoiceUploadDrawer   = lazyModal(() => import('./components/InvoiceUploadDrawer'));
const CostSheetDrawer       = lazyModal(() => import('./components/CostSheetDrawer'));
const BuildOrderDrawer      = lazyModal(() => import('./components/BuildOrderDrawer'));
const OpenMailboxDialog     = lazyModal(() => import('./components/OpenMailboxDialog'));
const Rules                 = lazyModal(() => import('./pages/Rules'));
// Only mounts when an AI quote is opened — kept out of the startup bundle.
const AiQuotePanel          = lazyModal(() => import('./components/AiQuotePanel'));

// Safe JSON getter used by the localStorage rehydrations below. Swallows
// parse errors (corrupt entries) and returns the fallback so a bad cache
// never crashes the mount.
function loadLS(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}

// Top-level component. Resolves the auth state on mount, gates the mail
// UI behind /api/auth/me, and renders <Login /> when unauthenticated.
// Once signed in, the rest of the app (the original component, now
// renamed AuthedApp) mounts with the resolved user passed in. On 401
// from any deep API call we treat the session as expired and bounce
// the user back to the login screen.
// LoginComponent and Brand let the /V2 shell (src/v2/AppV2.js) swap in its
// own login screen and top-bar brand without the classic bundle ever
// importing them.
export default function App({ LoginComponent = Login, Brand = null }) {
  const [authStatus,  setAuthStatus]  = useState('loading'); // 'loading'|'unauthed'|'authed'
  const [currentUser, setCurrentUser] = useState(null);

  const refreshAuth = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
      if (res.status === 401) {
        setCurrentUser(null);
        setAuthStatus('unauthed');
        return;
      }
      const data = await res.json();
      setCurrentUser(data);
      setAuthStatus('authed');
    } catch {
      setCurrentUser(null);
      setAuthStatus('unauthed');
    }
  }, []);

  useEffect(() => { refreshAuth(); }, [refreshAuth]);

  // Switching mailbox from the sidebar (lib/impersonate.js switchMailbox, or
  // Back / Forward) changes no page: the mail UI below is rebuilt for the new
  // mailbox by remounting it under a new key — its folders, list, caches and
  // live-push stream all start fresh for that mailbox, while the sign-in above
  // (and its /me) is untouched.
  const [mailboxKey, setMailboxKey] = useState(() => getImpersonate()?.mailbox || 'own');
  useEffect(() => {
    const onSwitch = () => setMailboxKey(getImpersonate()?.mailbox || 'own');
    window.addEventListener(MAILBOX_SWITCHED_EVENT, onSwitch);
    return () => window.removeEventListener(MAILBOX_SWITCHED_EVENT, onSwitch);
  }, []);

  // The fetch interceptor in index.js fires this when any /api/* call
  // (other than /auth/login + /auth/me) returns 401 — typically because
  // the session cookie has expired mid-session. Drop back to the login
  // screen so the user can re-auth without a confusing blank UI.
  useEffect(() => {
    function onExpired() {
      setCurrentUser(null);
      setAuthStatus('unauthed');
    }
    window.addEventListener('pp:session-expired', onExpired);
    return () => window.removeEventListener('pp:session-expired', onExpired);
  }, []);

  async function handleLogout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch { /* ignore — we'll clear client state regardless */ }
    // Wipe per-user caches so the next sign-in doesn't briefly show the
    // previous user's mail before the new fetches land.
    try {
      localStorage.removeItem('pp_folders');
      localStorage.removeItem(START_FOLDER_KEY);
      localStorage.removeItem('pp_labelMap');
      localStorage.removeItem('pp_drafts'); // legacy shared key
      // Namespaced per-user draft stores (pp_drafts:<mailbox>) — clear them all
      // so no signed-out user's drafts linger on a shared browser profile.
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('pp_drafts:')) localStorage.removeItem(k);
      }
    } catch { /* ignore quota / serialisation errors */ }
    setCurrentUser(null);
    setAuthStatus('unauthed');
  }

  if (authStatus === 'loading') {
    // Avoid flashing the login form during the /api/auth/me round-trip.
    return <div style={{ height: '100vh', background: 'var(--bg)' }} />;
  }
  if (authStatus === 'unauthed' || !currentUser) {
    return <LoginComponent onSuccess={(user) => { setCurrentUser(user); setAuthStatus('authed'); }} />;
  }
  return (
    <AuthedApp
      key={mailboxKey}
      currentUser={currentUser}
      onLogout={handleLogout}
      Brand={Brand}
    />
  );
}

// fetch() for a write that is safe to repeat: setting a flag status or a read
// state to a given value lands the same whether it is sent once or twice.
//
// "Couldn't update flag/tick: Failed to fetch" (2026-10-07): the browser lost
// the connection and got NO response — while the server log shows the very same
// request answered 200 in 278 ms. The change had been applied; the UI rolled it
// back and showed an error. A dropped connection is retried once here, after a
// short pause, so a blip is invisible and a real outage still surfaces.
//
// ONLY a network-level rejection is retried (fetch rejects — there is no
// response at all). An HTTP error is an answer and is returned untouched.
// Never use this for a send: a repeated send is a second email.
async function fetchRepeatable(url, init, { retryDelayMs = 700 } = {}) {
  try {
    return await fetch(url, init);
  } catch (err) {
    if (init?.signal?.aborted) throw err;
    await new Promise(r => setTimeout(r, retryDelayMs));
    return fetch(url, init);
  }
}

// Stable identity for a physical message that does NOT depend on the Graph
// message id. Graph hands out two id namespaces (default folder-relative vs
// immutable), and a message cached under one can meet the server copy keyed
// under the other — so id-only dedup lets the same email show twice in search
// (one row from the local cache, one from the server). sender + subject +
// received timestamp is identical for the same message and differs for any
// two genuinely-distinct emails (the timestamp is full-precision, not the
// minute-rounded value shown in the UI).
function msgContentKey(m) {
  if (!m) return '';
  const norm = (s) => String(s || '').trim().toLowerCase();
  // Canonicalise the timestamp to an epoch, NOT the raw string. The same
  // physical message reaches us with two different serialisations depending
  // on the source: Graph's receivedDateTime ("2026-06-02T16:07:00Z") on
  // cache/Graph rows vs the local-FTS branch's Postgres received_at, which
  // node-postgres renders with milliseconds ("2026-06-02T16:07:00.000Z").
  // Comparing the strings byte-for-byte would make this key differ for the
  // SAME email and defeat the dedup; parsing to epoch makes both collapse to
  // the same instant. NaN (missing/unparseable) falls back to '' so two
  // dateless rows still compare on from+subject.
  const raw = m.date || m.receivedDateTime || '';
  const epoch = raw ? Date.parse(raw) : NaN;
  const when = Number.isNaN(epoch) ? '' : String(epoch);
  return `${norm(m.from)}|${norm(m.subject)}|${when}`;
}

// Fetch a message via the UNCAPPED ?inline=1 path — no 8s attachment-listing
// soft-cap, and inline contentBytes included. Returns the parsed message, or
// null on failure / non-ok. Single source of truth for the three callers that
// need the real attachment listing (reader auto-retry, ensureInlineBytes,
// openForward) so the endpoint/error contract lives in one place.
async function fetchInlineMessage(id) {
  try {
    const r = await fetch(`/api/mail/messages/${encodeURIComponent(id)}?inline=1`);
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

// Find a folder anywhere in the (possibly nested) folder tree by id. The
// folders payload nests child folders under `childFolders`, so a flat
// Array.find misses any selected subfolder.
function findFolderById(list, id) {
  if (!id || !Array.isArray(list)) return null;
  for (const f of list) {
    if (f.id === id) return f;
    const hit = f.childFolders ? findFolderById(f.childFolders, id) : null;
    if (hit) return hit;
  }
  return null;
}

// The folder the list opens on (the Inbox), remembered per mailbox from the
// last /folders answer. Starting from it, the list (IndexedDB paint + server
// fetch) begins the moment the app mounts instead of after /folders — a live
// Graph folder listing on the server, p99 2.9 s in the Oct 2026 perf log —
// has answered. Mailbox-scoped like pp_folders (lib/impersonate.js
// MAILBOX_SCOPED_KEYS: a shared mailbox keeps its own copy, an admin viewing a
// colleague keeps none), and also tagged with the mailbox it was learned for,
// so a folder id from one mailbox is never opened in another — not even when
// someone else signs in on this browser after a session simply expired.
const START_FOLDER_KEY = 'pp_start_folder';
function viewedMailbox(currentUser) {
  return String(getImpersonate()?.mailbox || currentUser?.mailbox || '').toLowerCase();
}
function cachedStartFolder(currentUser) {
  const c = loadLS(START_FOLDER_KEY, null);
  const owner = viewedMailbox(currentUser);
  return (owner && c && c.mailbox === owner && c.folder?.id) ? c.folder : null;
}

function AuthedApp({ currentUser, onLogout, Brand }) {
  const [folders,         setFolders]         = useState(() => loadLS('pp_folders', []));
  const [selectedFolder,  setSelectedFolder]  = useState(() => cachedStartFolder(currentUser));
  // The id the list started on from cache, so the /folders answer can tell
  // "still on the start folder" from "the person has already moved on".
  const startFolderIdRef = useRef(selectedFolder?.id || null);
  // Refs the (deps-[]) folders poll reads without re-subscribing, so it can
  // tell whether new unread landed in the folder the user is *currently*
  // viewing and, if so, pull that folder's message list in BEFORE bumping
  // the badge — otherwise the count appears next to Inbox up to a poll
  // interval before the email row itself shows up.
  const foldersRef         = useRef(folders);
  const selectedFolderRef  = useRef(null);
  const pollMessagesRef     = useRef(null);  // set by the messages-poll effect
  const messagesRef         = useRef([]);    // current list, for the poll's "gained a row?" answer
  const catchUpTimersRef    = useRef([]);    // short re-polls after the badge outran the list
  const refreshFoldersRef   = useRef(null);  // set by the folders-poll effect (used by the SSE push effect)
  const [messages,        setMessages]        = useState([]);
  const [selectedMsg,     setSelectedMsg]     = useState(null);
  const [loadingFolders,  setLoadingFolders]  = useState(true);
  const [loadingMsgs,     setLoadingMsgs]     = useState(false);
  const [searchQuery,     setSearchQuery]     = useState('');
  // Where the latest search results came from. 'local' = local FTS
  // index; 'graph' = Microsoft Graph (beyond the local index's LOCAL_INDEX_DAYS).
  // When 'local' AND user is searching, we surface a "Search older
  // mail" footer button that re-runs the same query against Graph.
  const [searchSource,    setSearchSource]    = useState(null);
  const [searchingOlder,  setSearchingOlder]  = useState(false);
  const [searching,       setSearching]       = useState(false);
  const [searchFocused,   setSearchFocused]   = useState(false);
  // Thread mode — populated when the user clicks the "Thread" pill on the
  // open email. Holds the seed metadata (normalised subject + counterpart
  // domain + open message id) so we can render a banner above the message
  // list. The actual messages live in `messages` like any other view; this
  // is purely a marker that the current list IS a thread view. Cleared by:
  //   - the Clear button on the banner
  //   - any subsequent folder load / search (loadMessages / search effect
  //     unset it explicitly to keep the banner from outliving its results)
  const [threadInfo,      setThreadInfo]      = useState(null);
  const [threadLoading,   setThreadLoading]   = useState(false);
  // Toggles the orphan-drafts review modal. Set by MessageList's
  // "Sweep orphans" pill (rendered only when viewing the Drafts
  // folder). The modal handles its own fetch + delete + close.
  const [orphanModalOpen, setOrphanModalOpen] = useState(false);
  // Snapshot of msgFilter at the moment thread mode was entered, so
  // Clear can restore it. Without this, loadThread's setMsgFilter('all')
  // persists to localStorage (the pp_filter effect writes on every
  // change), permanently overwriting a Flagged / Unread filter the user
  // had set in the inbox — they reported "my filter keeps resetting".
  // Null = no thread session active (don't restore on Clear).
  const priorMsgFilterRef = useRef(null);
  const [searchHistory,   setSearchHistory]   = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem('pp_search_history') || '[]');
      return Array.isArray(v) ? v.slice(0, 10) : [];
    } catch { return []; }
  });
  // When true the search excludes Sent Items folder server-side.
  // Defaults to FALSE — "Include sent items" starts TICKED, so search
  // shows the full conversation (both sides) by default. Matches
  // Outlook's own search behaviour. Users untick only when they want
  // the narrower "find what someone sent me" view, e.g. to filter out
  // their own replies in a noisy thread.
  //
  // Earlier default was true (exclude sent by default). That hid the
  // "Re: ..." reply for every thread the user had answered, so a
  // search like "totalmerchandise leather" returned the original
  // enquiry but dropped our quote-back. Reversing the burden so the
  // common case (see the whole conversation) needs no toggle.
  const [excludeSent,     setExcludeSent]     = useState(false);
  // True between "user typed something" and "server returned authoritative
  // results". The IndexedDB local-cache scan can populate the list during
  // this window, so we tag messages as a preview to avoid the user
  // committing to a stale-feeling result and being surprised when the
  // server response arrives and the order shifts.
  const [searchPreview,   setSearchPreview]   = useState(false);
  const [resolvedContacts, setResolvedContacts] = useState([]);
  const [composing,       setComposing]       = useState(false);
  const [composeDefaults, setComposeDefaults] = useState({});
  const [rulesOpen,       setRulesOpen]       = useState(false);
  const [openMailboxDlg,  setOpenMailboxDlg]  = useState(false);
  const [aiToast,         setAiToast]         = useState(false);
  // Phase 1c.7: AiQuotePanel renders in the reading-pane slot when set.
  // Holds the Graph message object (not just the id) so the panel can
  // pick up the source-email metadata for its header without a refetch.
  const [aiQuoteMsg,      setAiQuoteMsg]      = useState(null);
  // Minimised = panel stays MOUNTED (its draft edits survive) but hidden, and
  // a "Quote · …" tab appears in the strip to bring it back. Mirrors the
  // BuildOrderDrawer keep-mounted pattern.
  const [aiQuoteMinimised, setAiQuoteMinimised] = useState(false);
  const [actionError,     setActionError]     = useState(null);
  const [ctxMenu,         setCtxMenu]         = useState(null);
  // When truthy, render the Empty-Folder confirm dialog. Holds the
  // FOLDER OBJECT to empty (not just a boolean), so the right-click
  // entry point in FolderTree can target a folder that isn't the
  // currently-selected one. Falls back to selectedFolder for the
  // legacy message-list-button entry point.
  const [emptyConfirm,    setEmptyConfirm]    = useState(null);
  const [emptying,        setEmptying]        = useState(false);
  // Mobile layout state — on <=768px only one panel shows at a time. Desktop
  // ignores both. 'list' = message list, 'read' = reading pane. Hamburger
  // drawer is independent so the sidebar can overlay either pane.
  const [mobilePane,      setMobilePane]      = useState('list');
  const [sidebarOpen,     setSidebarOpen]     = useState(false);
  const [hasMore,         setHasMore]         = useState(false);
  const [loadingMore,     setLoadingMore]     = useState(false);
  const [todayCount,      setTodayCount]      = useState(null);
  // Seeded from the authenticated session. `currentUser` is guaranteed
  // truthy here — this component only mounts after authStatus = 'authed'.
  const [mailbox,         setMailbox]         = useState(currentUser?.mailbox || '');
  // True while the SSE stream is live. Drives the adaptive poll cadence: when
  // SSE is delivering pokes, the periodic polls drop to a slow backstop; if the
  // stream drops, they snap back to the fast cadence. Stays false when SSE is
  // disabled, so polling behaves exactly as before.
  const [sseConnected,    setSseConnected]    = useState(false);
  // Bumped when a message-list poll completes. Lets the badge override below
  // tell "gap just appeared" from "gap survived a whole poll" (= drift, not
  // new mail). badgeDeferRef remembers the gap it is currently deferring.
  const [pollGen, setPollGen] = useState(0);
  const badgeDeferRef = useRef(null);
  const [customers,       setCustomers]       = useState([]);
  const [loadingCustomers,setLoadingCustomers]= useState(true);
  const [selectedCustomer,setSelectedCustomer]= useState(null);
  const [selectedLabel,   setSelectedLabel]   = useState(null);
  const [labelMap,        setLabelMap]        = useState(() => loadLS('pp_labelMap', {})); // graph_message_id → label
  const [reactionMap,     setReactionMap]     = useState({});                                // graph_message_id → {count, ownerReactionType, ownerReactionTime} | null
  // Order lookup per message-id: graph_message_id →
  //   { orderNumber, customerOrderNumber, status, url } | null (no match)
  // | undefined (not yet fetched). Populated by an effect below that
  // runs whenever selectedMsg changes; survives the session in
  // memory only (no localStorage — orders move between statuses, so
  // a fresh lookup each session is safer than a stale cached one).
  const [orderLookupMap,  setOrderLookupMap]  = useState({});
  const [showSendErrors,  setShowSendErrors]  = useState(false);
  const [showAppErrors,   setShowAppErrors]   = useState(false);
  const [appErrorCount,   setAppErrorCount]   = useState(0);
  const [sendErrorCount,  setSendErrorCount]  = useState(0);
  const [accountDrawerEmail, setAccountDrawerEmail] = useState(null);
  const [accountDrawerCustomerId, setAccountDrawerCustomerId] = useState(null);
  // Which of the two stacked drawers (Order / Account) is on top.
  // Drives a swap on click: clicking the visible portion of the
  // drawer that's behind raises it to the front. Null when only one
  // (or neither) is open.
  const [topDrawer, setTopDrawer] = useState(null);
  const [invoiceUploadSource, setInvoiceUploadSource] = useState(null);
  const [costSheetOpenId, setCostSheetOpenId] = useState(null);
  const [orderDrawerNumber, setOrderDrawerNumber] = useState(null);
  // Build Order — multi-tab. Users can have several in-progress
  // orders going at once (one per anchor email), each surfaced as a
  // tab in the draft-tabs strip. Switching tabs swaps which drawer
  // is visible; all drawers stay MOUNTED in the DOM (via display:none)
  // so each tab's ticks/PO/renames/etc. survive switches without
  // re-fetching.
  //
  // buildOrders[] is the persistent list (one entry per open tab).
  // activeBuildOrderKey is whichever drawer is visible (null = none).
  // Per-order state lives INSIDE each drawer instance — React preserves
  // it across visibility flips because the drawer's key is stable.
  const [buildOrders, setBuildOrders] = useState([]);
  // Each entry: { key, anchorMessageId, anchorFallback, anchorSubject,
  //               anchorFromName, createdAt, updatedAt }
  const [activeBuildOrderKey, setActiveBuildOrderKey] = useState(null);

  function openBuildOrder(message) {
    // Re-clicking Build Order on a message that already has an open
    // tab just activates the existing tab (preserves all its state).
    const existing = buildOrders.find(o => o.anchorMessageId === message.id);
    if (existing) {
      setActiveBuildOrderKey(existing.key);
      return;
    }
    const toAddrs = Array.isArray(message.toAddresses)
      ? message.toAddresses.map(a => a?.email).filter(Boolean).join(', ')
      : '';
    const newOrder = {
      key:             `bo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      anchorMessageId: message.id,
      anchorFallback: {
        subject:        message.subject || '',
        from:           message.from || '',
        fromName:       message.fromName || '',
        to:             toAddrs,
        conversationId: message.conversationId || '',
        receivedAt:     message.date || null,
      },
      anchorSubject:   message.subject || '(no subject)',
      anchorFromName:  message.fromName || message.from || '',
      // Pre-tick the anchor — opening Build Order on this email IS the
      // implicit "tick this email" action. All OTHER emails are ticked
      // by the user via the inbox checkboxes. The drawer no longer
      // auto-shows or auto-ticks domain-matched candidates.
      tickedEmails: {
        [message.id]: { name: '', tickedAt: Date.now() },
      },
      createdAt:       Date.now(),
      updatedAt:       Date.now(),
    };
    setBuildOrders(prev => [...prev, newOrder]);
    setActiveBuildOrderKey(newOrder.key);
  }

  function discardBuildOrder(key) {
    setBuildOrders(prev => prev.filter(o => o.key !== key));
    setActiveBuildOrderKey(prev => (prev === key ? null : prev));
  }
  function openBuildOrderTab(key) { setActiveBuildOrderKey(key); }
  function closeActiveBuildOrder() { setActiveBuildOrderKey(null); }

  // tickedEmails: per-order map of { [messageId]: { name, tickedAt } }.
  // Mutated when the user clicks the MessageList checkbox AND when the
  // drawer's source-emails tickbox is clicked. The drawer reads this
  // (via the activeOrder) so its source-emails section reflects the
  // same selection.
  const activeOrder = buildOrders.find(o => o.key === activeBuildOrderKey) || null;

  function toggleActiveBuildOrderTick(messageId, name = null) {
    if (!activeBuildOrderKey) return;
    setBuildOrders(prev => prev.map(o => {
      if (o.key !== activeBuildOrderKey) return o;
      const current = o.tickedEmails || {};
      if (current[messageId]) {
        // Untick — remove the entry.
        const { [messageId]: _drop, ...rest } = current;
        return { ...o, tickedEmails: rest, updatedAt: Date.now() };
      }
      // Tick — add. Default name to the supplied value (from MessageList's
      // prompt) or an empty string (drawer ticks without a prompt).
      return {
        ...o,
        tickedEmails: {
          ...current,
          [messageId]: { name: name || '', tickedAt: Date.now() },
        },
        updatedAt: Date.now(),
      };
    }));
  }
  function renameActiveBuildOrderTick(messageId, newName) {
    if (!activeBuildOrderKey) return;
    setBuildOrders(prev => prev.map(o => {
      if (o.key !== activeBuildOrderKey) return o;
      const current = o.tickedEmails || {};
      if (!current[messageId]) return o;
      return {
        ...o,
        tickedEmails: {
          ...current,
          [messageId]: { ...current[messageId], name: newName },
        },
        updatedAt: Date.now(),
      };
    }));
  }
  // When the same customer-order-number is on multiple OnPoint orders
  // (e.g. one customer PO split across two of our orders), the lookup
  // ships every candidate so the OrderDrawer can render a Switch Order
  // picker. Same shape as AccountDrawer's `matches` array.
  const [orderDrawerMatches, setOrderDrawerMatches] = useState([]);
  // Close the cost-sheet / order drawers when the user opens a
  // different email — they're contextual to whichever message was
  // selected when they were opened, so leaving them stuck on the
  // previous email is confusing.
  useEffect(() => {
    if (selectedMsg?.id) {
      setCostSheetOpenId(null);
      setOrderDrawerNumber(null);
      setAccountDrawerEmail(null);
      setAccountDrawerCustomerId(null);
      setInvoiceUploadSource(null);
    }
    // eslint-disable-next-line
  }, [selectedMsg?.id]);
  // Bills drawer was retired in favour of the Account drawer; the
  // pending-bills counter is no longer rendered, so the polling
  // effect at line ~350 is now a no-op (kept for potential future
  // surfacing — does not affect render).
  const [, setXeroPendingCount] = useState(0);
  const [showAddLabel,    setShowAddLabel]    = useState(false);
  const [labelsRefreshKey, setLabelsRefreshKey] = useState(0);
  const [customLabels,    setCustomLabels]    = useState([]);
  // Sidebar Flags list (every flagged email, any folder) — lib/useFlaggedMail.js.
  const {
    list: flaggedList, more: flaggedMore, loaded: flaggedLoaded,
    refresh: refreshFlagged, noteFlag: noteFlagged,
  } = useFlaggedMail();
  const [sendToasts,      setSendToasts]      = useState([]);                               // { id, status, subject, draftKey, err }
  const [addingSample,    setAddingSample]    = useState(false);                            // true → render AddSampleModal over the reading pane
  const [activeSampleKey, setActiveSampleKey] = useState(null);                              // when set, AddSampleModal opens with restored state
  const [msgFilter,       setMsgFilter]       = useState(() => localStorage.getItem('pp_filter') || 'all');
  const [archiveFolders,  setArchiveFolders]  = useState([]); // online archive tree (empty if no archive)
  // Draft tabs: map of draftKey → saved compose state. Persisted in
  // localStorage — NAMESPACED per authenticated user so one person's drafts
  // (which carry their recipients) can't be reopened by the next user on a
  // shared browser profile. The old shared `pp_drafts` key was a cross-user
  // leak. (While impersonating, the impersonate shim suppresses writes to
  // pp_drafts* so an admin viewing a colleague never persists their drafts.)
  const draftsKey = `pp_drafts:${String(currentUser?.mailbox || '').toLowerCase()}`;
  const [drafts,          setDrafts]          = useState(() => {
    try {
      localStorage.removeItem('pp_drafts');   // retire the shared legacy key
      const raw = localStorage.getItem(draftsKey);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [activeDraftKey,  setActiveDraftKey]  = useState(null);
  // Mirror of activeDraftKey readable from async callbacks without going
  // stale (same render-time-assignment pattern as handleCloseRef). Used to
  // guard the background inline-image patch: a slow fetch must not patch a
  // reply the user has since closed or swapped for a different message.
  const activeDraftKeyRef = useRef(null);
  activeDraftKeyRef.current = activeDraftKey;
  // Body-load diagnostics. Every getMessage hydration records its timing +
  // outcome here so the top-bar Debug button can show what's slow when the
  // reading pane white-screens (long Graph fetch vs failed/aborted fetch).
  // Newest first, capped at 12.
  const [bodyDiag,      setBodyDiag]      = useState([]);
  const [showBodyDebug, setShowBodyDebug] = useState(false);
  const bodyDiagSeq = useRef(0);
  // In-progress samples: same model as drafts so they can flick between.
  // Keyed by `sample-<graphMessageId>`. Each value is a snapshot of the
  // AddSampleModal form state so re-opening restores everything (customer,
  // products, billable invoice lines, etc.).
  const [samples,         setSamples]         = useState(() => {
    try {
      const raw = localStorage.getItem('pp_samples');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  // Label correction dialog — populated with { msg, targetLabel } when the
  // user picks a new label from the right-click menu.
  const [labelCorrection, setLabelCorrection] = useState(null);
  // "Show external content" toggle — surfaced in the bottom-left
  // account menu. When true, the reading pane stops sanitising the
  // org-policy "blocked content" markup that Microsoft injects into
  // some emails (e.g. when an external image host isn't on the org's
  // allow list). Persists per-browser so the user doesn't have to
  // re-toggle every time they open a flagged email.
  const [showExternalContent, setShowExternalContent] = useState(() => {
    try { return localStorage.getItem('pp_extContent') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('pp_extContent', showExternalContent ? '1' : '0'); } catch {}
  }, [showExternalContent]);

  // (Display zoom is handled by the browser's native zoom — Ctrl+/Ctrl-/
  // Ctrl+0. Chrome and Firefox remember the chosen zoom per origin, so
  // users set it once per device. We deliberately do NOT apply CSS zoom
  // programmatically: it interacts badly with vh/vw/fixed-position and
  // every workaround we tried introduced a new layout bug. The
  // FolderTree account menu surfaces the keyboard shortcuts as a help
  // row so users know where to look.)
  // Sender info — { matchType: 'contact'|'customer'|'new', customer } —
  // looked up for the currently-viewed email. Lives at the App level
  // so the topbar's "New Contact" / "New Customer" badge AND the
  // ReadingPane's Add-to-OnPoint affordance both read from a single
  // source. Resolved by the useEffect below whenever selectedMsg.from
  // changes.
  const [senderInfo, setSenderInfo] = useState(null);
  // Surfaced separately so ReadingPane can mount the AddContactModal
  // without owning the lookup state.
  const [addingContact, setAddingContact] = useState(false);
  // "Update Contact" modal — opened from the topbar pill when the
  // sender's name near-matches an existing OnPoint contact whose email
  // has changed (senderInfo.updateCandidate is populated).
  const [updatingContact, setUpdatingContact] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(draftsKey, JSON.stringify(drafts)); } catch { /* quota or serialisation */ }
  }, [drafts, draftsKey]);
  useEffect(() => {
    try { localStorage.setItem('pp_samples', JSON.stringify(samples)); } catch { /* quota or serialisation */ }
  }, [samples]);

  // Stale-draft auto-prune. Drafts (partial reply / forward / new
  // email state stored in localStorage) accumulate when the user
  // starts a reply but later sends from a different client (Outlook
  // on phone, OnPoint quote flow, a colleague replying via shared
  // mailbox) — by end of day there's a clutter of tabs that have
  // already been answered. Every 5 min (and on tab refocus) we ask
  // the server whether each draft's source conversation has had a
  // sent message dated after the draft's createdAt; stale keys are
  // dropped silently. Reads `drafts` via a ref so the interval
  // attaches once and doesn't reset on every keystroke; Rule 2
  // pattern from bot/docs/dashboard.md.
  const draftsRef = useRef(drafts);
  useEffect(() => { draftsRef.current = drafts; }, [drafts]);
  useEffect(() => {
    let cancelled = false;
    async function prune() {
      if (cancelled) return;
      const current = draftsRef.current || {};
      const list = Object.entries(current).map(([key, d]) => {
        let sourceMessageId = null;
        if      (key.startsWith('reply-'))    sourceMessageId = key.slice('reply-'.length);
        else if (key.startsWith('replyAll-')) sourceMessageId = key.slice('replyAll-'.length);
        else if (key.startsWith('forward-'))  sourceMessageId = key.slice('forward-'.length);
        // New-mail drafts (key 'new-…') have no source — can't detect
        // whether their email's been sent without subject/recipient
        // matching, which is fuzzier than we want here. Skip them.
        if (!sourceMessageId || !d?.createdAt) return null;
        return { key, sourceMessageId, createdAt: d.createdAt };
      }).filter(Boolean);
      if (!list.length) return;
      try {
        const r = await fetch('/api/mail/drafts/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ drafts: list }),
          credentials: 'same-origin',
        });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled || !Array.isArray(data?.staleKeys) || !data.staleKeys.length) return;
        setDrafts(prev => {
          const next = { ...prev };
          for (const k of data.staleKeys) delete next[k];
          return next;
        });
      } catch { /* transient — retry on next tick */ }
    }
    // First sweep ~10 s after mount so the user has settled and
    // any in-flight Graph webhook deltas have propagated.
    const initial = setTimeout(prune, 10_000);
    const interval = setInterval(prune, 5 * 60 * 1000);
    const onFocus  = () => prune();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);  // attach once

  // Persist folder tree + labelMap so refresh shows them instantly from
  // cache while the background fetches update them in place.
  useEffect(() => {
    try { localStorage.setItem('pp_folders', JSON.stringify(folders)); } catch { /* ignore */ }
  }, [folders]);
  useEffect(() => {
    try { localStorage.setItem('pp_labelMap', JSON.stringify(labelMap)); } catch { /* ignore */ }
  }, [labelMap]);

  // Age out the mail cache once on mount. Idempotent, fire-and-forget.
  useEffect(() => {
    // Section 6 #5: prune during browser idle so the cursor sweep doesn't
    // compete with mount-time rendering. Falls back to a timeout where
    // requestIdleCallback isn't available (Safari).
    const run = () => mailCache.prune().catch(() => {});
    const ric = window.requestIdleCallback;
    const id = ric ? ric(run, { timeout: 5000 }) : setTimeout(run, 2000);
    return () => { if (ric) window.cancelIdleCallback(id); else clearTimeout(id); };
  }, []);

  // The cost-sheet, order, account and invoice drawers stay mounted while
  // closed (that is what gives them their slide-in and an instant open), but
  // they are code-split — mounting them at startup downloaded, parsed and ran
  // ~275 KB of their JS in the middle of the first list load, delaying every
  // startup request behind it (perf/bench.js, Oct 2026). Mount them once the
  // first list has loaded and the browser is idle — not merely idle, which it
  // is while waiting on the network mid-startup — or straight away if one is
  // asked for before then.
  const [drawersIdle, setDrawersIdle] = useState(false);
  const firstListLoaded = useRef(false);
  if (!loadingMsgs && messages.length) firstListLoaded.current = true;
  useEffect(() => {
    if (drawersIdle || !firstListLoaded.current) return undefined;
    const ready = () => setDrawersIdle(true);
    const ric = window.requestIdleCallback;
    const id = ric ? ric(ready, { timeout: 4000 }) : setTimeout(ready, 2000);
    return () => { if (ric) window.cancelIdleCallback(id); else clearTimeout(id); };
  }, [drawersIdle, loadingMsgs, messages.length]);
  const mountDrawers = drawersIdle
    || !!(costSheetOpenId || orderDrawerNumber || accountDrawerEmail || accountDrawerCustomerId || invoiceUploadSource);

  // Send-failure badge: poll the persisted log so the topbar pill shows
  // a count even if the user wasn't on this tab when a send failed.
  // 60 s is plenty — failures are rare and the modal does its own
  // load-on-open fetch for the live list.
  useEffect(() => {
    let cancelled = false;
    async function pull() {
      if (document.hidden) return;   // #9: don't poll a backgrounded tab
      try {
        const r = await fetch('/api/send-failures');
        const d = await r.json();
        if (!cancelled) setSendErrorCount((d.failures || []).length);
      } catch { /* ignore */ }
    }
    pull();
    const t = setInterval(pull, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Unresolved app_errors count for the header badge. /count is a single
  // indexed COUNT(*), not the rows, so polling it every minute is cheap.
  useEffect(() => {
    let cancelled = false;
    async function pull() {
      if (document.hidden) return;   // #9: don't poll a backgrounded tab
      try {
        const r = await fetch('/api/app-errors/count');
        const d = await r.json();
        if (!cancelled) setAppErrorCount(d.count || 0);
      } catch { /* ignore */ }
    }
    pull();
    const t = setInterval(pull, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Xero invoice drafts: poll the count of pending+extracted+failed
  // (i.e. anything not yet ticked off in Xero) so the topbar pill
  // tells you when there are bills awaiting human action.
  useEffect(() => {
    let cancelled = false;
    async function pull() {
      if (document.hidden) return;   // #9: don't poll a backgrounded tab
      try {
        const r = await fetch('/api/xero/drafts?limit=200');
        const d = await r.json();
        const pending = (d.drafts || []).filter(x => x.status !== 'posted').length;
        if (!cancelled) setXeroPendingCount(pending);
      } catch { /* ignore */ }
    }
    pull();
    const t = setInterval(pull, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    try { localStorage.setItem('pp_filter', msgFilter); } catch { /* ignore */ }
  }, [msgFilter]);

  function draftKeyFor(def) {
    if (def.replyToId) return `${def.replyAll ? 'replyAll' : 'reply'}-${def.replyToId}`;
    if (def.forwardId) return `forward-${def.forwardId}`;
    return `new-${Date.now()}`;
  }
  function saveDraft(key, data) {
    setDrafts(prev => ({ ...prev, [key]: data }));
  }
  function discardDraft(key) {
    setDrafts(prev => { const n = { ...prev }; delete n[key]; return n; });
    if (activeDraftKey === key) setActiveDraftKey(null);
  }
  function openDraft(key) {
    const d = drafts[key];
    if (!d) return;
    // If a different draft is already open, snapshot its current
    // edits into the drafts map BEFORE we swap. Without this step,
    // switching from draft A to draft B silently lost A's in-flight
    // typing — and depending on which path fired first, A's tab
    // could even drop out of the strip entirely. We use the
    // ComposeModal's imperative .snapshot() (no unmount, just
    // returns current form state) so the modal can swap defaults
    // in place without churn.
    if (composing && activeDraftKey && activeDraftKey !== key && composeRef.current?.snapshot) {
      try {
        const snap = composeRef.current.snapshot();
        const existing = drafts[activeDraftKey];
        saveDraft(activeDraftKey, {
          ...snap,
          msgSubject: existing?.msgSubject || snap.subject || '',
          msgFrom:    existing?.msgFrom || '',
          createdAt:  existing?.createdAt || Date.now(),
        });
      } catch { /* fall through; worst case the user loses unsaved edits */ }
    }
    setComposeDefaults(d);
    setActiveDraftKey(key);
    setComposing(true);
  }
  // ── In-progress sample helpers ────────────────────────────────────────────
  // Sample tabs share the strip with draft tabs (`tabList` below). A sample
  // is keyed by the email it's being filed against — `sample-<graphMessageId>` —
  // so opening Add Sample from the same email twice resumes the in-progress
  // one rather than starting fresh.
  function sampleKeyFor(messageId) { return `sample-${messageId || 'orphan'}`; }
  function saveSample(key, data) {
    setSamples(prev => ({ ...prev, [key]: { ...data, updatedAt: Date.now() } }));
  }
  function discardSample(key) {
    setSamples(prev => { const n = { ...prev }; delete n[key]; return n; });
    if (activeSampleKey === key) { setActiveSampleKey(null); setAddingSample(false); }
  }
  function openSample(key) {
    if (!samples[key]) return;
    // Jump to the source email so the modal's customer / context lines up.
    const sourceMsgId = key.replace(/^sample-/, '');
    if (sourceMsgId && sourceMsgId !== 'orphan' && selectedMsg?.id !== sourceMsgId) {
      const found = messages.find(m => m.id === sourceMsgId);
      if (found) handleSelectMsg(found);
    }
    setActiveSampleKey(key);
    setAddingSample(true);
  }
  // Open a fresh Add Sample modal for the currently-viewed email. Seeds an
  // empty tab in the samples map IMMEDIATELY (not on close) so the tab
  // strip shows the in-progress sample alongside any reply drafts from
  // the moment the user clicks Add Sample. This is the difference
  // between "the tab appears after I close" and "I can flick between an
  // open sample and an open draft right now".
  function startSample(msg) {
    if (!msg) return;
    const key = sampleKeyFor(msg.id);
    setSamples(prev => prev[key] ? prev : ({
      ...prev,
      [key]: {
        sourceMsgId:   msg.id,
        sourceFrom:    msg.from || '',
        sourceSubject: msg.subject || '',
        customerName:  '',
        createdAt:     Date.now(),
        updatedAt:     Date.now(),
      },
    }));
    setActiveSampleKey(key);
    setAddingSample(true);
  }

  const draftList = Object.entries(drafts)
    .map(([key, d]) => ({ key, kind: 'draft', ...d }))
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  const sampleList = Object.entries(samples)
    .map(([key, s]) => ({ key, kind: 'sample', ...s }))
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  // Unified list passed to ReadingPane; ReadingPane decides per-item how to render.
  //
  // The "Email" tab is a synthetic "go back to the source email" pill
  // shown only when:
  //   (a) a modal (compose or AddSample) is open, AND
  //   (b) the current email DOESN'T already have a draft / sample tied
  //       to it.
  //
  // Why (b): when there's a draft for this email, that draft tab IS
  // the way back — clicking it both opens the modal and points it at
  // this email. Showing both an "Email" pill AND the draft pill
  // together (the previous behaviour) confused users about which one
  // to click and meant TWO tabs were filled-accent at the same time
  // (Email tab is filled-accent by design; the active draft is
  // filled-accent via .draftTabActive). Now only the active
  // draft/sample tab gets the highlight.
  const currentMsgId = selectedMsg?.id;
  const hasDraftForCurrent =
    !!currentMsgId && (
      drafts[`reply-${currentMsgId}`]    ||
      drafts[`replyAll-${currentMsgId}`] ||
      drafts[`forward-${currentMsgId}`]  ||
      samples[`sample-${currentMsgId}`]
    );
  const showEmailTab = (composing || addingSample) && !!selectedMsg && !hasDraftForCurrent;
  const emailTabEntry = showEmailTab ? [{
    key:           `email-${selectedMsg.id}`,
    kind:          'email',
    subject:       selectedMsg.subject || '',
    fromName:      selectedMsg.fromName || selectedMsg.from || '',
    updatedAt:     Date.now() + 1,   // pin it to the front
  }] : [];
  const buildOrderTabs = buildOrders.map(o => ({
    key:            o.key,
    kind:           'buildOrder',
    anchorSubject:  o.anchorSubject,
    anchorFromName: o.anchorFromName,
    updatedAt:      o.updatedAt || o.createdAt,
  }));
  // AI Quote tab — shown while a quote panel is open (visible or minimised)
  // so the user can always click back to it. × on the tab closes the quote.
  const quoteTabs = aiQuoteMsg ? [{
    key:      `quote-${aiQuoteMsg.id}`,
    kind:     'quote',
    subject:  aiQuoteMsg.subject || '',
    fromName: aiQuoteMsg.fromName || aiQuoteMsg.from || '',
    updatedAt: aiQuoteMsg.__openedAt || 0,
  }] : [];
  const tabList = [
    ...emailTabEntry,
    ...[...draftList, ...sampleList, ...buildOrderTabs, ...quoteTabs].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
  ];

  const PAGE_SIZE = 100;
  // Backstop poll while the SSE stream is live. It was 5 minutes, chosen when
  // every list load was a Graph call and the SSE poke was trusted to refresh
  // sooner. The list is served from the local index now (single-digit ms on a
  // cache hit), so a slow backstop buys nothing and costs the whole lag when a
  // poke doesn't land — the stream reports connected but events can be delayed
  // or buffered in transit, which reads as "the app is minutes behind Outlook"
  // even with ingestion completely current (2026-09-10).
  const SSE_BACKSTOP_MS = 90_000;

  // Resizable middle-pane width, persisted to localStorage. The
  // hard cap of 820 px is a UX maximum (no value in dragging the
  // message list wider than that on a typical desktop). The dynamic
  // cap below is what protects the reading pane from being squeezed
  // off-screen on narrow viewports — the message list never grows
  // past viewport - sidebar - resizer - reading-pane-min.
  const SIDEBAR_W   = 212;
  const RESIZER_W   = 6;
  const READING_MIN = 480;          // min-width on .reader (ReadingPane.module.css)
  const LIST_HARD_MAX = 820;
  const LIST_MIN    = 320;

  function clampListW(width, viewport = (typeof window !== 'undefined' ? window.innerWidth : 1280)) {
    const dynamicMax = Math.max(LIST_MIN, viewport - SIDEBAR_W - RESIZER_W - READING_MIN);
    return Math.max(LIST_MIN, Math.min(LIST_HARD_MAX, dynamicMax, width));
  }

  const [listW, setListW]   = useState(() => {
    const v = parseInt(localStorage.getItem('pp_listW') || '', 10);
    const stored = Number.isFinite(v) && v >= LIST_MIN && v <= LIST_HARD_MAX ? v : 520;
    // Clamp on read so a 720 px stored value doesn't blow out a
    // 1024 px laptop viewport on first paint.
    return clampListW(stored);
  });
  const [dragging, setDragging] = useState(false);

  // Re-clamp the message-list width whenever the viewport changes
  // (window resize, devtools docking on the side, browser-zoom).
  // Without this, dragging the resizer wide on a big monitor and
  // then docking devtools would leave the reading pane below its
  // 480 px minimum and force horizontal overflow.
  useEffect(() => {
    function onResize() {
      // When the viewport shrinks (e.g. devtools docks to the side)
      // we shrink the list to fit. When it grows back we want to
      // RESTORE the user's last-set width — not stay stuck at the
      // shrunken value. Pull from localStorage so the desired width
      // is preserved through the squeeze-and-release cycle.
      setListW(() => {
        const stored = parseInt(localStorage.getItem('pp_listW') || '', 10);
        const target = Number.isFinite(stored) && stored >= LIST_MIN && stored <= LIST_HARD_MAX
          ? stored : 520;
        return clampListW(target);
      });
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // clampListW is stable — its only dependency (window.innerWidth)
    // is read inside the callback at call time.
  }, []); // eslint-disable-line

  const searchTimer        = useRef(null);
  const searchQueryRef     = useRef('');  // latest typed query, for stale-guarding async local-cache scans
  const searchAbortRef     = useRef(null); // AbortController for the in-flight server search
  const searchPreviewTimer = useRef(null);
  const searchPreviewAbortRef = useRef(null); // …and for the in-flight preview=1 request
  const searchFullDoneRef  = useRef(null);  // the query whose FULL answer has painted — a later preview must not replace it
  const searchNextLink     = useRef(null); // Graph nextLink cursor for paginating search results
  const searchLocalHitsRef = useRef([]);   // Most recent local-cache hits for the current query, so the server-response callback can merge instead of replace (Graph $search lags real-time by minutes — local hits for new mail would otherwise vanish).
  const customerFetchRef   = useRef(null);
  const customerNextLink   = useRef(null);
  // Track local read/unread ops for ~30 s so the 20 s poll doesn't clobber
  // the user's click if Graph is slow to settle. Key: graphMessageId, value:
  // { isRead, at }. Entries older than 30 s are ignored.
  const recentReadOp       = useRef(new Map());
  // Same protection for flag / completed state. Without this, pressing
  // Ctrl+D right after Send (or any time the 20 s poll happens to fire
  // before Graph has propagated our PATCH /flag) lets the polling pull
  // the stale "not flagged" state and overwrite the user's optimistic
  // mark-done — looks to the user like Ctrl+D didn't take. Same shape
  // as recentReadOp.
  const recentFlagOp       = useRef(new Map());
  const PENDING_OP_MAX_MS  = 5 * 60_000;
  // Same 30 s guard for the replied / forwarded arrow. When the user sends a
  // reply/forward, Graph's PR_ICON_INDEX (which drives the arrow) takes a
  // moment to propagate — so a poll firing in between would pull the stale
  // "not replied" state and wipe the optimistic arrow until the next refresh.
  // Value: { replied, forwarded, at }.
  const recentReplyOp      = useRef(new Map());
  // The user's own reply openers ("when the email says X, start my reply with
  // Y") — loaded once, handed to buildReplyOpener, edited in ReplyOpenersDialog.
  const [replyOpeners, setReplyOpeners] = useState([]);
  const [replyOpenersDialog, setReplyOpenersDialog] = useState(null); // { seed } | null
  useEffect(() => {
    let cancelled = false;
    fetch('/api/mail/reply-openers')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d && Array.isArray(d.openers)) setReplyOpeners(d.openers); })
      .catch(() => { /* the built-in openers still work */ });
    return () => { cancelled = true; };
  }, [mailbox]);
  // OnPoint staff's addresses: a reply to one starts without "Hi Name,"
  // (lib/replyOpener.js). Not loaded = everyone is greeted, as before.
  const [staffAddresses, setStaffAddresses] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/mail/staff-addresses')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d && Array.isArray(d.addresses)) setStaffAddresses(new Set(d.addresses)); })
      .catch(() => { /* everyone is greeted */ });
    return () => { cancelled = true; };
  }, []);
  // Message ids whose attachments we've already auto-retried this session, so
  // a persistently-slow listing doesn't loop the uncapped re-fetch.
  const attRetriedRef      = useRef(new Set());
  // Apply the 30 s recentReadOp / recentFlagOp / recentReplyOp guard to a list
  // of messages, so a freshly-fetched-but-not-yet-propagated server value
  // can't undo the user's just-made local change. Used both when priming the
  // inbox from the IndexedDB cache and when persisting poll results back into
  // it — keeps the cache honest so a later folder-switch / return-from-search
  // doesn't paint stale state then snap.
  const applyLocalReadFlag = (list) => {
    const now = Date.now();
    const RECENT_MS = 30_000;
    return (list || []).map(m => {
      const lr  = recentReadOp.current.get(m.id);
      const lf  = recentFlagOp.current.get(m.id);
      const lrp = recentReplyOp.current.get(m.id);
      if (lr && now - lr.at > PENDING_OP_MAX_MS) recentReadOp.current.delete(m.id);
      else if (lr && m.isRead === lr.isRead) recentReadOp.current.delete(m.id);
      if (lf && now - lf.at > PENDING_OP_MAX_MS) recentFlagOp.current.delete(m.id);
      else if (lf && m.flagged === lf.flagged && m.completed === lf.completed) recentFlagOp.current.delete(m.id);
      const activeR = recentReadOp.current.get(m.id);
      const activeF = recentFlagOp.current.get(m.id);
      const useR   = !!activeR;
      const useF   = !!activeF;
      const useRep = lrp && now - lrp.at < RECENT_MS;
      if (!useR && !useF && !useRep) return m;
      return {
        ...m,
        isRead:    useR   ? activeR.isRead     : m.isRead,
        flagged:   useF   ? activeF.flagged    : m.flagged,
        completed: useF   ? activeF.completed  : m.completed,
        replied:   useRep ? lrp.replied   : m.replied,
        forwarded: useRep ? lrp.forwarded : m.forwarded,
      };
    });
  };
  // Optimistically mark a source message replied/forwarded the instant the
  // user sends, so the arrow shows without waiting for the ~60 s poll / a
  // refresh. kind = 'replied' | 'forwarded'. Guarded by recentReplyOp so the
  // poll can't clobber it before Graph's icon index catches up.
  function markRepliedLocal(sourceId, kind) {
    if (!sourceId) return;
    const patch = kind === 'forwarded'
      ? { replied: false, forwarded: true }
      : { replied: true,  forwarded: false };
    // What the row showed before, so a failed send can put it back.
    const was = messages.find(m => m.id === sourceId) || (selectedMsg && selectedMsg.id === sourceId ? selectedMsg : null);
    const before = { replied: !!(was && was.replied), forwarded: !!(was && was.forwarded) };
    setMessages(prev => prev.map(m => m.id === sourceId ? { ...m, ...patch } : m));
    setSelectedMsg(prev => prev && prev.id === sourceId ? { ...prev, ...patch } : prev);
    mailCache.updateField(sourceId, patch).catch(() => {});
    recentReplyOp.current.set(sourceId, { ...patch, before, at: Date.now() });
  }
  // Undo an optimistic replied/forwarded mark when the send fails (e.g. the
  // blank-body 422): clearing the recentReplyOp guard is the important part —
  // it stops the 30s guard pinning the wrong arrow so the next poll restores
  // the server's true state; we also flip the local flag off for instant
  // feedback. Without this a rejected send shows a replied ↩ arrow for ~30s.
  function revertRepliedLocal(sourceId) {
    if (!sourceId) return;
    // Back to what it showed BEFORE this send — not a blanket "not replied".
    // An email already answered once keeps its arrow when a second reply
    // fails; clearing it made a replied-to email look unanswered.
    const op = recentReplyOp.current.get(sourceId);
    recentReplyOp.current.delete(sourceId);
    const patch = (op && op.before) || { replied: false, forwarded: false };
    setMessages(prev => prev.map(m => m.id === sourceId ? { ...m, ...patch } : m));
    setSelectedMsg(prev => prev && prev.id === sourceId ? { ...prev, ...patch } : prev);
    mailCache.updateField(sourceId, patch).catch(() => {});
  }
  // Same pattern for deletes. The mailCache (IndexedDB) write is
  // fire-and-forget, so a quick reload after delete can still see
  // the just-deleted message in the cached paint, then the server
  // fetch replaces it with the truth — looks like the message
  // "comes back then disappears". This guard keeps deleted IDs in
  // memory + localStorage with a 5-minute TTL so any subsequent
  // fetch / cache-read filters them out reliably.
  const recentDeleteOp     = useRef(new Map());
  // Hydrate from localStorage so the guard survives full reloads.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('recentDeleteOp.v1');
      if (!raw) return;
      const cutoff = Date.now() - 5 * 60_000;
      const obj = JSON.parse(raw);
      for (const [id, at] of Object.entries(obj || {})) {
        if (Number(at) > cutoff) recentDeleteOp.current.set(id, Number(at));
      }
    } catch {}
  }, []);
  function stampDelete(msgId) {
    const at = Date.now();
    recentDeleteOp.current.set(msgId, at);
    try {
      const cutoff = at - 5 * 60_000;
      const out = {};
      for (const [id, t] of recentDeleteOp.current.entries()) {
        if (t > cutoff) out[id] = t;
      }
      localStorage.setItem('recentDeleteOp.v1', JSON.stringify(out));
    } catch {}
  }
  function isRecentlyDeleted(msgId) {
    const t = recentDeleteOp.current.get(msgId);
    if (!t) return false;
    if (Date.now() - t > 5 * 60_000) {
      recentDeleteOp.current.delete(msgId);
      return false;
    }
    return true;
  }
  // Tracks the id of the message currently being hydrated by handleSelectMsg.
  // A slow getMessage() for an earlier click can come back after the user
  // has moved on to a different message — without this guard we'd snap them
  // back to the stale one. Every late fetch checks selectingRef.current ===
  // msg.id before committing state.
  const selectingRef       = useRef(null);
  // Holds the AbortController for the in-flight message-body fetch so
  // switching messages cancels the previous open instead of letting it run
  // to completion. An abandoned open otherwise keeps the client waiting on
  // (and the server working) a message the user has already clicked away
  // from — the "it kept loading the original" report. See hydrateBody /
  // handleSelectMsg.
  const bodyFetchAbortRef  = useRef(null);
  // Poll-prune miss counter (message content-key → consecutive polls the row
  // was absent from the fetched page). A row must be missing for TWO polls
  // before it's pruned, so Graph ordering/eventual-consistency jitter (a row
  // briefly dropping out of one page) doesn't flicker it out of the list.
  const pruneMissRef       = useRef(new Map());
  // Imperative handle on the open ComposeModal so we can ask it to close +
  // save its draft when the user clicks a different message mid-compose.
  const composeRef         = useRef(null);
  // Same pattern for AddSampleModal — used by the "email" tab click to
  // ask the modal to snapshot itself before unmounting.
  const sampleRef          = useRef(null);
  // Before the sidebar moves this tab to another mailbox (lib/impersonate.js
  // switchMailbox), put an open reply / sample away as its tab — the same
  // close-and-keep a click on another email does — and give the drafts store
  // a moment to save it, while storage still points at THIS mailbox.
  const switchStateRef = useRef({ composing: false, addingSample: false });
  switchStateRef.current = { composing, addingSample };
  useEffect(() => {
    const putAway = async () => {
      const { composing: c, addingSample: s } = switchStateRef.current;
      if (c) composeRef.current?.requestClose?.();
      if (s) sampleRef.current?.requestClose?.();
      if (c || s) await new Promise(r => setTimeout(r, 200));
    };
    window.__ppBeforeMailboxSwitch = putAway;
    return () => { if (window.__ppBeforeMailboxSwitch === putAway) window.__ppBeforeMailboxSwitch = null; };
  }, []);
  // Tracks every message id we've ever *tried* to fetch a label for, win or
  // lose. Prevents the labels useEffect below from retrying a fetch on every
  // re-render if the first call failed (network error, 500, rate limit).
  // A Map<id, lastAttemptedAt> in a ref — never in state — so writes
  // never trigger a render. Timestamp lets ids retry after a cooldown
  // so messages still being classified server-side eventually surface
  // their labels without forcing the user to refresh.
  const attemptedLabelIds    = useRef(new Map());
  // Quick re-asks for labels on just-arrived mail (see the labels effect):
  // tries so far per id, and the pending timers (cleared on unmount).
  const freshLabelTries      = useRef(new Map());
  const freshLabelTimers     = useRef(new Set());
  // Bumps on visibility/focus so the labels useEffect re-runs and
  // re-fetches anything that was attempted-while-server-was-still-
  // classifying earlier. See the listener below labels useEffect.
  const [labelsFocusTick, setLabelsFocusTick] = useState(0);
  // Same locking pattern for the order-lookup effect — id goes in the
  // set BEFORE the fetch starts so a state update during the request
  // can't re-trigger the effect and cancel its own fetch (the previous
  // version of this effect did exactly that, leaving every entry
  // permanently null and the View Order button never appearing).
  const attemptedOrderIds    = useRef(new Set());
  const attemptedReactionIds = useRef(new Set());

  // ── Apply resizable width as a CSS variable ──────────────────────────────────
  useEffect(() => {
    document.documentElement.style.setProperty('--msglist-w', listW + 'px');
  }, [listW]);
  // NOTE: persistence is INTENTIONALLY NOT a `[listW]`-dep effect.
  // Auto-saving on every listW change would persist the shrunken
  // value the resize handler sets when devtools docks to the side,
  // so closing devtools later read back the shrunken width as the
  // user's preference and the list never sprung back. Persist only
  // on actual user-initiated changes (drag end, double-click reset).
  function persistListW(v) {
    try { localStorage.setItem('pp_listW', String(v)); } catch {}
  }
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      // Use the shared clamp so manual drag respects the same
      // dynamic ceiling as initial mount and resize.
      const next = clampListW(e.clientX - SIDEBAR_W);
      setListW(next);
      persistListW(next);
    };
    const onUp = () => setDragging(false);
    // Belt-and-braces release. Mousemove inside a sandboxed iframe (the
    // reading-pane email body) does not bubble to window, so without a
    // pointer-capture overlay the user can drag into the iframe and
    // we never see the mouseup that follows. Symptom: cursor stays
    // col-resize, every click triggers another setListW call, and
    // nothing else in the UI becomes clickable. Fix:
    //   - listen on `mouseup` AND `pointerup` on window+document at
    //     capture phase so we catch the release no matter where the
    //     pointer is.
    //   - blur + visibilitychange + escape all force-end the drag if
    //     the OS swallows the up event entirely (alt-tab, lock screen).
    //   - add a transparent `dragShield` overlay (CSS class) that the
    //     resizer turns on while dragging — its element captures the
    //     pointer in front of the iframe so events stay in our window.
    window.addEventListener('mousemove',  onMove,  true);
    window.addEventListener('mouseup',    onUp,    true);
    window.addEventListener('pointerup',  onUp,    true);
    window.addEventListener('blur',       onUp);
    window.addEventListener('keydown',    onKey);
    document.addEventListener('mouseleave', onUp);
    document.addEventListener('visibilitychange', onUp);
    function onKey(e) { if (e.key === 'Escape') onUp(); }
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      window.removeEventListener('mousemove',  onMove,  true);
      window.removeEventListener('mouseup',    onUp,    true);
      window.removeEventListener('pointerup',  onUp,    true);
      window.removeEventListener('blur',       onUp);
      window.removeEventListener('keydown',    onKey);
      document.removeEventListener('mouseleave', onUp);
      document.removeEventListener('visibilitychange', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [dragging]);

  // ── Close context menu on outside click / scroll / resize ────────────────────
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [ctxMenu]);

  // ── Keyboard: Ctrl+D (or Cmd+D on Mac) → Mark as done on selected message ────
  // Ctrl+T is reserved by the browser for New Tab and can't be intercepted; Ctrl+D
  // triggers the browser's bookmark dialog but preventDefault() does suppress it.
  // Listener is attached *once*. The handler reads the latest selectedMsg +
  // handleMarkDone + composing/addingSample via a ref so changing them doesn't
  // thrash add/remove on every parent render.
  const keyboardRef = useRef({});
  keyboardRef.current = { selectedMsg, handleMarkDone, handleDelete };
  useEffect(() => {
    function onKeyDown(e) {
      // Plain Delete / Backspace deletes the selected email, matching
      // Outlook's row shortcut. Skip when focus is in an editable
      // surface so it never eats a character mid-typing.
      if ((e.key === 'Delete' || e.key === 'Del') && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
        const t = e.target;
        if (t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA' || t?.isContentEditable) return;
        if (e.repeat) return;
        const { selectedMsg: sel, handleDelete: del } = keyboardRef.current;
        if (!sel || !del) return;
        e.preventDefault();
        del(sel.id);
        return;
      }
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
      if (e.key !== 'd' && e.key !== 'D') return;
      const { selectedMsg: sel, handleMarkDone: done } = keyboardRef.current;
      if (!sel) return;
      // Skip ONLY when typing in plain text inputs (search, filters,
      // pill inputs). Compose body is a contentEditable div — Ctrl+D
      // on it should still mark the underlying email done, matching
      // user's expectation that "Ctrl+D works even while sending".
      // The previous `if (composing) return` gate was a stale-state
      // race: after clicking Send, React batches setComposing(false)
      // with the rest of the handler — so a Ctrl+D pressed in the same
      // tick read composing=true from the ref and silently no-op'd.
      // Dropping that gate fixes it; the INPUT / TEXTAREA filter is
      // narrow enough not to swallow keystrokes meant for the editor.
      const t = e.target;
      if (t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA') return;
      // Ignore key auto-repeat so a held Ctrl+D doesn't spam Graph.
      if (e.repeat) return;
      e.preventDefault();
      // Idempotent: always lands on done. The right-click menu still
      // toggles for the "I marked it done by accident" case.
      done(sel.id, 'set');
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Keep mailbox in sync if the parent passes a different user (e.g. a
  // re-login after session expiry). Mailbox is seeded above from
  // currentUser; the inequality guard means setMailbox only fires when
  // the values genuinely differ, so including `mailbox` in the deps is
  // safe (the next render finds them equal and short-circuits).
  useEffect(() => {
    if (currentUser?.mailbox && currentUser.mailbox !== mailbox) {
      setMailbox(currentUser.mailbox);
    }
  }, [currentUser, mailbox]);

  // Custom labels — fetched here so the right-click "Set label" menu
  // can list them alongside the built-in ones. Re-fetches whenever a
  // new label is added (labelsRefreshKey bumps).
  useEffect(() => {
    let cancelled = false;
    fetch('/api/labels/custom')
      .then(r => r.json())
      .then(d => { if (!cancelled) setCustomLabels(d.customLabels || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [labelsRefreshKey]);

  // One-time: load customers for the Customers sidebar section.
  useEffect(() => {
    fetch('/api/mail/customers')
      .then(r => r.json())
      .then(data => { setCustomers(Array.isArray(data) ? data : []); setLoadingCustomers(false); })
      .catch(() => setLoadingCustomers(false));
  }, []);

  // Resolve sender info for the currently-viewed email. Drives both the
  // ReadingPane "Add to OnPoint" affordance and the top-bar pill that
  // surfaces "New Contact" / "New Customer" alongside Add Sample. Skips
  // the round-trip when nothing's selected.
  useEffect(() => {
    setSenderInfo(null);
    if (!selectedMsg?.from) return;
    let cancelled = false;
    // `name` lets the lookup spot a same-name / changed-email contact
    // and surface "Update Contact" instead of "New Contact".
    fetch(`/api/mail/sender-lookup?email=${encodeURIComponent(selectedMsg.from)}&name=${encodeURIComponent(selectedMsg.fromName || '')}`)
      .then(r => r.json())
      .then(data => { if (!cancelled) setSenderInfo(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedMsg?.from]);

  function refreshSenderInfo() {
    if (!selectedMsg?.from) return;
    fetch(`/api/mail/sender-lookup?email=${encodeURIComponent(selectedMsg.from)}&name=${encodeURIComponent(selectedMsg.fromName || '')}`)
      .then(r => r.json())
      .then(setSenderInfo)
      .catch(() => {});
  }

  // ── Folders ──────────────────────────────────────────────────────────────────
  // Pre-warm: fire-and-forget inbox messages fetch the moment this
  // authenticated shell mounts. The user's "real" loadMessages call
  // is gated on /api/mail/folders resolving first, which adds ~150-
  // 500ms — the pre-warm runs in parallel via the well-known `inbox`
  // alias so Graph's auth token + HTTPS keep-alive are already hot
  // by the time the real fetch fires. We throw the response away;
  // any successful round-trip is the win. The server's 10s
  // messageListCache may even serve the user's request from cache
  // when the two land within the same window.
  // Skipped when the list already started from the remembered Inbox: the real
  // fetch is in flight, and a second, differently-paged one only competes.
  useEffect(() => {
    if (startFolderIdRef.current) return;
    fetch('/api/mail/folders/inbox/messages?top=50&skip=0').catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/mail/folders')
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        setFolders(arr);
        const inbox = arr.find(f => f.displayName?.toLowerCase() === 'inbox');
        if (inbox) {
          try {
            localStorage.setItem(START_FOLDER_KEY, JSON.stringify({ mailbox: viewedMailbox(currentUser), folder: inbox }));
          } catch { /* quota / private mode — next start just waits for /folders */ }
          // Open the Inbox unless the list already did. Keeping the SAME object
          // when the cached start folder was right avoids reloading the list;
          // a click on another folder made meanwhile is left alone.
          setSelectedFolder(prev => {
            if (!prev) return inbox;
            if (prev.id === startFolderIdRef.current && prev.id !== inbox.id) return inbox;
            return prev;
          });
        }
        setLoadingFolders(false);
      })
      .catch(() => setLoadingFolders(false));
  }, []);

  // Keep the deps-[] folders poll's view of current state fresh without
  // making it re-subscribe (which would reset the 60 s timer every render).
  useEffect(() => { foldersRef.current = folders; }, [folders]);
  useEffect(() => { selectedFolderRef.current = selectedFolder; }, [selectedFolder]);
  // The list as it is right now, for the poll to tell "this fetch brought a
  // new row" from "nothing new yet" without re-subscribing on every change.
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    const CATCH_UP_DELAYS_MS = [8_000, 20_000, 40_000];
    function clearCatchUp() {
      catchUpTimersRef.current.forEach(clearTimeout);
      catchUpTimersRef.current = [];
    }
    function scheduleCatchUp() {
      clearCatchUp(); // one pending series at a time, however many badges tick up
      catchUpTimersRef.current = CATCH_UP_DELAYS_MS.map(ms => setTimeout(async () => {
        if (!pollMessagesRef.current) return; // searching / thread mode — poll is paused
        let gained = false;
        try { gained = await pollMessagesRef.current(); } catch { /* next one tries */ }
        if (gained) clearCatchUp();
      }, ms));
    }
    async function refreshFolders() {
      let data;
      try {
        const r = await fetch('/api/mail/folders?bg=1', { cache: 'no-store' });
        data = await r.json();
      } catch { return; }
      if (!Array.isArray(data)) return;
      // If new unread just landed in the folder the user is *currently*
      // looking at, pull that folder's message list in FIRST, then bump
      // the badge — so the count never appears next to Inbox before the
      // email row it's counting. (Only the open folder can show rows; for
      // other folders the badge legitimately leads.) Skipped while searching
      // or in thread mode, when the message poll is paused and pollMessagesRef
      // is null.
      const openId = selectedFolderRef.current?.id;
      if (openId && pollMessagesRef.current) {
        const incoming = findFolderById(data, openId);
        const prev     = findFolderById(foldersRef.current, openId);
        const wentUp   = incoming && prev
          && (incoming.unreadItemCount || 0) > (prev.unreadItemCount || 0);
        if (wentUp) {
          let gained = false;
          try { gained = await pollMessagesRef.current(); } catch { /* fall through */ }
          // The badge is Graph's live count; the list is the local index, which
          // only has the row once the webhook has been ingested (~10-40 s). When
          // the refresh above brought nothing new, the index simply is not there
          // yet — and waiting for the next 60 s tick is the "badge shows, the
          // email turns up a minute later" gap. Look again shortly instead: the
          // list is index-served, so these are cheap, and they stop at the first
          // one that brings a row.
          if (!gained) scheduleCatchUp();
        }
      }
      setFolders(data);
      // Same cadence for the sidebar Flags list: poll, refocus, SSE poke.
      // Rate-floored inside the hook; the server caches it too.
      refreshFlagged();
    }
    // Expose refreshFolders so the SSE push effect can trigger an immediate
    // refresh (folders + the open folder's messages) when the server signals
    // this mailbox changed.
    refreshFoldersRef.current = refreshFolders;
    // Interval ticks are no-ops while the tab is hidden — a backgrounded
    // tab makes no idle Graph calls. The visibilitychange / focus handlers
    // below still call refreshFolders directly the moment it's refocused.
    const t = setInterval(() => {
      if (typeof document === 'undefined' || !document.hidden) refreshFolders();
    }, sseConnected ? SSE_BACKSTOP_MS : 60_000); // backstop when SSE is live
    // Background tabs throttle setInterval to once-a-minute or less,
    // so a tab left alone for an hour comes back with a stale unread
    // badge until the next tick lands. Refresh the moment the tab is
    // foregrounded so the badge matches the message list within a
    // few hundred ms of regaining focus.
    function onVisible() {
      if (document.visibilityState === 'visible') refreshFolders();
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refreshFolders);
    return () => {
      clearInterval(t);
      clearCatchUp();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refreshFolders);
      refreshFoldersRef.current = null;
    };
  }, [sseConnected, refreshFlagged]);

  // ── SSE push (Section 3 #2, Phase 2) ──────────────────────────────────────
  // When SSE is enabled server-side, open a Server-Sent Events stream and
  // refresh the instant the server says this mailbox changed, rather than
  // waiting out the 60s poll. ADDITIVE: polling keeps running, so if SSE is off
  // or the stream drops, nothing breaks. Pokes are debounced so a burst (e.g.
  // delta reconciliation routing many changes) collapses to one refresh, and
  // hidden tabs stay quiet (they catch up on refocus, like the poll).
  useEffect(() => {
    const sseEnabled = currentUser?.sseEnabled;
    if (!sseEnabled) return undefined;
    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') return undefined;
    let debounce = null;
    // Liveness watchdog. onopen only proves the socket opened — a proxy that
    // silently BUFFERS /api/events (Apache ignores X-Accel-Buffering) leaves the
    // stream "open" with zero events arriving and NO onerror, so without this
    // the client would sit on the 5-min poll backstop while mail silently lags.
    // The server sends a data heartbeat every ~25s; if we hear NOTHING (not even
    // the initial hello / a ping) for 70s, treat the stream as dead and flip to
    // disconnected so polls snap back to 60s. Any later event re-arms it.
    const HEARTBEAT_TIMEOUT_MS = 70_000;
    let watchdog = null;
    const armWatchdog = () => {
      if (watchdog) clearTimeout(watchdog);
      watchdog = setTimeout(() => setSseConnected(false), HEARTBEAT_TIMEOUT_MS);
    };
    // withImpersonate: EventSource can't carry the X-Impersonate-Mailbox
    // header, so a tab working in another mailbox (shared sales@, or an admin
    // view) names it in the URL — otherwise it would get its OWN pushes.
    const es = new EventSource(withImpersonate('/api/events'), { withCredentials: true });
    es.onopen = () => { setSseConnected(true); armWatchdog(); };
    es.onmessage = (e) => {
      // ANY event (hello / ping / mail:changed) proves the stream delivers —
      // re-arm the watchdog and (re)confirm connected. setSseConnected(true) is
      // a no-op re-render when already true, so the per-heartbeat call is cheap.
      armWatchdog();
      setSseConnected(true);
      let evt;
      try { evt = JSON.parse(e.data); } catch { return; }
      if (evt.type !== 'mail:changed') return;
      if (debounce) return; // coalesce a burst into a single refresh
      debounce = setTimeout(() => {
        debounce = null;
        if (typeof document !== 'undefined' && document.hidden) return;
        try { refreshFoldersRef.current?.(); } catch { /* poll still covers it */ }
        // Also refresh the OPEN folder's message list on any poke — not just
        // when unread went up (refreshFolders only pulls the list on an unread
        // increase). A move/delete leaves unread unchanged but INVALIDATES the
        // row's folder-relative id; with SSE on, the periodic list poll is a
        // 5-min backstop, so without this a moved message's stale row lingers
        // for minutes and every tick/read on it hits "not found in the store".
        // Event-driven (only on a real change), background-priority (?bg=1).
        try { pollMessagesRef.current?.(); } catch { /* the periodic poll covers it */ }
      }, 1200);
    };
    // EventSource auto-reconnects on error (honouring the server's `retry:`).
    // Mark disconnected so the polls speed back up to the fast cadence until
    // the stream is healthy again — the poll fully covers the gap.
    es.onerror = () => setSseConnected(false);
    return () => {
      if (debounce) clearTimeout(debounce);
      if (watchdog) clearTimeout(watchdog);
      es.close();
      setSseConnected(false);
    };
  }, [currentUser]);

  // Online Archive. Single call on mount — archive structure doesn't change
  // anywhere near as often as primary folder counts, so no polling.
  useEffect(() => {
    fetch('/api/mail/archive-folders')
      .then(r => r.json())
      .then(data => {
        if (!data?.root) return;
        // Wrap the root so it renders as a single "Online Archive" node with
        // all archive subfolders nested underneath it.
        setArchiveFolders([{
          id: data.root.id,
          displayName: data.root.displayName || 'Online Archive',
          unreadItemCount: (data.folders || []).reduce((s, f) => s + (f.unreadItemCount || 0), 0),
          childFolders: data.folders || [],
          isArchive: true,
        }]);
      })
      .catch(() => {});
  }, []);

  // ── Messages ─────────────────────────────────────────────────────────────────
  // Graph's $orderby=receivedDateTime on Sent / Drafts / Outbox is noticeably
  // slower than Inbox — server-side indexing is less aggressive on those
  // folders. Fetch a smaller first page so the initial render lands sooner;
  // Load More picks up the rest when the user scrolls.
  const isOutgoingFolder = (folder) => /sent|outbox|drafts/i.test(folder?.displayName || '');
  const isDraftsFolder   = (folder) => /^drafts\b/i.test(folder?.displayName || '');

  // Open an Outlook Drafts-folder message for editing in the compose
  // modal. Differs from openReply in that there's no quoted history
  // and graphDraftId is preset so subsequent edits PATCH the same
  // Graph message instead of creating a new draft.
  //
  // The body arrives with cid: references (the autosave stores inline images
  // as Graph attachments, and Outlook-authored drafts do the same). Resolving
  // those to data: URIs needs a ?inline=1 refetch, so ComposeModal owns it —
  // it opens instantly with the cid: body and swaps the bytes in when they
  // land, and its send path waits on that same promise. Doing it here would
  // either block the open on a Graph round-trip or race the send.
  function openOutlookDraft(msg) {
    const toAddrs  = (msg.toAddresses || []).map(a => a.email).filter(Boolean).join('; ');
    const ccAddrs  = (msg.ccAddresses || []).map(a => a.email).filter(Boolean).join('; ');
    const bccAddrs = (msg.bccAddresses || []).map(a => a.email).filter(Boolean).join('; ');
    setComposeDefaults({
      mode:          'draft',
      graphDraftId:  msg.id,
      to:            toAddrs,
      cc:            ccAddrs,
      bcc:           bccAddrs,
      subject:       msg.subject || '',
      bodyHtml:      msg.bodyHtml || msg.body || '',
      attachments:   [],   // editing existing attachments not yet supported
    });
    const key = `draft-${msg.id}`;
    setActiveDraftKey(key);
    setComposing(true);
  }
  // Run-token so a loadMessages kicked off by e.g. a filter change can be
  // abandoned if the user switches folder or filter mid-pagination. Every
  // run bumps the ref; setState is only allowed while the captured runId
  // still matches.
  const loadRunRef = useRef(0);
  // Which folder the last open fetched, and when — the messages poll skips its
  // immediate first tick when it would just repeat this.
  const lastFolderLoadRef = useRef(null);
  const loadMessages = useCallback(async (folder) => {
    if (!folder) return;
    const runId = ++loadRunRef.current;
    lastFolderLoadRef.current = { folderId: folder.id, at: Date.now() };
    setLoadingMsgs(true);
    setSelectedMsg(null);
    setSearchQuery('');
    // Folder load exits thread mode. Discard the prior-filter snapshot
    // — the new folder choice is the user's explicit intent, so we
    // don't try to restore a thread-mode-saved filter on top of it.
    setThreadInfo(null);
    priorMsgFilterRef.current = null;
    setHasMore(false);
    setTodayCount(null);
    const hasActiveFilter = !!(msgFilter && msgFilter !== 'all');
    // Filter-active pages are larger so we finish pagination faster;
    // Graph's cap is 999 per page.
    const pageSize = hasActiveFilter ? 250
      : (isOutgoingFolder(folder) ? 50 : PAGE_SIZE);
    // Safety cap so a pathological filter hit (e.g. "unread" on an
    // inbox with 20 000 unread) doesn't lock up the tab.
    const MAX_ROWS = 2000;
    // Prime from cache on folder-switch ONLY — a cache prime while a
    // filter is active would show irrelevant rows briefly before the
    // server responds. networkPainted guards a race where the server
    // cache returns in <10 ms (faster than IndexedDB), so a late cache
    // resolve doesn't clobber fresh server data.
    let networkPainted = false;
    if (!hasActiveFilter) {
      mailCache.listRecent(folder.id, pageSize)
        .then(cached => {
          if (networkPainted || runId !== loadRunRef.current) return;
          if (cached.length) {
            // Hide IDs the user just deleted so they don't flash back
            // before the server fetch replaces the cache.
            const filtered = applyLocalReadFlag(cached.filter(m => !isRecentlyDeleted(m.id)));
            setMessages(filtered);
          } else {
            // Nothing cached for this folder: show the loader, not the
            // PREVIOUS folder's rows under this folder's name until the server
            // answers (~1 s for a Graph-served folder) — a click in that
            // window opened an email from the folder just left.
            setMessages([]);
          }
        })
        .catch(() => {});
    } else {
      setMessages([]);
    }
    try {
      let accumulated = [];
      let skip = 0;
      while (true) {
        if (runId !== loadRunRef.current) return; // user navigated away
        const qs = new URLSearchParams({ top: String(pageSize), skip: String(skip) });
        if (hasActiveFilter) qs.set('filter', msgFilter);
        const res  = await fetch(`/api/mail/folders/${encodeURIComponent(folder.id)}/messages?${qs}`);
        const data = await res.json();
        if (runId !== loadRunRef.current) return;
        const chunk = data.messages || [];
        if (!chunk.length) {
          // Server says no messages. If we already painted cached
          // rows from listRecent, they're stale — replace with empty
          // so the user sees the truth (otherwise an emptied folder
          // looks like it still has its old messages until the next
          // poll cycle). Only on the FIRST page (skip=0) — later
          // empty pages just mean we hit the end of pagination.
          if (skip === 0 && accumulated.length === 0) { networkPainted = true; setMessages([]); }
          break;
        }
        accumulated = accumulated.concat(chunk);
        networkPainted = true;
        // Filter out recently-deleted IDs in case the server cache is
        // still serving stale data within the 5-min guard window.
        setMessages(accumulated.filter(m => !isRecentlyDeleted(m.id)));
        mailCache.upsertMany(chunk, folder.id).catch(() => {});
        // Stop conditions:
        //   - No nextLink AND the page came back short  → Graph is out of results.
        //   - hasActiveFilter is false → only the first page (click Load More for the rest).
        //   - MAX_ROWS safety cap reached.
        if (!hasActiveFilter) break;
        if (!data.nextLink && chunk.length < pageSize) break;
        if (accumulated.length >= MAX_ROWS) break;
        skip += pageSize;
      }
      // For filtered views we've fetched everything we're going to fetch.
      // For normal views, keep Load More available if the first page filled.
      if (hasActiveFilter) setHasMore(false);
      else setHasMore(!!((accumulated.length === pageSize)));
    } finally {
      if (runId === loadRunRef.current) setLoadingMsgs(false);
    }
  }, [msgFilter]);

  const loadCustomerMessages = useCallback(async (customer) => {
    if (!customer?.domain) {
      // Customer has no email domain on file — clear list and stop
      setMessages([]);
      setSelectedMsg(null);
      setLoadingMsgs(false);
      setSearchQuery('');
      setHasMore(false);
      setTodayCount(null);
      return;
    }
    // Abort any in-flight customer fetch so switching quickly never shows stale results
    if (customerFetchRef.current) customerFetchRef.current.abort();
    const controller = new AbortController();
    customerFetchRef.current = controller;
    customerNextLink.current = null;

    setMessages([]);
    setLoadingMsgs(true);
    setSelectedMsg(null);
    setSearchQuery('');
    setHasMore(false);
    setTodayCount(null);
    try {
      const res  = await fetch(
        `/api/mail/search?q=${encodeURIComponent('from:' + customer.domain)}&top=25`,
        { signal: controller.signal }
      );
      const data = await res.json();
      if (!controller.signal.aborted) {
        setMessages(data.messages || []);
        customerNextLink.current = data.nextLink || null;
        setHasMore(!!data.nextLink);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
    } finally {
      if (!controller.signal.aborted) setLoadingMsgs(false);
    }
  }, []);

  // Thread mode — fetch every email in the local FTS index sharing the
  // open message's normalised subject + counterpart domain. Replaces the
  // current list with the thread results (matches how /search drives the
  // same list pane). The seed message stays selected so the reading pane
  // doesn't blank out while results load. See bot/src/mail/routes.js
  // → router.get('/thread', ...) for the matching server-side query.
  const loadThread = useCallback(async (msg) => {
    if (!msg?.id) return;
    setThreadLoading(true);
    // Clear any active search so the two modes don't overlap in the UI.
    setSearchQuery('');
    // Reset the client-side filter to 'all'. MessageList still applies
    // its Filter dropdown over whatever `messages` holds, so if the user
    // had Filter=Flagged active in the inbox, the thread results would
    // be filtered down to just the flagged ones — usually zero, which
    // looked like the bug "banner says 11 emails but list is empty".
    // The user's intent when clicking Thread is "show me everything in
    // this thread"; they can re-apply a filter inside thread mode if
    // they actually want it.
    //
    // Snapshot the PRIOR filter so Clear can restore it. Don't overwrite
    // the snapshot if the user clicks Thread again while already in
    // thread mode (priorMsgFilterRef is already set to the pre-thread
    // value; we don't want to overwrite it with the current 'all').
    if (priorMsgFilterRef.current == null) {
      priorMsgFilterRef.current = msgFilter;
    }
    setMsgFilter('all');
    try {
      // Forward the open message's known identifiers + envelope as a
      // fallback payload — Graph hands us either a "default" or an
      // "immutable" id depending on which endpoint surfaced the message,
      // and the local index stores only the default. Without this
      // fallback the thread lookup 404'd on every id-namespace mismatch
      // and the user got a misleading "older than the local index" banner
      // for an email that's right in front of them. The server prefers
      // the message_id lookup; the fallback is consulted only when both
      // the id and internet-message-id paths miss.
      const qp = new URLSearchParams({ messageId: msg.id });
      if (msg.subject)           qp.set('subject',           msg.subject);
      if (msg.from)              qp.set('from',              msg.from);
      if (msg.to)                qp.set('to',                msg.to);
      if (msg.conversationId)    qp.set('conversationId',    msg.conversationId);
      if (msg.internetMessageId) qp.set('internetMessageId', msg.internetMessageId);
      const res  = await fetch(`/api/mail/thread?${qp.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      // Seed not in the local index (LOCAL_INDEX_DAYS). Show a one-row "thread
      // unavailable" banner instead of an empty mystery state — the user
      // clicked Thread on this exact email and deserves an explanation.
      if (data.source === 'unavailable') {
        setThreadInfo({
          seedMessageId: msg.id,
          subject: msg.subject || '(no subject)',
          counterpartDomain: null,
          messageCount: 0,
          unavailable: true,
        });
        setMessages([msg]);  // keep the open email visible
        setHasMore(false);
        return;
      }

      setThreadInfo({
        seedMessageId: msg.id,
        subject: data.seed?.subject || msg.subject || '(no subject)',
        normalisedSubject: data.seed?.normalisedSubject || '',
        counterpartDomain: data.seed?.counterpartDomain || '',
        messageCount: (data.messages || []).length,
        // distinctDomains = how many DIFFERENT external sender domains
        // appear in the result set. When the subject is generic
        // ("Quote", "Re: Order Confirmation"), the subject-only match
        // can pull in unrelated customers' threads. >1 means at least
        // some cross-customer mixing; the banner uses this to warn
        // the user before they reply on the wrong thread.
        distinctDomains: data.seed?.distinctDomains || 1,
        unavailable: false,
      });
      setMessages(data.messages || []);
      setHasMore(false);   // local-only; no Graph pagination for thread mode
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('thread load failed', err?.message || err);
      setThreadInfo({
        seedMessageId: msg.id,
        subject: msg.subject || '(no subject)',
        unavailable: true,
        error: err?.message || 'load failed',
      });
    } finally {
      setThreadLoading(false);
    }
  }, []);

  const loadMorePage = useCallback(async () => {
    if (loadingMore) return;
    // While a free-text search is active, paginate the search itself
    // (Graph nextLink cursor) instead of loading folder pages. This
    // replaces the previous behaviour which capped results at the
    // first server page.
    if (searchQuery) {
      const cursor = searchNextLink.current;
      if (!cursor) { setHasMore(false); return; }
      setLoadingMore(true);
      try {
        const res  = await fetch(`/api/mail/search?cursor=${encodeURIComponent(cursor)}`);
        const data = await res.json();
        const list = data.messages || [];
        if (!list.length) { setHasMore(false); return; }
        setMessages(prev => {
          const seenIds  = new Set(prev.map(m => m.id));
          const seenKeys = new Set(prev.map(msgContentKey));
          return prev.concat(list.filter(m => !seenIds.has(m.id) && !seenKeys.has(msgContentKey(m))));
        });
        searchNextLink.current = data.nextLink || null;
        setHasMore(!!data.nextLink);
        mailCache.upsertMany(list).catch(() => {});
      } finally {
        setLoadingMore(false);
      }
      return;
    }
    setLoadingMore(true);
    try {
      if (selectedCustomer) {
        const cursor = customerNextLink.current;
        if (!cursor) { setHasMore(false); return; }
        const res  = await fetch(`/api/mail/search?q=${encodeURIComponent('from:' + selectedCustomer.domain)}&cursor=${encodeURIComponent(cursor)}`);
        const data = await res.json();
        const list = data.messages || [];
        if (!list.length) { setHasMore(false); return; }
        setMessages(prev => {
          const seenIds  = new Set(prev.map(m => m.id));
          const seenKeys = new Set(prev.map(msgContentKey));
          return prev.concat(list.filter(m => !seenIds.has(m.id) && !seenKeys.has(msgContentKey(m))));
        });
        customerNextLink.current = data.nextLink || null;
        setHasMore(!!data.nextLink);
      } else {
        if (!selectedFolder) return;
        const skip = messages.length;
        const qs = new URLSearchParams({ top: String(PAGE_SIZE), skip: String(skip) });
        if (msgFilter && msgFilter !== 'all') qs.set('filter', msgFilter);
        const res  = await fetch(`/api/mail/folders/${encodeURIComponent(selectedFolder.id)}/messages?${qs}`);
        const data = await res.json();
        const list = data.messages || [];
        if (!list.length) { setHasMore(false); return; }
        setMessages(prev => {
          const seenIds  = new Set(prev.map(m => m.id));
          const seenKeys = new Set(prev.map(msgContentKey));
          return prev.concat(list.filter(m => !seenIds.has(m.id) && !seenKeys.has(msgContentKey(m))));
        });
        setHasMore(!!data.nextLink || list.length === PAGE_SIZE);
      }
    } finally {
      setLoadingMore(false);
    }
  }, [selectedCustomer, selectedFolder, loadingMore, messages.length, msgFilter, searchQuery]);
  // One page at a time, decided synchronously. `loadingMore` is render state,
  // so two scroll events landing before the re-render both saw false and both
  // fetched the same skip — a duplicate index query + Graph reconcile, more
  // likely now the list asks for the next page two screens early.
  const loadMoreBusyRef = useRef(false);
  const loadMore = useCallback(async () => {
    if (loadMoreBusyRef.current) return;
    loadMoreBusyRef.current = true;
    try { await loadMorePage(); } finally { loadMoreBusyRef.current = false; }
  }, [loadMorePage]);

  useEffect(() => { loadMessages(selectedFolder); }, [selectedFolder, loadMessages]);

  // Fetch labels for whatever messages are currently on screen. Batched
  // POST /api/labels/messages → { id: label }. Only ids not already known
  // in labelMap and not attempted recently are requested, so paging and
  // polling stay cheap.
  //
  // Loop-guard pattern: `attemptedLabelIds` is a Map<id, timestamp> (a
  // ref so a thrown fetch can't cause a re-render loop per the
  // bot/docs/dashboard.md rules). The previous Set blocked an id
  // forever once asked, even if the server returned 200 with no label
  // because the message was still being classified — so a batch of
  // emails arriving mid-session stayed unlabeled until the user
  // refreshed. The Map lets ids re-attempt after a cooldown so
  // classifications that complete server-side get picked up by the
  // next polling tick.
  //
  // NEW mail is shown the moment it lands in the index — BEFORE the server has
  // queued and labelled it (thread history + the classifier take a few
  // seconds) — so the first ask for its label always comes back empty. With
  // only the 30 s cooldown, nothing asked again until the list next changed
  // (the 90 s SSE backstop) or the window regained focus: labels "took a while
  // to appear" (2026-10-15). So an email that arrived in the last
  // FRESH_LABEL_WINDOW_MS and is still unlabelled is asked about again after
  // each of FRESH_LABEL_RETRY_MS (lib/labelRetry.js).
  useEffect(() => {
    // Sent / Drafts / Outbox items never have meaningful labels — the
    // webhook only fires on Inbox-level `created` events, so outgoing
    // mail isn't in email_queue. Skip the round-trip for those views.
    if (isOutgoingFolder(selectedFolder)) return;
    const RETRY_AFTER_MS = 30_000;
    const now = Date.now();
    const ids = messages.map(m => m.id).filter(id => {
      if (!id || (id in labelMap)) return false;
      const last = attemptedLabelIds.current.get(id);
      return last == null || (now - last) > RETRY_AFTER_MS;
    });
    if (!ids.length) return;
    // Stamp BEFORE the fetch — if the fetch throws, the timestamp keeps
    // the id out of immediate re-fires while still allowing a retry
    // after the cooldown.
    for (const id of ids) attemptedLabelIds.current.set(id, now);
    // No cancel-on-rerun: a label answer is never stale, and new mail is
    // exactly when the list changes again mid-request (the server pokes when
    // the email is indexed AND when it is queued). Dropping that answer also
    // dropped the quick re-ask below, and the id then sat in its cooldown.
    (async () => {
      try {
        const res = await fetch('/api/labels/messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids }),
        });
        const data = await res.json();
        // Only store labels the server actually returned. Ids the
        // server didn't label (still classifying) stay out of labelMap
        // — the next polling tick after the cooldown re-asks for them.
        setLabelMap(prev => {
          const next = { ...prev };
          for (const id of ids) if (data[id]) next[id] = data[id];
          return next;
        });
        // Fresh mail the server hasn't labelled yet: ask again shortly.
        const plan = planFreshLabelRetry(messages, ids, data, freshLabelTries.current, Date.now());
        if (plan && freshLabelTimers.current) {
          plan.ids.forEach(id => freshLabelTries.current.set(id, (freshLabelTries.current.get(id) || 0) + 1));
          const t = setTimeout(() => {
            freshLabelTimers.current?.delete(t);
            plan.ids.forEach(id => attemptedLabelIds.current.delete(id));
            setLabelsFocusTick(n => n + 1);
          }, plan.delayMs);
          freshLabelTimers.current.add(t);
        }
      } catch { /* ignore — the cooldown stamp already gates re-fetch */ }
    })();
  }, [messages, labelMap, selectedFolder, labelsFocusTick]);
  // On unmount (sign-out, mailbox switch): cancel pending re-asks, and null
  // the set so an answer landing afterwards schedules none.
  useEffect(() => {
    if (!freshLabelTimers.current) freshLabelTimers.current = new Set();
    return () => { freshLabelTimers.current?.forEach(clearTimeout); freshLabelTimers.current = null; };
  }, []);

  // Force a labels re-fetch whenever the tab regains focus or
  // visibility. Browsers throttle setInterval on hidden tabs
  // (Chrome → once/min), so a user who steps away can come back to
  // an inbox where the recent emails arrived during the throttle
  // and their labels were "attempted" at a moment when the server
  // hadn't classified them yet. The 30 s cooldown then keeps them
  // unlabelled even after returning, until the user manually
  // refreshed. On visibility/focus we clear the cooldown for any
  // visible message that still lacks a label, then bump
  // labelsFocusTick so the labels useEffect above re-runs straight
  // away. Refs hold the latest messages/labelMap so this listener
  // can attach once.
  const labelsListRef = useRef({ messages, labelMap });
  labelsListRef.current = { messages, labelMap };
  useEffect(() => {
    function refreshIfVisible() {
      if (typeof document !== 'undefined' && document.hidden) return;
      const { messages: ms, labelMap: lm } = labelsListRef.current;
      let cleared = false;
      for (const m of ms || []) {
        const id = m?.id;
        if (!id || (id in lm)) continue;
        if (attemptedLabelIds.current.has(id)) {
          attemptedLabelIds.current.delete(id);
          cleared = true;
        }
      }
      // Bump even when nothing was cleared — keeps the effect
      // re-runnable in edge cases where labelMap thinks an id is
      // present but the server has updated the label since.
      setLabelsFocusTick(t => t + 1);
      void cleared;
    }
    document.addEventListener('visibilitychange', refreshIfVisible);
    window.addEventListener('focus', refreshIfVisible);
    return () => {
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.removeEventListener('focus', refreshIfVisible);
    };
  }, []);

  // Reactions — same batched-fetch shape as labels. Keyed by
  // graph_message_id → { count, ownerReactionType, ownerReactionTime } | null.
  // Stored as state so handleReactToMessage can optimistically push the
  // user's own reaction in immediately; the Graph webhook later confirms.
  // attemptedReactionIds (ref) is the loop guard from the labels Rule 1
  // pattern — see CLAUDE.md "React effect loop prevention".
  useEffect(() => {
    if (isOutgoingFolder(selectedFolder)) return;
    const ids = messages.map(m => m.id).filter(id =>
      id && !(id in reactionMap) && !attemptedReactionIds.current.has(id)
    );
    if (!ids.length) return;
    for (const id of ids) attemptedReactionIds.current.add(id);
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/mail/reactions/messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids }),
        });
        const data = await res.json();
        if (cancelled) return;
        setReactionMap(prev => {
          const next = { ...prev };
          for (const id of ids) next[id] = data[id] ?? null;
          return next;
        });
      } catch { /* attemptedReactionIds already locked these out */ }
    })();
    return () => { cancelled = true; };
  }, [messages, reactionMap, selectedFolder]);

  // Merge labels + reactions into messages so MessageList / ReadingPane
  // see msg.label and msg.reactions. Memoised (Section 6 #2) so it only
  // rebuilds when messages/labels/reactions change — not on every unrelated
  // App re-render (badge counts, drawer state, …).
  // A row whose source object, label and reactions are unchanged keeps the SAME
  // decorated object across rebuilds (the WeakMap), so the memoised MessageRow
  // skips it — otherwise every label/reaction answer or appended page gave
  // every labelled row a new object and the whole list re-rendered.
  const decoratedCacheRef = useRef(new WeakMap());
  const decoratedMessages = useMemo(() => messages.map(m => {
    const label = labelMap[m.id];
    const reactions = reactionMap[m.id];
    if (!label && !reactions) return m;
    const hit = decoratedCacheRef.current.get(m);
    if (hit && hit.label === label && hit.reactions === reactions) return hit.out;
    let next = m;
    if (label)     next = { ...next, label };
    if (reactions) next = { ...next, reactions };
    decoratedCacheRef.current.set(m, { label, reactions, out: next });
    return next;
  }), [messages, labelMap, reactionMap]);

  // Unread badge for the folder you're CURRENTLY viewing is reconciled against
  // the loaded message list so the count can't tick up before the row it counts:
  //   - Graph updates a folder's unread metadata a beat before its message-list
  //     endpoint returns the new mail, so a Graph-driven badge LEADS the row
  //     ("the 1 shows, then the email loads").
  //   - But trusting the loaded list alone UNDER-counts a backlog sitting beyond
  //     the loaded page (return-from-holiday, a non-inbox folder) — the inverse
  //     bug.
  // Resolution: DEFER only a small positive gap (Graph just ahead of the list by
  // at most a burst of new arrivals the next poll will load) and trust Graph's
  // count when the gap is large (a real off-page backlog). loadedUnread counts
  // only rows that belong to the open folder, so search stragglers briefly left
  // in `messages` can't inflate it. Memoised so it doesn't re-walk messages + the
  // folder tree on every unrelated render. Gated to the open folder, no search /
  // filter / mid-load; otherwise Graph's count stands untouched.
  const NEW_ARRIVAL_DEFER = 5;  // gap this small = treat as not-yet-loaded new mail
  const effectiveFolders = useMemo(() => {
    const derive = !!selectedFolder && !searchQuery
      && (!msgFilter || msgFilter === 'all') && !loadingMsgs;
    if (!derive) return folders;
    const fid = selectedFolder.id;
    const loadedUnread = messages.reduce(
      (n, m) => n + (m && !m.isRead && (m.parentFolderId == null || m.parentFolderId === fid) ? 1 : 0),
      0,
    );
    const reconcile = (list) => list.map(f => {
      const kids = f.childFolders ? reconcile(f.childFolders) : f.childFolders;
      if (f.id === fid) {
        const graph = f.unreadItemCount || 0;
        const gap = graph - loadedUnread;
        // Defer a small gap ONLY transiently. It exists for new arrivals the
        // next poll will load — but a gap that survives a completed poll is
        // not new mail, it is the index disagreeing with Graph on some rows'
        // read state (unread drift; scripts/diag-unread-drift.js finds them).
        // Deferring it forever made the badge FLIP with folder selection: 2
        // while the Inbox was open, 4 the moment you clicked Sent Items (where
        // this override does not apply). Graph's count is the authoritative
        // badge, so once the gap has persisted across a poll, show it — then
        // the number no longer depends on what you happen to be looking at.
        let shown = graph;
        if (gap > 0 && gap <= NEW_ARRIVAL_DEFER) {
          const d = badgeDeferRef.current;
          const persisted = !!d && d.fid === fid && d.gap === gap && pollGen > d.gen;
          if (!persisted) {
            if (!d || d.fid !== fid || d.gap !== gap) badgeDeferRef.current = { fid, gap, gen: pollGen };
            shown = loadedUnread;
          }
        } else if (badgeDeferRef.current && badgeDeferRef.current.fid === fid) {
          badgeDeferRef.current = null;
        }
        return { ...f, unreadItemCount: shown, childFolders: kids };
      }
      return kids === f.childFolders ? f : { ...f, childFolders: kids };
    });
    return reconcile(folders);
  }, [folders, messages, selectedFolder, searchQuery, msgFilter, loadingMsgs, pollGen]);

  // Auto-refresh. Every 60 s:
  //   1. Poll the currently-open folder. Prepend any messages newer than the
  //      current top AND update read/flagged/completed on existing rows, so
  //      marking an email read in Outlook / on mobile reflects here without
  //      the user clicking Refresh.
  //   2. Poll the today-count endpoint for server-truth daily total.
  // Paused while searching or in thread mode. cache:'no-store' + cache-buster
  // prevents the browser from handing back a stale response.
  // Cost: ~2 small Graph API calls every 60 s per open tab. The Graph
  // webhook still pushes new mail / read-state flips in real time —
  // polling is a fallback when the subscription is briefly down.
  useEffect(() => {
    // While searching / composing / in thread mode the poll is paused,
    // so expose no message-refresh to the folders poll — it must not
    // bump the badge ahead of a list it can't refresh right now.
    //
    // Thread mode: `messages` is the result of a thread lookup, NOT the
    // folder's contents. If the poll fires it fetches the folder, then
    // setMessages(prev => merge(incoming, prev)) prepends the folder's
    // new inbox rows onto the thread list — the user perceives this as
    // "thread view eventually clearing back to the inbox" because over
    // a few polls the thread rows get pushed off the top by unrelated
    // incoming mail. Pausing the poll until Clear keeps the list stable.
    //
    // Composing does NOT pause it (it did until 2026-10-07). The list stays
    // visible beside the compose pane, so pausing froze it for as long as a
    // reply was open — and it nulled pollMessagesRef, so SSE pokes and the
    // unread-went-up refresh did nothing either. Mail that the server had
    // indexed within a minute "appeared as soon as I replied", because closing
    // the compose re-ran this effect. The folders poll already re-renders the
    // app under an open compose, and the poll's merge only touches list rows.
    pollMessagesRef.current = null;
    if (!selectedFolder) return undefined;
    if (searchQuery || threadInfo) return undefined;

    // Adaptive cadence (Phase 3): when the SSE stream is live it delivers
    // near-real-time pokes, so this periodic poll is only a safety backstop —
    // run it slowly (5 min). With SSE off/disconnected it's the primary refresh
    // and runs at the normal 60s. Event-driven refreshes (focus, SSE poke) are
    // unaffected and always immediate.
    const POLL_MS = sseConnected ? SSE_BACKSTOP_MS : 60_000;
    let cancelled = false;
    const folderId = selectedFolder.id;

    async function pollMessages(reconcile = false) {
      // Hidden tabs contribute ZERO Graph calls. A background tab left
      // open used to keep polling every ~60s, and several of them stacked
      // up into the foreground slot contention seen in the slot logs. The
      // visibilitychange catch-up below refreshes instantly on refocus.
      // reconcile=true (refocus resync) drops externally-deleted rows on the
      // first miss instead of waiting for a second poll — see the prune below.
      if (typeof document !== 'undefined' && document.hidden) return false;
      let gained = false;
      try {
        const qs = new URLSearchParams({ top: String(PAGE_SIZE), _: String(Date.now()) });
        if (msgFilter && msgFilter !== 'all') qs.set('filter', msgFilter);
        // bg=1 → the server runs this timer poll's Graph calls on the reserved
        // background slot so an interactive read/send never queues behind it.
        // Interactive folder-opens (which fetch the same route) omit it.
        qs.set('bg', '1');
        const res  = await fetch(
          `/api/mail/folders/${encodeURIComponent(folderId)}/messages?${qs}`,
          { cache: 'no-store' }
        );
        const data = await res.json();
        if (cancelled) return false;
        const incoming = data.messages || [];
        // Did this fetch bring a row the list does not already show? Answered
        // against the ref, before the merge — the caller (refreshFolders) uses
        // it to decide whether the index has caught up with the badge yet.
        {
          const have     = messagesRef.current || [];
          const haveIds  = new Set(have.map(m => m.id));
          const haveKeys = new Set(have.map(msgContentKey));
          gained = incoming.some(m => !haveIds.has(m.id) && !haveKeys.has(msgContentKey(m)));
        }
        setMessages(prev => {
          if (!prev.length) return incoming;
          const byId  = new Map(incoming.map(m => [m.id, m]));
          // Also index by content key: a message re-fetched under a different
          // id namespace (immutable vs default) is the SAME email, so match on
          // either — otherwise the prune below sees a cross-namespace row as
          // "vanished" and re-adds it as a fresh top row every poll.
          const byKey = new Map(incoming.map(m => [msgContentKey(m), m]));
          const RECENT_MS = 30_000;
          const now = Date.now();
          let changed = false;
          // Prune rows that have vanished from the folder — deleted or moved
          // elsewhere (e.g. from Outlook or a phone). Without this the poll
          // only ever updates/prepends, so a message removed on another
          // device lingers until a full folder reload. The poll fetches only
          // the top page, so bound the prune to the window `incoming` covered:
          // never drop a row NEWER than the newest fetched message (a just-
          // arrived optimistic top row), and — when the page came back full —
          // never drop one OLDER than the oldest fetched message (it's on a
          // later Load-More page, which this page didn't cover). A short page
          // (< PAGE_SIZE) means the fetch saw the whole folder, so any absent
          // in-window row is genuinely gone. Pure array math over data the
          // poll already fetched — no extra Graph calls, no speed cost.
          const filterActive   = !!(msgFilter && msgFilter !== 'all');
          const pageWin        = pageWindow(incoming, PAGE_SIZE);
          const missCounts     = pruneMissRef.current;
          const heldOptimistic = (id) => {
            const r = recentReadOp.current.get(id);
            const f = recentFlagOp.current.get(id);
            const p = recentReplyOp.current.get(id);
            return (r && now - r.at < PENDING_OP_MAX_MS)
                || (f && now - f.at < PENDING_OP_MAX_MS)
                || (p && now - p.at < RECENT_MS);
          };
          // Is an absent row genuinely a prune candidate THIS poll? The rules
          // (filtered poll, no dated rows, op guards, a later Load-More page,
          // a just-arrived top row — for two minutes, not forever) live in
          // lib/pollPrune.js, where they are unit-tested.
          const isPruneCandidate = (m) => pollPruneCandidate(m, { window: pageWin, filterActive, heldOptimistic, now });
          const updated = prev.map(m => {
            const inc = byId.get(m.id) || byKey.get(msgContentKey(m));
            if (!inc) {
              const key = msgContentKey(m);
              // Deleted here by the user → drop immediately, no grace period.
              if (isRecentlyDeleted(m.id)) { missCounts.delete(key); changed = true; return null; }
              if (isPruneCandidate(m)) {
                const misses = (missCounts.get(key) || 0) + 1;
                // Steady polling requires TWO consecutive misses before
                // dropping, so a single jittery poll where the row briefly
                // falls out of the page doesn't flicker it out. But a RECONCILE
                // poll (return-from-hidden refocus) is an authoritative resync
                // after the tab was idle — like a manual page refresh — so it
                // drops an in-window absent row on the FIRST miss. Without this,
                // a message deleted on another device while you were away still
                // showed until a second poll (up to the 5-min SSE backstop
                // later); one refocus wasn't enough, only a full reload was.
                if (misses >= (reconcile ? 1 : 2)) { missCounts.delete(key); changed = true; return null; }
                missCounts.set(key, misses);
              } else {
                missCounts.delete(key);  // not a candidate → reset any streak
              }
              return m;
            }
            missCounts.delete(msgContentKey(m)); // present again → clear miss streak
            // If the user marked read/unread in the last 30 s, keep their
            // local isRead — server hasn't propagated yet (or the PATCH is
            // in flight). Otherwise trust server.
            const local = recentReadOp.current.get(m.id);
            if (local && now - local.at > PENDING_OP_MAX_MS) {
              recentReadOp.current.delete(m.id);
            } else if (local && inc.isRead === local.isRead) {
              recentReadOp.current.delete(m.id);
            }
            const activeRead = recentReadOp.current.get(m.id);
            const useLocal = !!activeRead;
            const effectiveRead = useLocal ? activeRead.isRead : inc.isRead;
            // Same protection for flag/completed — the polling tick used
            // to clobber Ctrl+D's optimistic mark-done if Graph hadn't
            // settled the flag PATCH yet. Now we keep the local value
            // for 30 s, then trust server.
            const localFlag = recentFlagOp.current.get(m.id);
            if (localFlag && now - localFlag.at > PENDING_OP_MAX_MS) {
              recentFlagOp.current.delete(m.id);
            } else if (localFlag && inc.flagged === localFlag.flagged && inc.completed === localFlag.completed) {
              recentFlagOp.current.delete(m.id);
            }
            const activeFlag = recentFlagOp.current.get(m.id);
            const useLocalFlag = !!activeFlag;
            const effectiveFlagged   = useLocalFlag ? activeFlag.flagged   : inc.flagged;
            const effectiveCompleted = useLocalFlag ? activeFlag.completed : inc.completed;
            // Replied / forwarded come from PR_ICON_INDEX on the message —
            // Outlook updates them when the user replies / forwards on
            // any device (including from Outlook proper, not just the
            // dashboard). Without keeping these in sync the reply
            // indicator would only appear after a manual page refresh,
            // which the user reported as bug #21. Guard with recentReplyOp
            // (30 s) so a poll between send and Graph's icon-index propagation
            // can't wipe the optimistic arrow.
            const localReply = recentReplyOp.current.get(m.id);
            const useLocalReply = localReply && now - localReply.at < RECENT_MS;
            const effectiveReplied   = useLocalReply ? localReply.replied   : inc.replied;
            const effectiveForwarded = useLocalReply ? localReply.forwarded : inc.forwarded;
            if (m.isRead !== effectiveRead
                || m.flagged !== effectiveFlagged
                || m.completed !== effectiveCompleted
                || m.replied !== effectiveReplied
                || m.forwarded !== effectiveForwarded) {
              changed = true;
              return {
                ...m,
                isRead:    effectiveRead,
                flagged:   effectiveFlagged,
                completed: effectiveCompleted,
                replied:   effectiveReplied,
                forwarded: effectiveForwarded,
              };
            }
            return m;
          });
          // Dedupe by id AND content key: a row cached under one Graph id
          // namespace (e.g. an immutable id) and re-fetched under the other
          // (default id) is the SAME email — id-only dedup would prepend it as
          // a phantom new row in the live list (the cross-namespace duplicate
          // msgContentKey was added to kill, on the poll path too).
          // Drop pruned (null) rows before deduping / returning.
          const kept = updated.filter(Boolean);
          const knownIds  = new Set(kept.map(m => m.id));
          const knownKeys = new Set(kept.map(msgContentKey));
          // Bound the miss counter to rows still in the list, so streaks for
          // rows that left for any other reason don't accumulate in the ref.
          for (const k of missCounts.keys()) if (!knownKeys.has(k)) missCounts.delete(k);
          const newOnes = incoming.filter(m => !knownIds.has(m.id) && !knownKeys.has(msgContentKey(m)));
          if (!newOnes.length && !changed) return prev;
          return [...newOnes, ...kept];
        });
        // Keep selectedMsg's read/flag/replied/forwarded state in sync
        // with Outlook too — applying the same recentFlagOp / recentReadOp
        // protection so the polling tick can't clobber a fresh Ctrl+D.
        setSelectedMsg(prev => {
          if (!prev) return prev;
          const inc = (data.messages || []).find(m => m.id === prev.id);
          if (!inc) return prev;
          const now = Date.now();
          const RECENT_MS = 30_000;
          const localRead  = recentReadOp.current.get(prev.id);
          const localFlag  = recentFlagOp.current.get(prev.id);
          const localReply = recentReplyOp.current.get(prev.id);
          if (localRead && now - localRead.at > PENDING_OP_MAX_MS) {
            recentReadOp.current.delete(prev.id);
          } else if (localRead && inc.isRead === localRead.isRead) {
            recentReadOp.current.delete(prev.id);
          }
          if (localFlag && now - localFlag.at > PENDING_OP_MAX_MS) {
            recentFlagOp.current.delete(prev.id);
          } else if (localFlag && inc.flagged === localFlag.flagged && inc.completed === localFlag.completed) {
            recentFlagOp.current.delete(prev.id);
          }
          const activeRead = recentReadOp.current.get(prev.id);
          const activeFlag = recentFlagOp.current.get(prev.id);
          const useLocalRead  = !!activeRead;
          const useLocalFlag  = !!activeFlag;
          const useLocalReply = localReply && now - localReply.at < RECENT_MS;
          const effRead      = useLocalRead  ? activeRead.isRead    : inc.isRead;
          const effFlagged   = useLocalFlag  ? activeFlag.flagged   : inc.flagged;
          const effCompleted = useLocalFlag  ? activeFlag.completed : inc.completed;
          const effReplied   = useLocalReply ? localReply.replied   : inc.replied;
          const effForwarded = useLocalReply ? localReply.forwarded : inc.forwarded;
          if (prev.isRead === effRead
              && prev.flagged === effFlagged
              && prev.completed === effCompleted
              && prev.replied === effReplied
              && prev.forwarded === effForwarded) return prev;
          return {
            ...prev,
            isRead:    effRead,
            flagged:   effFlagged,
            completed: effCompleted,
            replied:   effReplied,
            forwarded: effForwarded,
          };
        });
        // Persist the poll's fresh read/flag state into the IndexedDB cache
        // (guarded by recentReadOp/recentFlagOp). Without this the cache only
        // refreshed on a full folder load, so an email read in Outlook while
        // the inbox is open updated the on-screen list but NOT the cache — and
        // the next return-from-search primed the stale cache, showing it
        // unread for an instant before the network fetch corrected it.
        mailCache.upsertMany(applyLocalReadFlag(incoming), folderId).catch(() => {});
        setPollGen(g => g + 1);
      } catch { /* next tick retries */ }
      return gained;
    }

    async function pollTodayCount() {
      if (typeof document !== 'undefined' && document.hidden) return; // hidden tabs stay silent
      try {
        const res  = await fetch(
          `/api/mail/folders/${encodeURIComponent(folderId)}/today-count?_=${Date.now()}&bg=1`,
          { cache: 'no-store' }
        );
        const data = await res.json();
        if (cancelled) return;
        if (typeof data.count === 'number') setTodayCount(data.count);
      } catch { /* ignore */ }
    }

    // Expose this folder's message refresh so the folders poll can pull new
    // rows in before bumping the unread badge (see refreshFolders). Returns
    // pollMessages' promise so the caller can await the merge.
    pollMessagesRef.current = pollMessages;

    // Not when a folder open has just fetched this same page (loadMessages'
    // effect runs first in the same commit): the poll repeated it at once,
    // so every folder switch downloaded the list twice and re-rendered it a
    // second time. The interval and every other trigger poll as before.
    const justLoaded = lastFolderLoadRef.current;
    if (!(justLoaded && justLoaded.folderId === folderId && Date.now() - justLoaded.at < 5000)) pollMessages();
    pollTodayCount();
    const t1 = setInterval(pollMessages,    POLL_MS);
    const t2 = setInterval(pollTodayCount,  POLL_MS);
    // Catch up the instant the tab is focused again — the interval ticks
    // are no-ops while hidden, so without this a backgrounded tab would
    // show stale state for up to a full POLL_MS after refocus.
    const onVisible = () => {
      // Authoritative resync on refocus: reconcile=true so a message deleted /
      // moved on another device while the tab was hidden drops on this first
      // poll, matching what a manual page reload does.
      if (typeof document !== 'undefined' && !document.hidden) { pollMessages(true); pollTodayCount(); }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(t1);
      clearInterval(t2);
      document.removeEventListener('visibilitychange', onVisible);
      if (pollMessagesRef.current === pollMessages) pollMessagesRef.current = null;
    };
  }, [selectedFolder, searchQuery, msgFilter, threadInfo, sseConnected]);

  // ── Search ───────────────────────────────────────────────────────────────────
  function pushSearchHistory(q) {
    const trimmed = (q || '').trim();
    if (trimmed.length < 2) return; // skip single-char noise
    setSearchHistory(prev => {
      const next = [trimmed, ...prev.filter(x => x.toLowerCase() !== trimmed.toLowerCase())].slice(0, 10);
      try { localStorage.setItem('pp_search_history', JSON.stringify(next)); } catch {}
      return next;
    });
  }
  function clearSearchHistory() {
    setSearchHistory([]);
    try { localStorage.removeItem('pp_search_history'); } catch {}
  }

  // Cache-first search:
  //
  //   1. Local IndexedDB scan fires immediately — feels instant. Tagged
  //      via setSearchPreview(true) so the UI can show a "Showing local
  //      results — refining…" hint.
  //   2. A preview=1 server search fires after a short 250 ms pause: the
  //      server's local index only (FTS over subject, people AND body; no
  //      Graph), so real results paint within a round trip of the pause
  //      instead of after the full search (~2 s in prod). It replaces the
  //      cached rows when it has hits and is ignored once the full answer
  //      has painted.
  //   3. The full server search fires after a 700 ms typing pause
  //      (debounce). It goes through the KQL rewriter
  //      (bot/src/mail/searchQuery.js) and contact resolver
  //      (bot/src/mail/contactResolver.js) so a name-typed query like
  //      "Tom England" ranks actual Tom-England messages above noisy
  //      address-substring hits, reconciles live state and adds Graph.
  //      The server shares one FTS query between the preview and this.
  //
  // Critical correctness rules, learned from earlier user-reported bugs:
  //
  //   - DON'T let stale local-cache results outlive the server response.
  //     Old behaviour was to MERGE local + server, which left
  //     "rosspromot"-era rows visible after the user had typed
  //     "rosspromotional" and the server returned the proper subset.
  //     New behaviour: server response REPLACES the result list. Local
  //     cache only fills the gap until the server returns; once it
  //     does, we trust it.
  //
  //   - DO show local results while the server is still resolving so
  //     the user sees something immediately. But never show local
  //     results for an OUTDATED query — the searchQueryRef gate
  //     handles that.
  //
  //   - DO cancel in-flight server requests with AbortController on
  //     each new keystroke. Without this, a slow request for "ross"
  //     could land after a fast one for "rosspromotional" and clobber
  //     the user's newer query.
  //
  //   - DO clear messages explicitly on a void result so an empty
  //     server response shows "No messages" rather than a stale list.
  function handleSearch(q, opts = {}) {
    const { excludeSent: optExcludeSent } = opts;
    // A fresh search (nothing was being searched) always starts with "Include
    // sent items" TICKED, however the last one ended. Clearing with ✕ reset
    // it, but leaving a search by clicking a folder set it UNticked, so the
    // next search quietly hid your own sent mail (taylor@, 2026-10-22).
    const freshSearch = typeof optExcludeSent !== 'boolean' && !searchQueryRef.current && !!q.trim();
    const useExcludeSent = typeof optExcludeSent === 'boolean' ? optExcludeSent : (freshSearch ? false : excludeSent);
    if (freshSearch && excludeSent) setExcludeSent(false);
    setSearchQuery(q);
    // Typing in the search box exits thread mode. Same logic as
    // loadMessages — a fresh search is an explicit user intent, so
    // we drop the snapshot rather than try to restore later.
    setThreadInfo(null);
    priorMsgFilterRef.current = null;
    clearTimeout(searchTimer.current);
    clearTimeout(searchPreviewTimer.current);
    searchFullDoneRef.current = null;
    // Cancel any in-flight server search (full or preview) — the next
    // keystroke owns the result-set now.
    for (const ref of [searchAbortRef, searchPreviewAbortRef]) {
      if (ref.current) {
        try { ref.current.abort(); } catch {}
        ref.current = null;
      }
    }
    if (!q.trim()) {
      searchQueryRef.current = '';
      setSearching(false);
      setSearchPreview(false);
      setResolvedContacts([]);
      setSearchSource(null);
      // Reset the sent-items toggle — every new search starts with
      // "Include sent items" TICKED (sent included) so the user sees
      // full conversations by default. Untick is a per-search opt-in
      // when the user wants the narrower view.
      setExcludeSent(false);
      if (selectedCustomer) { loadCustomerMessages(selectedCustomer); return; }
      loadMessages(selectedFolder);
      return;
    }
    // Reset pagination cursor — the search response below populates
    // searchNextLink and toggles hasMore based on Graph's nextLink.
    searchNextLink.current = null;
    setHasMore(false);
    searchQueryRef.current = q;
    searchLocalHitsRef.current = [];   // reset every new query
    setSelectedMsg(null);
    setSearchPreview(true);
    // Local scan — instant fill. Cleared if a newer query has been
    // typed by the time it returns.
    mailCache.searchLocal(q).then(local => {
      if (searchQueryRef.current !== q) return;
      // Only paint if the user is still on the same query AND the
      // local hits actually contain the substring (cheap re-filter so
      // a partial-prefix scan from before doesn't leak through).
      const lower = q.toLowerCase();
      let filtered = (local || []).filter(m =>
        (m.subject  && m.subject.toLowerCase().includes(lower)) ||
        (m.from     && m.from.toLowerCase().includes(lower)) ||
        (m.fromName && m.fromName.toLowerCase().includes(lower))
      );
      // Mirror the server-side folder scope on the local cache preview
      // — when the user searches from Sent Items / Drafts / Outbox we
      // shouldn't briefly paint inbox results and then strip them
      // when the server response lands.
      if (selectedFolder && isOutgoingFolder(selectedFolder)) {
        filtered = filtered.filter(m => {
          const fid = m.parentFolderId || m.folder_id;
          return fid === selectedFolder.id;
        });
      }
      // Mirror the excludeSent toggle on the local preview so cached
      // sent-items don't briefly flash up before the server response
      // strips them out. Two-pronged check matching the server-side
      // filter:
      //   1. `from === userMailbox` — catches every from-me message
      //      regardless of which folder it currently lives in. This
      //      mirrors the dashboard's own SENT-pill rendering rule
      //      (MessageList.js: isSent = msg.from === mailbox).
      //   2. parentFolderId === Sent Items — defensive belt-and-
      //      braces for messages where `from` is unset / formatted
      //      oddly. We resolve the Sent folder id from the
      //      already-loaded folder tree.
      // The cache stores parentFolderId as `folder_id` and live
      // server rows as `parentFolderId`; check both.
      if (useExcludeSent) {
        const mbLower = (mailbox || '').toLowerCase();
        const sentFolder = folders.find(f =>
          /^sent\b/i.test(f.displayName || '') ||
          (f.displayName || '').toLowerCase() === 'sent items'
        );
        const sentId = sentFolder?.id;
        filtered = filtered.filter(m => {
          const fromLower = (m.from || '').toLowerCase();
          if (mbLower && fromLower === mbLower) return false;
          if (sentId) {
            const fid = m.parentFolderId || m.folder_id;
            if (fid === sentId) return false;
          }
          return true;
        });
      }
      // Stash local hits keyed by id so the server-response callback
      // below can merge them in. Without this, a server response of
      // [] would wipe local matches that Graph's $search just hadn't
      // indexed yet (recent-arrival lag is 1-10 minutes in practice).
      searchLocalHitsRef.current = filtered;
      if (filtered.length) setMessages(filtered);
    }).catch(() => {});
    const searchParams = () => {
      const params = new URLSearchParams({ q });
      // When the user kicks off a search from an outgoing folder
      // (Sent Items, Drafts, Outbox), scope to that folder. The
      // "exclude sent items" filter is meaningless in that case
      // (every result IS a sent item), so we drop it.
      const inOutgoing = selectedFolder && isOutgoingFolder(selectedFolder);
      if (inOutgoing) {
        params.set('folderId', selectedFolder.id);
        if (selectedFolder.displayName) params.set('folderName', selectedFolder.displayName);
      }
      if (useExcludeSent && !inOutgoing) params.set('excludeSent', '1');
      return params;
    };
    // Server rows first, then any local-cache hits the server did not return
    // (see the merge note in the full search below). Matched by content too,
    // so a local-cache row that is the SAME email as a server hit but carries
    // a different Graph id (default vs immutable namespace) is a duplicate,
    // not a straggler — otherwise it renders as a second identical row.
    const withLocalStragglers = (list) => {
      const serverIds  = new Set(list.map(m => m.id));
      const serverKeys = new Set(list.map(msgContentKey));
      const localStragglers = (searchLocalHitsRef.current || [])
        .filter(m => m && m.id
          && !serverIds.has(m.id)
          && !serverKeys.has(msgContentKey(m))
          && !isRecentlyDeleted(m.id));
      return localStragglers.length ? [...list, ...localStragglers] : list;
    };
    searchPreviewTimer.current = setTimeout(async () => {
      const ac = new AbortController();
      searchPreviewAbortRef.current = ac;
      try {
        const params = searchParams();
        params.set('preview', '1');
        const res  = await fetch(`/api/mail/search?${params}`, { signal: ac.signal });
        const data = await res.json();
        if (searchQueryRef.current !== q || ac.signal.aborted || searchFullDoneRef.current === q) return;
        const list = data.messages || [];
        // Nothing in the local index: keep what the cache painted and let
        // the full search (which can reach Graph) decide.
        if (!list.length) return;
        setMessages(withLocalStragglers(list));
        setResolvedContacts(data.resolvedContacts || []);
      } catch { /* aborted by the next keystroke, or failed — the full search still runs */ }
    }, 250);
    searchTimer.current = setTimeout(async () => {
      const ac = new AbortController();
      searchAbortRef.current = ac;
      setSearching(true);
      try {
        const res  = await fetch(`/api/mail/search?${searchParams()}`, { signal: ac.signal });
        const data = await res.json();
        if (searchQueryRef.current !== q || ac.signal.aborted) return;
        const list = data.messages || [];
        // Merge with local hits. Graph's $search index lags real-time
        // by 1-10 minutes — so a brand-new email that the webhook has
        // already pushed into mailCache (and which our local search
        // finds instantly) won't appear in the server response yet.
        // The earlier "REPLACE — server is source of truth" rule wiped
        // those hits and made the user think the email was gone:
        // they'd see 2 matches flash up then disappear.
        //
        // Merge order: server hits first (their ordering + metadata
        // is authoritative), then any local-only stragglers tacked on
        // at the end. Recently-deleted ids stay filtered out so a
        // cache row that's already been moved/deleted by the user
        // doesn't get resurrected (withLocalStragglers above).
        searchFullDoneRef.current = q;
        setMessages(withLocalStragglers(list));
        setResolvedContacts(data.resolvedContacts || []);
        setSearchSource(data.source || null);
        setSearchPreview(false);
        searchNextLink.current = data.nextLink || null;
        setHasMore(!!data.nextLink);
        mailCache.upsertMany(list).catch(() => {});
      } catch (err) {
        // AbortError is expected when the user keeps typing — silent.
        // Other failures keep whatever local results we already
        // painted; the user can retry by typing again.
        if (err?.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('search failed', err?.message || err);
        }
      } finally {
        // Clear BOTH here (not just on the success path) so a failed/aborted
        // server search doesn't leave the "refining…" hint stuck on screen.
        if (searchQueryRef.current === q) { setSearching(false); setSearchPreview(false); }
      }
      pushSearchHistory(q);
    }, 700);  // local results paint instantly above; only the Graph augmentation
              // waits this out. Widened 350->700 to cut Graph search load under
              // throttling (server also caches + abort-bails the Graph legs now).
  }

  // Re-run the active search against Graph regardless of what the
  // local FTS index has. Triggered by the "Search older mail" footer
  // button — the user already saw the local-window results and wants
  // anything older. Appends new rows, dedupes by id, keeps source as
  // 'graph' so the button doesn't keep re-appearing.
  async function searchOlder() {
    const q = searchQueryRef.current;
    if (!q || searchingOlder) return;
    setSearchingOlder(true);
    try {
      const params = new URLSearchParams({ q, older: '1' });
      const inOutgoing = selectedFolder && isOutgoingFolder(selectedFolder);
      if (inOutgoing) {
        params.set('folderId', selectedFolder.id);
        if (selectedFolder.displayName) params.set('folderName', selectedFolder.displayName);
      }
      if (excludeSent && !inOutgoing) params.set('excludeSent', '1');
      const res  = await fetch(`/api/mail/search?${params}`);
      const data = await res.json();
      if (searchQueryRef.current !== q) return;
      const incoming = data.messages || [];
      // Append, dedupe by id AND content key (preserves the existing rows on
      // top). The content key stops a cross-id-namespace duplicate of an
      // already-shown message from being appended (see msgContentKey).
      setMessages(prev => {
        const seenIds  = new Set(prev.map(m => m.id));
        const seenKeys = new Set(prev.map(msgContentKey));
        const extras = incoming.filter(m => !seenIds.has(m.id) && !seenKeys.has(msgContentKey(m)));
        return [...prev, ...extras];
      });
      setSearchSource('graph');
      searchNextLink.current = data.nextLink || null;
      setHasMore(!!data.nextLink);
    } catch (err) {
      console.warn('searchOlder failed', err?.message || err);
    } finally {
      setSearchingOlder(false);
    }
  }

  // Outlook-style deferred mark-read. When the user clicks an unread
  // email we keep it unread in the UI so the Unread filter doesn't
  // instantly drop it out from under them. The commit fires when the
  // selectedMsg changes (catches click-to-another-email, folder switch,
  // search, explicit deselect) via the useEffect below, plus explicitly
  // when the user opens a reply / replyAll / forward.
  const prevSelectedRef = useRef(null);
  function commitMarkRead(msgId, { force = false } = {}) {
    if (!msgId) return;
    // force: the caller holds FRESHER truth than the list row. The list is
    // served from the local index, whose is_read can be stale (drift: the
    // index says read while Graph still has the message unread). The deselect
    // effect has just seen Graph's live isRead=false on the body it fetched on
    // open — but this lookup preferred the stale list row, `row.isRead` was
    // true, and the PATCH was silently skipped. The app showed the message
    // read, Graph was never told, and the Inbox badge kept counting it unread
    // until Outlook wrote the state. With force, the live truth wins.
    if (!force) {
      // Look it up in the current list — if it's already read, nothing to do.
      const row = messages.find(m => m.id === msgId) || (selectedMsg?.id === msgId ? selectedMsg : null);
      if (!row || row.isRead) return;
    }
    // Even forced: a message THIS app just deleted has nothing to mark. The
    // list check above used to cover this (the row was already filtered out);
    // force skips it, so after "delete the unread email I'm reading" the
    // deselect effect PATCHed the deleted id → Graph 404 → 409 stale → the
    // "That email had moved or been removed" toast on every such delete
    // (reported 2026-09-15, the day after force landed).
    if (isRecentlyDeleted(msgId)) return;
    applyReadState(msgId, true);
    recentReadOp.current.set(msgId, { isRead: true, at: Date.now() });
    fetchRepeatable(`/api/mail/messages/${encodeURIComponent(msgId)}/read`, { method: 'POST' })
      .then(parseActionResponse)
      .then(() => { /* keep the recentReadOp guard: releasing on HTTP success
        lets the slower message-LIST index revert the read state on the next
        poll. It's released when a LIST poll's data matches (pollMessages /
        applyLocalReadFlag) or at the PENDING_OP_MAX_MS cap. */ })
      .catch(err => {
        console.warn('read failed', msgId, err?.message || err);
        recentReadOp.current.delete(msgId);
        applyReadState(msgId, false);
        // This mark-read is automatic (deselect / reply / forward), so a stale
        // id — the message moved, was deleted elsewhere, or was recalled while
        // open — is not something the user can "try again": just refresh the
        // list so the row picks up its current id or drops off. The toast
        // stays for real failures, and for the explicit handleMarkRead.
        if (err?.stale) {
          try { pollMessagesRef.current?.(); } catch { /* the periodic poll covers it */ }
          return;
        }
        setActionError(actionErrorText(err, "Couldn't mark as read"));
      });
  }

  // Any time selectedMsg changes to a different id (or null), the message
  // we were looking at just became "done reading" — if it was unread when
  // we opened it, commit the mark-read now. One central place catches:
  // click-to-another-email, folder switch, search, explicit deselect.
  useEffect(() => {
    const prev = prevSelectedRef.current;
    if (prev && prev.id !== selectedMsg?.id && !prev.isRead) {
      // prev.isRead came from the live body fetch on open — Graph's truth for
      // this one message. Force so a stale index-served list row can't veto it.
      commitMarkRead(prev.id, { force: true });
    }
    prevSelectedRef.current = selectedMsg;
  // eslint-disable-next-line
  }, [selectedMsg]);

  // Order lookup — fires once per message-id. Result is null if the
  // subject doesn't reference any OnPoint order; the View Order
  // button is gated on a non-null result.
  //
  // Idempotency uses an attemptedOrderIds ref, not orderLookupMap-in-
  // deps. The earlier version put orderLookupMap in the deps array
  // and ate its own fetch: setOrderLookupMap → re-render → effect re-
  // runs → cleanup sets cancelled=true → original fetch's .then
  // skipped its setMap → orderLookupMap[id] stayed null forever →
  // button never appeared. (See dashboard.md → Rule 1 in the docs:
  // never retry / re-fire a state-driven fetch via state in deps.)
  useEffect(() => {
    if (!selectedMsg?.id || !selectedMsg.subject) return;
    if (attemptedOrderIds.current.has(selectedMsg.id)) return;
    attemptedOrderIds.current.add(selectedMsg.id);  // lock BEFORE fetch
    const id = selectedMsg.id;
    fetch(`/api/onpoint/order-lookup?subject=${encodeURIComponent(selectedMsg.subject)}&sender=${encodeURIComponent(selectedMsg.from || '')}`)
      .then(r => r.json())
      .then(data => {
        if (data) {
          setOrderLookupMap(prev => ({ ...prev, [id]: data }));
        } else {
          // No match yet — release the lock so re-selecting the message
          // (or the next render where the order has since been created
          // in OnPoint) tries again. Without this, an email opened
          // before its order existed stayed View-Order-less for the
          // whole session.
          attemptedOrderIds.current.delete(id);
        }
      })
      .catch(() => {
        // Soft-fail: button just doesn't appear. Release the lock
        // so a transient error doesn't permanently suppress lookup.
        attemptedOrderIds.current.delete(id);
      });
  }, [selectedMsg?.id, selectedMsg?.subject]);

  // ── Select (no auto-mark-read) ──────────────────────────────────────────────
  // Parse a Server-Timing header ("total;dur=19600, gqueue;dur=200, …")
  // into a { name: ms } map.
  function parseServerTiming(header) {
    const out = {};
    if (!header) return out;
    for (const part of header.split(',')) {
      const m = /([a-zA-Z]+)\s*;\s*dur=([\d.]+)/.exec(part.trim());
      if (m) out[m[1]] = Math.round(parseFloat(m[2]));
    }
    return out;
  }

  // Fetch + hydrate a message body, recording timing/outcome into bodyDiag.
  // Replaces the old fire-and-forget fetch whose catch silently swallowed
  // errors — a failed or hung getMessage used to leave a permanent white
  // screen with no signal. Now: a 30s AbortController timeout ends the
  // hang, every outcome is recorded for the Debug panel, and a failure is
  // stamped onto selectedMsg so the reading pane shows an error not a
  // forever-spinner. Safe to call again (Retry) — it re-runs cleanly.
  const BODY_FETCH_TIMEOUT_MS = 30000;

  // GET one message body → the parts hydrateBody reads, so a body fetched
  // ahead of the click (prefetchBody) can stand in for its own fetch.
  // Pass the list row's known hasAttachments so the server can skip the
  // attachments-listing Graph call on a no-attachment open (it verifies
  // against the message body, so a stale hint can't drop real files). Only
  // sent when it's a definite boolean on the row.
  async function fetchMessageBody(msg, { signal, prefetch = false } = {}) {
    const qs = new URLSearchParams();
    if (typeof msg.hasAttachments === 'boolean') qs.set('hasAttachments', msg.hasAttachments ? '1' : '0');
    if (prefetch) qs.set('prefetch', '1');
    const q = qs.toString();
    const res = await fetch(`/api/mail/messages/${encodeURIComponent(msg.id)}${q ? `?${q}` : ''}`, { signal });
    return {
      ok: res.ok,
      status: res.status,
      serverTiming: res.headers.get('Server-Timing'),
      graphDiag: res.headers.get('X-Graph-Diag'),
      text: await res.text(),
    };
  }

  // Body prefetch on mouse-down. A click is mouse-down … mouse-up, ~100 ms on
  // a real mouse — about the whole server time of a typical open (p50 70 ms in
  // the Oct 2026 perf log) — so starting the GET on the press means the body
  // is usually there when the click lands. One entry per message, consumed by
  // the click's hydrate, dropped after BODY_PREFETCH_TTL_MS so a stale answer
  // is never shown for a later open. `prefetch=1` tells the server this is not
  // an open (it skips the "last opened" record; the click's /opened beacon is
  // the real signal, as for any cached open).
  const BODY_PREFETCH_TTL_MS = 10_000;
  const bodyPrefetchRef = useRef(new Map());   // id → { at, promise }

  // Full messages opened or prefetched in this mailbox this session, so going
  // back to one paints its body at once (lib/bodyCache.js). Drafts are never
  // kept: they change. AuthedApp is keyed by mailbox, so a switch or sign-out
  // starts an empty cache.
  const bodyCacheRef = useRef(null);
  if (!bodyCacheRef.current) bodyCacheRef.current = createBodyCache();
  const draftsFolderId = (folders || []).find(f => isDraftsFolder(f))?.id || null;
  function keepBody(id, full) {
    if (isDraftsFolder(selectedFolder) || (draftsFolderId && full?.parentFolderId === draftsFolderId)) {
      bodyCacheRef.current.delete(id);
      return;
    }
    bodyCacheRef.current.set(id, full);
  }

  function prefetchBody(msg) {
    if (!msg?.id || Array.isArray(msg.attachments)) return;   // already full — no fetch on open
    if (isDraftsFolder(selectedFolder)) return;              // drafts change; open them live
    if (bodyCacheRef.current.has(msg.id)) return;            // the open paints it from memory
    const map = bodyPrefetchRef.current;
    const prev = map.get(msg.id);
    if (prev && Date.now() - prev.at < BODY_PREFETCH_TTL_MS) return;
    const promise = fetchMessageBody(msg, { prefetch: true })
      .then(res => {
        // Keep it beyond the 10 s window too: an arrow-key prefetch the
        // person reaches later still paints at once (its open re-fetches).
        if (res.ok) { try { keepBody(msg.id, JSON.parse(res.text)); } catch { /* the open reports it */ } }
        return res;
      })
      .catch(() => null);
    map.set(msg.id, { at: Date.now(), promise });
    for (const [id, e] of map) if (Date.now() - e.at >= BODY_PREFETCH_TTL_MS) map.delete(id);
  }
  // A fresh prefetch for this message, removed so it is used at most once.
  function takeBodyPrefetch(id) {
    const e = bodyPrefetchRef.current.get(id);
    if (!e) return null;
    bodyPrefetchRef.current.delete(id);
    return Date.now() - e.at < BODY_PREFETCH_TTL_MS ? e.promise : null;
  }

  // Compose blank-send diagnostics — recorded into the same Debug feed as
  // body loads, so when the "message body was empty" error appears the
  // top-bar Debug button immediately shows the full evidence (which stage
  // blocked it, whether the editor DOM was still mounted, body lengths, a
  // head dump) with a Copy-details button for reporting.
  function recordComposeDiag(rec) {
    const seq = ++bodyDiagSeq.current;
    setBodyDiag(prev => [{
      seq, kind: 'blankSend', phase: 'error', startedAt: Date.now(), ...rec,
    }, ...prev].slice(0, 12));
  }

  async function hydrateBody(msg, { reason = 'select' } = {}) {
    const seq = ++bodyDiagSeq.current;
    const startedAt = Date.now();
    const t0 = performance.now();
    setBodyDiag(prev => [{
      seq, id: msg.id, subject: msg.subject || '(no subject)', from: msg.from || '',
      reason, phase: 'pending', startedAt,
    }, ...prev].slice(0, 12));
    const patch = (p) => setBodyDiag(prev => prev.map(r => (r.seq === seq ? { ...r, ...p } : r)));

    // Clear any prior body-load error on this row so a Retry starts clean.
    if (selectingRef.current === msg.id) {
      setSelectedMsg(prev => (prev && prev.id === msg.id && prev._bodyLoadError
        ? { ...prev, _bodyLoadError: null } : prev));
    }

    // Cancel any earlier body fetch still in flight — the user has moved on,
    // and leaving it running just makes the client wait on (and the server
    // keep working) a message they're no longer looking at.
    if (bodyFetchAbortRef.current) { try { bodyFetchAbortRef.current.abort(); } catch { /* already settled */ } }
    const controller = new AbortController();
    bodyFetchAbortRef.current = controller;
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; controller.abort(); }, BODY_FETCH_TIMEOUT_MS);
    try {
      // The mouse-down prefetch when there is one (awaited abortably, so a
      // switch to another message still supersedes it); a failed or missing
      // prefetch falls through to the normal fetch.
      let res = null;
      const pre = reason === 'select' ? takeBodyPrefetch(msg.id) : null;
      if (pre) {
        res = await new Promise((resolve, reject) => {
          if (controller.signal.aborted) return reject(new DOMException('Aborted', 'AbortError'));
          controller.signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true });
          pre.then(resolve);
        });
        if (res && !res.ok && res.status >= 500) res = null;   // a 4xx (stale id…) is the answer; retry only a server fault
      }
      if (!res) res = await fetchMessageBody(msg, { signal: controller.signal });
      const st = parseServerTiming(res.serverTiming);
      const serverMs = st.total ?? st.getmsg ?? null;
      const serverParts = { queueMs: st.gqueue ?? null, backoffMs: st.gbackoff ?? null, graphMs: st.ggraph ?? null };
      let graphDiag = null;
      try { const h = res.graphDiag; if (h) graphDiag = JSON.parse(h); } catch { /* header absent or malformed */ }
      const text = res.text;
      const durationMs = Math.round(performance.now() - t0);
      if (!res.ok) {
        // Gone or moved (a stale id): never paint the old copy again.
        if (res.status >= 400 && res.status < 500) bodyCacheRef.current.delete(msg.id);
        patch({ phase: 'error', durationMs, httpStatus: res.status, serverMs, serverParts, graphDiag, bytes: text.length, error: text.slice(0, 300) });
        if (selectingRef.current === msg.id) {
          setSelectedMsg(prev => (prev && prev.id === msg.id ? { ...prev, _bodyLoadError: `HTTP ${res.status} after ${durationMs}ms` } : prev));
        }
        return { ok: false };
      }
      let full;
      try { full = JSON.parse(text); }
      catch (e) {
        patch({ phase: 'error', durationMs, httpStatus: res.status, serverMs, bytes: text.length, error: `Invalid JSON: ${e.message}` });
        return { ok: false };
      }
      patch({ phase: 'ok', durationMs, httpStatus: res.status, serverMs, serverParts, graphDiag, bytes: text.length });
      keepBody(msg.id, full);   // before the stale check: still worth keeping if they've moved on
      if (selectingRef.current !== msg.id) return { ok: true, stale: true };
      // Guard against clobbering a just-applied Ctrl+D / mark-read. `full`
      // carries Graph's PRE-mark flagged/completed/isRead; if this body fetch
      // resolves AFTER the user hit Ctrl+D (then switched away), writing it
      // raw would overwrite the optimistic "done" in BOTH selectedMsg AND the
      // durable IndexedDB cache — the "mark-done lost when you change email,
      // but fine if you wait" bug. The polling reconcile already honours these
      // op-guards; apply the same here so an in-flight open can't win the race.
      const rf = recentFlagOp.current.get(msg.id);
      const rr = recentReadOp.current.get(msg.id);
      const guarded = {
        ...full,
        ...(rf ? { flagged: rf.flagged, completed: rf.completed } : {}),
        ...(rr ? { isRead: rr.isRead } : {}),
      };
      setSelectedMsg(prev => prev && prev.id === msg.id ? { ...guarded, label: prev.label || guarded.label } : prev);
      mailCache.upsertOne(guarded, cacheFolderIdFor(msg)).catch(() => {});
      // Reader auto-retry: the open path soft-caps the attachments listing at
      // 8s, so a slow message comes back with attachmentsUnavailable. Quietly
      // re-fetch the UNCAPPED listing (?inline=1) once in the background and
      // merge the attachments in — no "click the email again" needed.
      if (full.attachmentsUnavailable && !attRetriedRef.current.has(msg.id)) {
        attRetriedRef.current.add(msg.id);  // de-dupe concurrent retries
        fetchInlineMessage(msg.id)
          .then(refetched => {
            if (refetched && Array.isArray(refetched.attachments)) {
              if (selectingRef.current === msg.id) {
                setSelectedMsg(prev => prev && prev.id === msg.id
                  ? { ...prev, attachments: refetched.attachments, attachmentsUnavailable: false }
                  : prev);
              }
              mailCache.upsertOne({ ...full, attachments: refetched.attachments, attachmentsUnavailable: false }, cacheFolderIdFor(msg)).catch(() => {});
              keepBody(msg.id, { ...full, attachments: refetched.attachments, attachmentsUnavailable: false });
            } else {
              // Still degraded (Graph slow / non-ok) — release the lock so
              // re-opening the message attempts the uncapped listing again.
              // The previous code added the id BEFORE the fetch resolved, so a
              // single transient failure permanently disabled auto-retry for
              // the session and left only the de-emphasised "click again".
              attRetriedRef.current.delete(msg.id);
            }
          })
          .catch(() => { attRetriedRef.current.delete(msg.id); });
      }
      // A draft opens in compose to be finished: from the Drafts folder, and
      // from anywhere else it turns up (search, a thread). Shown as an email it
      // offered Reply, which Microsoft refuses on a draft (taylor@, 2026-11-03).
      const isDraft = isDraftsFolder(selectedFolder) || (draftsFolderId && full?.parentFolderId === draftsFolderId);
      if (isDraft && !msg._openedFromFlags && !composing) openOutlookDraft(full);
      return { ok: true };
    } catch (err) {
      const durationMs = Math.round(performance.now() - t0);
      const aborted = controller.signal.aborted;
      const errText = aborted
        ? (timedOut
            ? `Aborted after ${Math.round(BODY_FETCH_TIMEOUT_MS / 1000)}s — no response from server/Graph`
            : 'Superseded — switched to another message before this finished')
        : (err.message || String(err));
      patch({ phase: aborted ? (timedOut ? 'timeout' : 'superseded') : 'error', durationMs, error: errText });
      if (selectingRef.current === msg.id) {
        setSelectedMsg(prev => (prev && prev.id === msg.id ? { ...prev, _bodyLoadError: errText } : prev));
      }
      return { ok: false };
    } finally {
      clearTimeout(timer);
      if (bodyFetchAbortRef.current === controller) bodyFetchAbortRef.current = null;
    }
  }

  // The IndexedDB folder an opened email is cached under: the open folder —
  // except one opened from the sidebar Flags list, which can live in any folder
  // (Sent Items, a subfolder). Caching that under the open folder would paint
  // it into this folder's list on the next cache-primed load.
  function cacheFolderIdFor(msg) {
    return msg?._openedFromFlags ? (msg.parentFolderId || null) : selectedFolder?.id;
  }

  // Click on a sidebar Flags row. The email may not be in the open folder, so
  // it's opened as-is (no folder switch). When the list DOES have it, open that
  // row — it carries the label and the list's own read / flag state.
  function handleOpenFlagged(m) {
    const inList = messages.find(x => x.id === m.id);
    handleSelectMsg(inList || { ...m, _openedFromFlags: true });
    setSidebarOpen(false);
  }

  function handleSelectMsg(msg) {
    // Close an open compose first (snapshots the draft silently). The compose
    // modal is code-split, so when the user clicks away WHILE A REPLY IS STILL
    // OPENING the lazy chunk hasn't mounted yet and composeRef.current is null —
    // requestClose would no-op and the just-opened reply would stay pinned over
    // the email you clicked. `composing` is parent-owned, so fall back to a
    // direct close (no snapshot; there's no user content yet mid-open) so
    // navigating to another email always wins.
    if (composing) {
      if (composeRef.current?.requestClose) composeRef.current.requestClose();
      else { setComposing(false); setActiveDraftKey(null); }
    }
    // Same treatment for an open Add Sample modal — clicking a different
    // message means the user wants to read that email next, so the
    // sample form should snapshot itself + close. The sample tab stays
    // in the strip so they can flick back.
    if (addingSample && sampleRef.current?.requestClose) {
      sampleRef.current.requestClose();
    }

    // Mark-read for the previously-viewed message is handled by the
    // useEffect on selectedMsg below — fires for this click AND for
    // folder switches / search resets that null out selectedMsg.

    // On mobile the list and reader don't show together — sliding the
    // reader in is how you "open" a message.
    setMobilePane('read');

    // Abort the previous message's in-flight body fetch. Switching emails
    // abandons the last open; cancelling it stops the client waiting on a
    // message you've clicked away from. Covers the case where the message
    // just clicked is already fully cached and skips hydrateBody below,
    // which would otherwise leave the prior fetch running.
    if (bodyFetchAbortRef.current) { try { bodyFetchAbortRef.current.abort(); } catch { /* already settled */ } bodyFetchAbortRef.current = null; }

    // Commit the row data synchronously — reading pane headers (sender,
    // subject, date, avatar) render instantly. Body hydrates below. An email
    // already read this session also paints its body from memory now, with
    // the row's current read / flag / done state (lib/bodyCache.js); the
    // hydrate below still runs and replaces it, exactly as before.
    selectingRef.current = msg.id;
    const cachedBody = !Array.isArray(msg.attachments) && !isDraftsFolder(selectedFolder)
      ? bodyCacheRef.current.get(msg.id)
      : null;
    setSelectedMsg(cachedBody
      ? withCachedBody(msg, cachedBody, { flagOp: recentFlagOp.current.get(msg.id), readOp: recentReadOp.current.get(msg.id) })
      : msg);

    // Tell the server this message was opened — feeds the quote-prefill
    // last-context store. The hydrate fetch below only fires on cache
    // misses, so this beacon is the reliable "user opened an email"
    // signal; cached rows already carry the full address fields.
    fetch(`/api/mail/messages/${encodeURIComponent(msg.id)}/opened`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subject:     msg.subject,
        from:        msg.from,
        fromName:    msg.fromName,
        toAddresses: msg.toAddresses,
        ccAddresses: msg.ccAddresses,
      }),
    }).catch(() => {});

    // Prime from the local cache if we've seen this message before.
    mailCache.getOne(msg.id).then(cached => {
      if (!cached) return;
      if (selectingRef.current !== msg.id) return;
      setSelectedMsg(prev => prev && prev.id === msg.id
        ? { ...cached, ...prev, bodyText: prev.bodyText || cached.bodyText }
        : prev);
    }).catch(() => {});

    // Hydrate from Graph. Only commit if this is still the latest click.
    // Gate on `attachments` being an array (the unambiguous signal that
    // the full message has been fetched). Body text alone isn't enough:
    // search-result rows from the local IndexedDB cache include
    // bodyHtml/bodyText but no attachments array, so the previous
    // !msg.bodyHtml gate skipped the fetch and ReadingPane stayed on
    // "Loading attachments…" until the user clicked the row a second
    // time.
    if (!Array.isArray(msg.attachments)) {
      // Hydrate the body (instrumented; records timing + errors for the
      // Debug panel and stamps failures onto the row so it doesn't
      // white-screen silently). Preserves the label from the list row —
      // Graph's getMessage doesn't return it (labels live in Postgres).
      hydrateBody(msg, { reason: 'select' });
    } else {
      mailCache.upsertOne(msg, cacheFolderIdFor(msg)).catch(() => {});
    }
    // Note: no mark-read fires here. The user still sees the email as
    // unread in the list (bold + unread dot) while they're viewing it.
  }

  function handleEmptyFolder(folder) {
    const target = folder || selectedFolder;
    if (!target) return;
    setEmptyConfirm(target);
  }

  async function confirmEmptyFolder() {
    const target = emptyConfirm || selectedFolder;
    if (!target) return;
    setEmptying(true);
    try {
      const res = await fetch(
        `/api/mail/folders/${encodeURIComponent(target.id)}/empty`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      // Server deleted everything — clear the UI to match without waiting
      // for the next poll cycle. Counts on the folder tree get zeroed
      // immediately so the badge disappears the instant the dialog
      // closes (otherwise the user sees a stale "55" until the next
      // /api/mail/folders refresh — which they read as "the empty
      // didn't take effect").
      setFolders(prev => prev.map(f =>
        f.id === target.id
          ? { ...f, unreadItemCount: 0, totalItemCount: 0, childFolders: f.childFolders }
          : f
      ));
      // Drop every cached row for this folder. Without this the next
      // time the user clicks the folder, loadMessages's cache-prime
      // step (mailCache.listRecent) would re-paint the now-deleted
      // messages before the empty server response lands — exactly
      // the "I empty, come back, they're still there" bug we hit.
      mailCache.clearFolder(target.id).catch(() => {});
      if (selectedFolder?.id === target.id) {
        setMessages([]);
        setSelectedMsg(null);
        setHasMore(false);
      }
      setEmptyConfirm(null);
    } catch (err) {
      setActionError(`Couldn't empty folder: ${err?.message || 'unknown error'}`);
      setEmptyConfirm(null);
    } finally {
      setEmptying(false);
    }
  }

  function handleDelete(msgId) {
    // Advance to the next email in the list before clearing
    // selectedMsg, so the reading pane lands on the message below
    // the one being deleted (or above, if you deleted the last row).
    if (selectedMsg?.id === msgId) {
      const idx = messages.findIndex(m => m.id === msgId);
      const next = idx >= 0 ? (messages[idx + 1] || messages[idx - 1] || null) : null;
      if (next) handleSelectMsg(next);
      else setSelectedMsg(null);
    }
    stampDelete(msgId);
    setMessages(prev => prev.filter(m => m.id !== msgId));
    // Off the sidebar Flags list too (Deleted Items is excluded there).
    noteFlagged(msgId, { flagged: false, completed: false });
    mailCache.removeOne(msgId).catch(() => {});
    fetch(`/api/mail/messages/${encodeURIComponent(msgId)}`, { method: 'DELETE' }).catch(() => {});
  }

  async function parseActionResponse(res) {
    let body = {};
    try { body = await res.json(); } catch { /* non-JSON */ }
    if (!res.ok) {
      const err = new Error(body.error || `HTTP ${res.status}`);
      err.status = res.status;
      err.code = body.code;
      err.stale = body.stale === true;   // server flags a stale/moved-message id
      throw err;
    }
    return body;
  }

  function applyReadState(msgId, isRead) {
    const current = messages.find(m => m.id === msgId)
      || (selectedMsg?.id === msgId ? selectedMsg : null);
    const changed = !!current && current.isRead !== isRead;
    setMessages(prev => prev.map(m => (
      m.id === msgId && m.isRead !== isRead ? { ...m, isRead } : m
    )));
    setSelectedMsg(prev => prev && prev.id === msgId && prev.isRead !== isRead
      ? { ...prev, isRead }
      : prev);
    if (changed) {
      setFolders(prev => prev.map(f =>
        f.id === selectedFolder?.id
          ? { ...f, unreadItemCount: Math.max(0, (f.unreadItemCount || 0) + (isRead ? -1 : 1)) }
          : f
      ));
    }
    mailCache.updateField(msgId, { isRead }).catch(() => {});
  }

  // Is this email in the Inbox? List rows (index- or Graph-served) carry their
  // folder; one without counts when it's in the open Inbox's list.
  function isInboxRow(row) {
    const inboxId = folders.find(f => (f.displayName || '').toLowerCase() === 'inbox')?.id;
    if (!row || !inboxId) return false;
    if (row.parentFolderId) return row.parentFolderId === inboxId;
    return selectedFolder?.id === inboxId && messages.some(m => m.id === row.id);
  }

  // The email as this tab knows it: the open list, the reading pane, or the
  // sidebar Flags list (right-click → Unflag / Mark as done act on an email
  // that may be in none of the others) — so its current state is the rollback.
  function knownMsg(msgId) {
    return messages.find(m => m.id === msgId)
      || (selectedMsg?.id === msgId ? selectedMsg : null)
      || flaggedList.find(m => m.id === msgId)
      || null;
  }

  function applyFlagState(msgId, { flagged, completed }) {
    // Every in-app flag / unflag / tick (and a failed one's rollback) lands
    // here, so the sidebar Flags list gains or drops the email at once. That
    // list is Inbox-only, so an email flagged elsewhere is given no row to add.
    const row = knownMsg(msgId);
    noteFlagged(msgId, { flagged, completed }, isInboxRow(row) ? row : null);
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, flagged, completed } : m));
    setSelectedMsg(prev => prev && prev.id === msgId ? { ...prev, flagged, completed } : prev);
    mailCache.updateField(msgId, { flagged, completed }).catch(() => {});
  }

  // Shared handling for a failed message mutation (read / unread / flag / tick).
  // The server tags a moved-or-deleted message with stale:true — its
  // folder-relative Graph id went stale (we key on default, not immutable, ids,
  // which change on move). On that, silently refresh the list so the row picks
  // up its current id or drops off, and return a plain message instead of the
  // raw Graph "not found in the store" text. Every other error keeps its
  // specific prefix.
  function actionErrorText(err, fallbackPrefix) {
    if (err?.stale) {
      try { pollMessagesRef.current?.(); } catch { /* the periodic poll covers it */ }
      return 'That email had moved or been removed, so the change didn’t apply — the list has been refreshed. Try again.';
    }
    return `${fallbackPrefix}: ${err?.message || 'unknown error'}`;
  }

  // Returns a promise that always resolves (the .catch below is terminal), so
  // callers can sequence a follow-up write behind the flag instead of racing
  // it — see handleMarkDone.
  function postFlag(msgId, status, desired, previous) {
    recentFlagOp.current.set(msgId, { ...desired, at: Date.now() });
    return fetchRepeatable(`/api/mail/messages/${encodeURIComponent(msgId)}/flag`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    })
      .then(parseActionResponse)
      .then(data => {
        // Do NOT release the recentFlagOp guard here on confirmed:true. That
        // confirmation is a single-message read-back (getMessageState), which
        // propagates FASTER than the message-LIST index. Releasing now lets the
        // next list poll — especially the immediate one an SSE poke fires —
        // re-apply the still-stale list row, and the tick "reappears with the
        // flag" seconds later. The guard is released the correct way instead:
        // when a LIST poll's own data matches the optimistic value (the
        // recentFlagOp match in pollMessages / applyLocalReadFlag), or at the
        // PENDING_OP_MAX_MS cap.
        void data;
      })
      .catch(err => {
        console.warn('flag failed', msgId, err?.message || err);
        recentFlagOp.current.delete(msgId);
        if (previous) applyFlagState(msgId, previous);
        setActionError(actionErrorText(err, "Couldn't update flag/tick"));
      });
  }

  // Simple toggle — used by ReadingPane's flag button and the sidebar Flags
  // right-click Unflag.
  function handleFlag(msgId, flagged) {
    const status = flagged ? 'flagged' : 'notFlagged';
    const current = knownMsg(msgId);
    const previous = current ? { flagged: !!current.flagged, completed: !!current.completed } : null;
    const desired = { flagged, completed: false };
    applyFlagState(msgId, desired);
    postFlag(msgId, status, desired, previous);
  }

  // Set / unset / toggle the "done" (green tick) state.
  //
  // - Right-click menu → mode 'toggle' (legacy behaviour: done ↔ none).
  // - Keyboard Ctrl+D  → mode 'set' (idempotent: always lands on done).
  //
  // The toggle behaviour was hitting users who pressed Ctrl+D right
  // after sending a reply: the post-send polling sometimes lands the
  // server's `completed=true` flag on the row before they hit the key,
  // and Ctrl+D would flip it back off. Making the keyboard path a one-
  // way "set done" eliminates that whole class of race.
  //
  // Marking done also commits mark-read — "done" implies the user has
  // dealt with the email, so leaving it bold-unread in the list is just
  // visual noise.
  function handleMarkDone(msgId, mode = 'toggle') {
    const current = knownMsg(msgId);
    const nextCompleted = mode === 'set' ? true : !(current?.completed);
    // No-op if the user pressed Ctrl+D and it was already done — don't
    // even fire the PATCH. Saves a Graph round-trip and keeps the row
    // visually stable.
    if (mode === 'set' && current?.completed) return;
    const status = nextCompleted ? 'complete' : 'notFlagged';
    const previousFlag = current ? { flagged: !!current.flagged, completed: !!current.completed } : null;
    applyFlagState(msgId, { flagged: false, completed: nextCompleted });
    if (nextCompleted) applyReadState(msgId, true);
    // Stamp recentFlagOp BEFORE the PATCH fires so the next polling
    // tick (could land within ~20 s) reads our value instead of Graph's
    // pre-PATCH state. This is the fix for "Ctrl+D right after Send
    // appears to do nothing": the send fetch and the flag PATCH race,
    // and the 20 s poll used to fire in between with the unflagged
    // state, overwriting our optimistic UI.
    const flagRequest = postFlag(msgId, status, { flagged: false, completed: nextCompleted }, previousFlag);
    // Mark-read PATCH only when the email isn't already read AND we're
    // setting done (not un-setting). Mirrors the optimistic UI above.
    if (nextCompleted && current && !current.isRead) {
      recentReadOp.current.set(msgId, { isRead: true, at: Date.now() });
      // Chained behind the flag PATCH, not fired alongside it. Two writes to
      // one message in flight at once make Exchange reject the loser with 409
      // ErrorIrresolvableConflict (its change key moves under the second one),
      // and this pair was the single biggest source of them: ~17% of flag
      // writes conflicted, all of them recovered by a retry a moment later.
      // The read is the lighter write, so it landed first and the flag lost —
      // which is why /read and /unread showed no conflicts of their own.
      flagRequest
        .then(() => fetchRepeatable(`/api/mail/messages/${encodeURIComponent(msgId)}/read`, { method: 'POST' }))
        .then(parseActionResponse)
        .then(() => { /* keep the recentReadOp guard: releasing on HTTP success
        lets the slower message-LIST index revert the read state on the next
        poll. It's released when a LIST poll's data matches (pollMessages /
        applyLocalReadFlag) or at the PENDING_OP_MAX_MS cap. */ })
        .catch(err => {
          console.warn('mark-read after done failed', msgId, err?.message || err);
          recentReadOp.current.delete(msgId);
          applyReadState(msgId, false);
          setActionError(actionErrorText(err, "Couldn't mark completed email as read"));
        });
    }
  }

  // 3-state cycle — used by the MessageList flag slot: none → flagged → completed → none.
  function handleFlagCycle(msgId) {
    const current = messages.find(m => m.id === msgId);
    if (!current) return;
    let status;
    if (current.completed)      status = 'notFlagged';
    else if (current.flagged)   status = 'complete';
    else                        status = 'flagged';

    const flagged   = status === 'flagged';
    const completed = status === 'complete';
    const previous = { flagged: !!current.flagged, completed: !!current.completed };
    const desired = { flagged, completed };
    applyFlagState(msgId, desired);
    postFlag(msgId, status, desired, previous);
  }

  function handleMarkUnread(msgId) {
    const current = messages.find(m => m.id === msgId)
      || (selectedMsg?.id === msgId ? selectedMsg : null);
    const previousRead = current?.isRead ?? true;
    applyReadState(msgId, false);
    recentReadOp.current.set(msgId, { isRead: false, at: Date.now() });
    fetchRepeatable(`/api/mail/messages/${encodeURIComponent(msgId)}/unread`, { method: 'POST' })
      .then(parseActionResponse)
      .then(() => { /* keep the recentReadOp guard: releasing on HTTP success
        lets the slower message-LIST index revert the read state on the next
        poll. It's released when a LIST poll's data matches (pollMessages /
        applyLocalReadFlag) or at the PENDING_OP_MAX_MS cap. */ })
      .catch(err => {
        // Roll back the optimistic UI so the row doesn't linger "unread"
        // then silently snap back when polling returns.
        console.warn('unread failed', msgId, err?.message || err);
        recentReadOp.current.delete(msgId);
        applyReadState(msgId, previousRead);
        setActionError(`Couldn't mark as unread: ${err?.message || 'unknown error'}`);
      });
  }

  function handleMarkRead(msgId) {
    const current = messages.find(m => m.id === msgId)
      || (selectedMsg?.id === msgId ? selectedMsg : null);
    const previousRead = current?.isRead ?? false;
    applyReadState(msgId, true);
    recentReadOp.current.set(msgId, { isRead: true, at: Date.now() });
    fetchRepeatable(`/api/mail/messages/${encodeURIComponent(msgId)}/read`, { method: 'POST' })
      .then(parseActionResponse)
      .then(() => { /* keep the recentReadOp guard: releasing on HTTP success
        lets the slower message-LIST index revert the read state on the next
        poll. It's released when a LIST poll's data matches (pollMessages /
        applyLocalReadFlag) or at the PENDING_OP_MAX_MS cap. */ })
      .catch(err => {
        console.warn('read failed', msgId, err?.message || err);
        recentReadOp.current.delete(msgId);
        applyReadState(msgId, previousRead);
        setActionError(actionErrorText(err, "Couldn't mark as read"));
      });
  }

  // ── Reply / Forward ──────────────────────────────────────────────────────────

  // Re-fetch a message with ?inline=1 if its inlineAttachments lack
  // contentBytes. The reader path opens messages metadata-only — bytes
  // are streamed to the iframe via /inline/:aid — but the compose
  // preview's resolveCidImages needs raw bytes to build data: URIs
  // for the in-page iframe, so reply/forward go through this detour.
  // Server caches the full shape for 60s, so the cost is one Graph
  // round-trip per first-time forward/reply.
  async function ensureInlineBytes(msg) {
    const inline = msg?.inlineAttachments || [];
    // `every`, not `some`: a message whose first inline image has bytes but
    // whose second doesn't still needs the refetch. `some` short-circuited on
    // the first hit and left the rest byte-less forever.
    if (!inline.length || inline.every(a => a?.contentBytes)) return msg;
    const full = await fetchInlineMessage(msg.id);
    return full ? { ...msg, ...full, label: msg.label || full.label } : msg;
  }

  // Open the composer NOW with whatever inline bytes we have, then fetch
  // the missing ones in the background and patch the quoted block in when
  // they land. ensureInlineBytes hits Graph to download embedded-image
  // bytes (signatures/logos), which can take several seconds on a heavy
  // signature — awaiting it before opening was what made the Reply/Forward
  // button feel frozen. The bytes only affect how the QUOTED history
  // renders, never the user's ability to start typing.
  async function patchInlineQuoteWhenReady(msg, key) {
    const inline = msg?.inlineAttachments || [];
    if (!inline.length || inline.every(a => a?.contentBytes)) return; // nothing to load
    let enriched;
    try { enriched = await ensureInlineBytes(msg); } catch { return; }
    if (!enriched || enriched === msg) return;          // fetch failed / no change
    if (activeDraftKeyRef.current !== key) return;       // user moved on — don't clobber
    // Hand the composer the freshly-loaded inline bytes as a cid→dataURI map
    // so it swaps ONLY the <img> srcs in place. A full-quote rebuild here
    // used to wipe any text the user had already edited inside the quote
    // (e.g. correcting a sender's font-obfuscated "FIRE" back to "FOC").
    const cidMap = {};
    for (const a of (enriched.inlineAttachments || [])) {
      if (a?.contentId && typeof a.contentBytes === 'string' && a.contentBytes) {
        cidMap[String(a.contentId).replace(/^<|>$/g, '').toLowerCase()] =
          `data:${a.contentType || 'application/octet-stream'};base64,${a.contentBytes}`;
      }
    }
    // ComposeModal is code-split (Section 6): its chunk + mount can land AFTER
    // this fetch resolves, so composeRef.current is briefly null. The old
    // one-shot `composeRef.current?.patchQuoteImages?.(cidMap)` then silently
    // no-opped and the quoted inline images stayed broken — the recurring
    // "shows in the email, not on the reply" bug, which came back the moment
    // the modal became lazy. deliverQuoteCidMap polls until the handle mounts
    // AND the swap lands, bailing if the user leaves this draft.
    await deliverQuoteCidMap({
      cidMap,
      getPatch: () => composeRef.current?.patchQuoteImages,
      isActive: () => activeDraftKeyRef.current === key,
    });
  }

  async function openReply(msg, replyAll = false) {
    const key = `${replyAll ? 'replyAll' : 'reply'}-${msg.id}`;
    // If there's an in-progress draft for this reply, reopen it rather than
    // starting fresh and clobbering the user's work.
    if (drafts[key]) { openDraft(key); return; }
    // Initiating a reply is a commit point — user is done reading, mark it read.
    commitMarkRead(msg.id);
    // On reply-all, the recipient list is "from + all original To recipients".
    // Remove our own mailbox (we're the sender of the reply) and dedupe —
    // otherwise darren@pinpoint.promo ends up getting CC'd on his own reply.
    // When impersonating, "self" is the VIEWED mailbox (the reply is built
    // from their perspective), not the admin's own.
    const selfLower = (getImpersonate()?.mailbox || mailbox || '').toLowerCase();
    const seen      = new Set();
    const raw = replyAll
      ? [msg.from, ...(msg.toAddresses?.map(a => a.email) || [])]
      : [msg.from];
    const toAddresses = raw
      .filter(Boolean)
      .filter(addr => addr.toLowerCase() !== selfLower)
      .filter(addr => {
        const k = addr.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    // Reply-all also typically echoes the CC list of the original message
    // minus self. Keep To for primary recipients, Cc for the rest.
    const ccAddresses = replyAll
      ? (msg.ccAddresses || [])
          .map(a => a.email)
          .filter(Boolean)
          .filter(addr => addr.toLowerCase() !== selfLower)
          .filter(addr => !seen.has(addr.toLowerCase()))
      : [];
    // Pre-fill "Hi <FirstName>," plus an optional canned response to
    // a recognised pleasantry. buildReplyOpener now returns HTML with
    // block-paragraph <div>s (was previously plain text → <br><br>),
    // because Chrome silently dropped the trailing <br> at the end of
    // the user-typing div and the cursor landed on the line right
    // under the greeting instead of a blank line below it. Each block
    // div renders its own line — even empty ones — so the cursor lands
    // exactly where the user expects.
    //
    // We pass it via bodyHtml (not body + quoteHtml separately) so
    // ComposeModal's looksLikeHtml branch sets innerHTML directly,
    // bypassing buildBodyHtml's <br>-conversion. The quote is appended
    // inside the same string with the standard data-compose-quote
    // wrapper so patchInlineQuoteWhenReady still finds it.
    const quoteHtml = buildHtmlQuote(msg, false);
    setComposeDefaults({
      mode:      replyAll ? 'replyAll' : 'reply',
      replyToId: msg.id,
      // Move-stable id, sent with the reply so the server can re-resolve the
      // source message when its folder-relative id goes stale before Send
      // (filed in Outlook after it was opened). The server used to recover it
      // from the index by the stale id, but the list reconcile re-keys that
      // row to the new id first — so the recovery missed and the send 500'd
      // (taylor@, 2026-09-14). See bot/src/mail/sourceIdHeal.js.
      internetMessageId: msg.internetMessageId || null,
      replyAll,
      to:        toAddresses.join('; '),
      cc:        ccAddresses.join('; '),
      subject:   msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`,
      // selfMailbox: on a SENT item the greeting must address the original
      // recipient, not the mailbox owner ("Hi Taylor," to Taylor, 2026-07-15).
      bodyHtml:  buildReplyOpener(msg, quoteHtml, { selfMailbox: selfLower, customOpeners: replyOpeners, staffAddresses }),
    });
    setActiveDraftKey(key);
    setComposing(true);
    // Inline-image bytes load in the background; quoted images fill in when ready.
    patchInlineQuoteWhenReady(msg, key);
  }

  async function openForward(msg) {
    const key = `forward-${msg.id}`;
    if (drafts[key]) { openDraft(key); return; }
    // Forwarding is also a commit point — user is done reading, mark it read.
    commitMarkRead(msg.id);
    // If the reader degraded the attachments listing (8s soft-cap hit a
    // pathological message → attachmentsUnavailable), re-fetch the full
    // message before building source-attachment chips. Otherwise forwarding
    // such a message would silently carry NONE of the original attachments.
    // The ?inline=1 path has no soft-cap so it gets the real listing.
    if (msg.attachmentsUnavailable || !Array.isArray(msg.attachments)) {
      const full = await fetchInlineMessage(msg.id);
      if (full && Array.isArray(full.attachments)) msg = { ...msg, ...full };
    }
    // Seed compose with metadata-only chips for every non-inline source
    // attachment. Each chip carries `sourceMessageId` + `sourceAttachmentId`
    // — markers the backend recognises as "copy from this id, don't expect
    // contentBytes". The user can remove individual ones in the modal; the
    // backend then copies only the remaining set on send. Inline images
    // (signatures / cid: refs in the body) are skipped — they're handled
    // by the backend's auto-copy.
    const sourceAttachments = (msg.attachments || [])
      .filter(a => !a.isInline)
      .filter(a => !isNoiseAttachment(a))
      .map(a => ({
        name:               a.name || 'attachment',
        contentType:        a.contentType || 'application/octet-stream',
        size:               a.size || 0,
        sourceMessageId:    msg.id,
        sourceAttachmentId: a.id,
      }));
    setComposeDefaults({
      mode:        'forward',
      forwardId:   msg.id,
      internetMessageId: msg.internetMessageId || null,   // stale-id heal, as for reply
      subject:     msg.subject?.startsWith('Fwd:') ? msg.subject : `Fwd: ${msg.subject}`,
      body:        '',
      quoteHtml:   buildHtmlQuote(msg, true),
      attachments: sourceAttachments,
    });
    setActiveDraftKey(key);
    setComposing(true);
    // Inline-image bytes load in the background; quoted images fill in when ready.
    patchInlineQuoteWhenReady(msg, key);
  }

  function buildHtmlQuote(msg, isForward) {
    const dateStr = msg.date ? new Date(msg.date).toLocaleString('en-GB') : '';
    const fromName = escHtml(msg.fromName || msg.from || '');
    const fromEmail = escHtml(msg.from || '');
    const subjectLine = escHtml(msg.subject || '');
    // Resolve cid: references in the original body BEFORE embedding as a
    // quote, so inline images (logos/signatures) render in compose + send
    // exactly as they render in the reader.
    let quoteSrc = msg.bodyHtml;
    if (quoteSrc) {
      // Some senders (notably Japanese reservation systems — Hotel Granvia,
      // 2026-08-11) deliver "HTML" that is really plain text: no <br>/<p>/
      // <div> anywhere, all structure carried by raw newlines. Browsers
      // collapse literal newlines in HTML, so the quote flattened into one
      // blob. If the body has newlines but no line-break markup at all,
      // convert the newlines (\r\n, \r-only, and \n all normalise first).
      const hasBreakMarkup = /<\s*(br|p|div|tr|li|table|h[1-6])\b/i.test(quoteSrc);
      if (!hasBreakMarkup && /[\r\n]/.test(quoteSrc)) {
        quoteSrc = quoteSrc.replace(/\r\n?/g, '\n').replace(/\n/g, '<br>');
      }
    } else {
      quoteSrc = (msg.bodyText || '')
        .replace(/\r\n?/g, '\n') // CR/CRLF line endings → \n so the <br> pass sees them
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
    }
    const originalBody = resolveCidImages(quoteSrc, msg.inlineAttachments || []);
    const header = isForward
      ? `---------- Forwarded message ----------<br>From: <b>${fromName}</b> &lt;${fromEmail}&gt;<br>Date: ${dateStr}<br>Subject: ${subjectLine}<br><br>`
      : `On ${dateStr}, <b>${fromName}</b> &lt;${fromEmail}&gt; wrote:<br>`;
    // Horizontal rule separates the new reply from the quoted thread —
    // matches Outlook's on-screen separator and travels with the outgoing
    // HTML so the recipient sees the same visual break.
    const divider = `<hr style="border:0;border-top:1px solid #c7c2b9;margin:18px 0;">`;
    // Wrap the quoted content in a plain <div>, NOT a <blockquote>.
    // <blockquote> renders with a left border in every email client
    // that styles it (we did too, with the inline `border-left:3px
    // solid #e3dfd7` here previously). The user reported that as a
    // visual mismatch with Outlook web, which uses no left rule on
    // its quoted text. The <hr> divider above is enough separation;
    // the colour + font-size keep the quoted text visually subdued.
    return `${divider}<div style="margin:12px 0;color:#4b4e55;font-family:-apple-system,Segoe UI,Arial,sans-serif;font-size:12.5px;">${header}${originalBody}</div>`;
  }

  function escHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ── AI Reply ─────────────────────────────────────────────────────────────────
  // Phase 1c.7: opens AiQuotePanel inside the reading-pane slot instead
  // of the old ComposeModal handoff. The panel calls /api/approval/* (not
  // /api/mail/messages/:id/ai-reply) so the full Phase 1a/1c flow fires:
  //   1. Retrieval + pricing + proposed_quote write (already happens at
  //      classify time, but Panel can also (re)generate on demand)
  //   2. Explainability panel — products + reason + stock + price table
  //   3. Approve & Send → OnPoint sync → Graph send (all server-side)
  // The old /ai-reply endpoint still exists; we just stop calling it
  // from the UI. Keeping it around in case anyone else (script, future
  // workflow) wants the old shape.
  async function handleAiReply(msg) {
    // __openedAt drives the tab-strip sort; the panel itself only reads id +
    // header metadata, so the extra field is inert there.
    setAiQuoteMsg({ ...msg, __openedAt: Date.now() });
    setAiQuoteMinimised(false);
  }

  // handleQuickSend removed alongside the quick-reply bar that consumed
  // it. Reply / Reply All / Forward / AI Reply in the ReadingPane
  // toolbar are the canonical compose entry points now; the emoji
  // picker (handleReactToMessage below) is mounted in the toolbar
  // directly. Restore this function (and the bar) if "one-line reply"
  // becomes a documented UX requirement again.

  // ── Quick-reply emoji-as-reaction ────────────────────────────────────────────
  // POST /api/mail/messages/:id/react. Backend gates on the tenant domain
  // (Outlook reactions don't propagate cross-tenant), so callers must
  // already have established that the sender is internal — see the
  // ReadingPane.QuickReplyEmojiButton's `canReact` guard. Throws on
  // failure so the picker can fall back to text-insert.
  async function handleReactToMessage(messageId, reactionType) {
    const res = await fetch(`/api/mail/messages/${encodeURIComponent(messageId)}/react`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ reactionType }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    // Optimistically reflect the new reaction in the local map so the
    // MessageList badge updates without waiting for the Graph webhook.
    setReactionMap(prev => ({
      ...prev,
      [messageId]: {
        count:             Math.max(1, (prev[messageId]?.count || 0) + (prev[messageId]?.ownerReactionType ? 0 : 1)),
        ownerReactionType: reactionType,
        ownerReactionTime: new Date().toISOString(),
      },
    }));
  }

  // ── Send ─────────────────────────────────────────────────────────────────────
  // Optimistic: returns immediately so ComposeModal closes without waiting
  // on Graph. A toast (bottom-right) reports the outcome; on failure the
  // draft is saved back and clicking the red toast reopens it for retry.
  // Also fixes a latent bug where attachments on Reply / Reply-All were
  // silently dropped because the old payload didn't include them.
  function handleSend(formPayload) {
    const def = composeDefaults;
    const draftKey = activeDraftKey || draftKeyFor(def);
    // signature: kept | removed | none — what the compose window did with the
    // person's OnPoint signature (lib/composeSignature.js).
    const { to, cc, bcc, subject, attachments, confirmedExternal, signature, signatureText } = formPayload;
    // Stable idempotency key per LOGICAL send, reused on retry (saved into the
    // retry draft below). A send that times out client-side (30s) often still
    // completes server-side; reusing this id lets the server suppress the
    // duplicate when the user retries the "failed" send.
    const sendId = def.sendId || (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

    // Convert any data: URI images in the body back to cid: refs +
    // inline attachments. buildHtmlQuote / resolveCidImages inlined
    // cid: → data: so the compose UI could render the image; outgoing
    // mail must use cid: instead because Outlook + most email clients
    // refuse to render data: URI images and the raw <img> markup
    // leaks into the recipient's view.
    const { html: bodyHtml, extracted: inlineFromBody } =
      extractDataUriImages(formPayload.bodyHtml || '', oneShotCidFactory(Date.now()), MAX_INLINE_IMAGE_BYTES);

    // Split user-uploaded attachments (carry contentBytes the modal
    // is responsible for) from "copy-from-source" markers that the
    // forward path seeds when the modal opens. The backend handles
    // each kind differently:
    //   - userAtts   → pass via `attachments` (Graph-bound bytes)
    //   - sourceRefs → pass via `sourceAttachments`; backend
    //     re-fetches each one from the original message and copies
    //     it onto the outgoing draft. Markers without contentBytes
    //     can't be sent any other way.
    const userAtts   = [
      ...(attachments || []).filter(a => !a.sourceMessageId && a.contentBytes),
      ...inlineFromBody,
    ];
    const sourceRefs = (attachments || [])
      .filter(a => a.sourceMessageId && a.sourceAttachmentId)
      .map(a => ({ messageId: a.sourceMessageId, attachmentId: a.sourceAttachmentId }));

    let url, wirePayload;
    if (def.replyToId) {
      const norm = (v) => (Array.isArray(v) ? v : String(v || '').split(/[,;]/))
        .map(s => String(s).toLowerCase().trim())
        .filter(Boolean)
        .sort()
        .join(',');
      const toChanged  = norm(to)  !== norm(def.to);
      // Graph's /reply + /replyAll actions auto-populate To/Cc from the
      // original message and ignore any extra ccRecipients we pass in.
      // So when the user adds someone to Cc/Bcc (commonly via @mention),
      // we have to route to /send with the full explicit recipient list
      // — otherwise the added addresses are silently dropped.
      const ccChanged  = norm(cc)  !== norm(def.cc);
      const bccChanged = norm(bcc) !== norm(def.bcc);
      if (toChanged || ccChanged || bccChanged) {
        url         = '/api/mail/send';
        // sourceMessageId lets the server verify the recipients against the
        // original thread's participants (wrong-recipient guard); the reroute
        // carries the quoted history, so this is the path that must be checked.
        // replyAll tells the server which arrow to put on the original — this
        // route is a new message as far as Graph is concerned, so the server
        // has to mark the email as replied to itself.
        wirePayload = { to, cc, bcc, subject, bodyHtml, attachments: userAtts,
                        sourceMessageId: def.replyToId, replyAll: !!def.replyAll,
                        internetMessageId: def.internetMessageId || null,
                        confirmedExternal, signature };
      } else {
        url         = `/api/mail/messages/${encodeURIComponent(def.replyToId)}/reply`;
        // Pass to + cc so the backend's saveRecipients picks up these
        // addresses for the autocomplete cache. Without them, replying
        // to a customer never adds the customer's address to recent
        // recipients and the next "new email" autocomplete misses it.
        wirePayload = { bodyHtml, replyAll: !!def.replyAll, to, cc, bcc, attachments: userAtts,
                        confirmedExternal, signature,
                        // Lets the server heal a stale source id without the
                        // index (whose row may have been re-keyed by then).
                        internetMessageId: def.internetMessageId || null };
      }
    } else if (def.forwardId) {
      url         = `/api/mail/messages/${encodeURIComponent(def.forwardId)}/forward`;
      // sourceAttachments tells the backend exactly which of the
      // original's attachments to re-copy. If the user removed all of
      // them this is `[]` and only userAtts get sent.
      wirePayload = {
        to, cc, bodyHtml, bcc,
        attachments:       userAtts,
        sourceAttachments: sourceRefs,
        signature,
        internetMessageId: def.internetMessageId || null,   // stale-id heal, as for reply
      };
    } else {
      url         = '/api/mail/send';
      // userAtts, NOT the raw `attachments`: the body was rewritten to cid:
      // refs and userAtts carries the matching inline images (inlineFromBody).
      // Sending raw `attachments` here dropped a pasted screenshot/logo from a
      // brand-new email (and a resumed Outlook draft), so the recipient got a
      // broken cid: image.
      wirePayload = { to, cc, bcc, subject, bodyHtml, attachments: userAtts, signature };
    }

    const toastId = Date.now() + Math.random();
    setSendToasts(t => [...t, {
      id: toastId, status: 'sending',
      subject: (subject || '(no subject)').slice(0, 80),
    }]);

    // Optimistically show the replied / forwarded arrow on the source
    // message right away (mirrors the toast's optimism) instead of waiting
    // for the poll. A failed send self-corrects once recentReplyOp expires.
    if (def.replyToId)      markRepliedLocal(def.replyToId, 'replied');
    else if (def.forwardId) markRepliedLocal(def.forwardId, 'forwarded');

    // Fire-and-forget, deferred to the next event-loop tick. JSON.stringify
    // on a 20-30 MB base64 attachment blocks the main thread for several
    // hundred ms; deferring pushes that work past the current paint so
    // the modal close commits first and the user can click another row
    // without feeling the serialisation lag.
    setTimeout(async () => {
      // Send deadline: a hung POST used to leave the toast spinning on
      // "Sending…" forever (no timeout on this fetch). Abort after 30s so it
      // resolves to a red error the user can retry — and so the fetch
      // interceptor records it into app_errors for the daily digest.
      //
      // The deadline grows with the size of what is being uploaded. A flat 30s
      // is right for a text email and wrong for one carrying tens of MB of
      // attachments, which cannot physically upload in that time on an ordinary
      // connection — it was aborted mid-upload and reported as a timeout that
      // "may not have gone out". 30s + 1s per 125 KB (a 1 Mbit/s uplink), capped
      // at 5 minutes: a 40 MB email gets the full 5.
      const sendBody = JSON.stringify({ ...wirePayload, sendId });
      const SEND_TIMEOUT_MS = Math.min(300000, 30000 + Math.ceil(sendBody.length / 125000) * 1000);
      const ctrl = new AbortController();
      const sendTimer = setTimeout(() => ctrl.abort(), SEND_TIMEOUT_MS);
      try {
        let res;
        try {
          res = await fetch(url, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    sendBody,
            signal:  ctrl.signal,
          });
        } catch (e) {
          if (e?.name === 'AbortError') {
            // A send timeout is an abort — the fetch interceptor deliberately
            // filters aborts (autosave/search abort constantly), so report it
            // EXPLICITLY or a stuck send never reaches app_errors / the digest.
            reportError({
              source: 'send-timeout',
              message: `Send timed out after ${SEND_TIMEOUT_MS / 1000}s (${def.replyToId ? 'reply' : def.forwardId ? 'forward' : 'new'}, ${(sendBody.length / 1048576).toFixed(1)} MB)`,
              route: apiRoute(url),
              status: 0,
            });
            throw new Error(`Send timed out after ${SEND_TIMEOUT_MS / 1000}s — it may not have gone out. Check Sent Items before retrying.`);
          }
          throw e;
        } finally {
          clearTimeout(sendTimer);
        }
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          // 409 + stale:true — the email being replied to / forwarded moved or
          // was deleted since it was opened and its id could not be re-resolved.
          // Refresh the list so the row picks up its current id (or drops off),
          // as the read/flag actions do; the retry draft below keeps the text.
          if (err.stale === true) {
            try { pollMessagesRef.current?.(); } catch { /* the periodic poll covers it */ }
          }
          throw new Error(err.error || `HTTP ${res.status}`);
        }
        setSendToasts(t => t.map(x =>
          x.id === toastId ? { ...x, status: 'sent' } : x
        ));
        setTimeout(() => {
          setSendToasts(t => t.filter(x => x.id !== toastId));
        }, 3500);
      } catch (err) {
        // The send didn't go out — undo the optimistic replied/forwarded
        // arrow set before the fetch (esp. the blank-body 422), so a rejected
        // reply doesn't keep showing as replied for the ~30s guard window.
        if (def.replyToId)      revertRepliedLocal(def.replyToId);
        else if (def.forwardId) revertRepliedLocal(def.forwardId);
        // The send POST is an /api call, so the fetch interceptor already
        // reported it (server fault, or HTML-instead-of-JSON) with the URL and
        // status. Reporting again here would be a second row for one failure.
        // Save the payload back as a draft so retry is one click away.
        const retryDraft = {
          ...def,
          // The signature as it went (lib/composeSignature.js): the retry
          // neither adds a second one nor brings back a deleted one.
          ...signatureMemoryOfSend({ signature, signatureText }),
          sendId, // reuse the SAME idempotency key on retry → server dedups
          to:      Array.isArray(to)  ? to.join('; ')  : (to  || ''),
          cc:      Array.isArray(cc)  ? cc.join('; ')  : (cc  || ''),
          bcc:     Array.isArray(bcc) ? bcc.join('; ') : (bcc || ''),
          subject: subject || '',
          bodyHtml,
          body: '',
          attachments: attachments || [],
          updatedAt: Date.now(),
          msgSubject: subject || (selectedMsg?.subject || ''),
          msgFrom:    selectedMsg?.fromName || selectedMsg?.from || '',
          createdAt:  Date.now(),
        };
        saveDraft(draftKey, retryDraft);
        setSendToasts(t => t.map(x =>
          x.id === toastId ? { ...x, status: 'failed', err: err.message, draftKey } : x
        ));
        // Also record into the Debug feed — the toast truncates the error and
        // vanishes, so the top-bar Debug button is where the full evidence
        // (complete Graph error, attachment list, endpoint) lives.
        recordComposeDiag({
          kind: 'sendFail',
          error: err.message || 'unknown error',
          mode: def.replyToId ? 'reply' : def.forwardId ? 'forward' : 'new',
          subject: subject || '',
          endpoint: url,
          attachmentCount: (attachments || []).length,
          attachmentSummary: (attachments || [])
            .map(a => `${a.name} (${Math.round((a.size || 0) / 1024)}KB)`)
            .join(', ')
            .slice(0, 600),
        });
      }
    }, 0);
  }

  function dismissToast(id) {
    setSendToasts(t => t.filter(x => x.id !== id));
  }

  function openContext(e, msg) {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, msg });
  }

  const impersonate = getImpersonate();

  return (
    <div className={styles.shell}>
      {/* Admin-impersonation banner. Renders only when the URL carries
          ?as=<mailbox>; the boot path in lib/impersonate.js stamps a
          header on every /api/* call so reads come from that mailbox.
          Bright accent so it's impossible to mistake for the admin's
          own inbox. */}
      {/* A shared mailbox (sales@) has no bar: the sidebar's Mailboxes list
          shows which one this tab is in, as Outlook's folder pane does. */}
      {impersonate && !impersonate.shared && (
        <div className={styles.impersonateBanner}>
          <span>
            👁  Viewing <strong>{impersonate.displayName}</strong>'s mailbox
            <span style={{ color: 'inherit', opacity: 0.75, marginLeft: 6 }}>({impersonate.mailbox})</span>
          </span>
          <button
            type="button"
            className={styles.impersonateExit}
            onClick={exitImpersonation}
            title="Return to your own inbox"
          >
            Exit
          </button>
        </div>
      )}
      {/* Top bar */}
      <header className={styles.topbar}>
        <button
          className={styles.hamburger}
          // Tapping the hamburger toggles the drawer. Previous version
          // only ever opened it — there was no way to dismiss the drawer
          // without tapping the backdrop, which is a discoverability
          // dead-end on mobile.
          onClick={() => setSidebarOpen(open => !open)}
          title="Menu"
          aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          aria-expanded={sidebarOpen}
        >
          <Icon name={sidebarOpen ? 'x' : 'menu'} size={16} />
        </button>
        <div className={styles.brand}>
          {Brand ? <Brand /> : (
            <img
              src="/pinpoint.png"
              alt="Pinpoint"
              className={styles.brandLogo}
            />
          )}
        </div>

        {/* searchAndThread wraps the search bar + the Thread pill in one
            container that occupies exactly the message-list column width
            (--msglist-w, default 520px). The inner .searchWrap flex:1's
            to fill the remaining space when Thread is rendered, or takes
            the full 520px when nothing is selected. Result: Thread sits
            on the same row as the search input, visually inside the
            middle column rather than spilling into the reading pane. */}
        <div className={styles.searchAndThread}>
        <div className={styles.searchWrap}>
          {!searching && (
            <span className={styles.searchIcon}><Icon name="search" size={14} /></span>
          )}
          {searching && <span className={styles.searchSpinner}>⟳</span>}
          <input
            className={`${styles.searchInput} ${searchQuery ? styles.searchInputActive : ''}`}
            placeholder="Search mail — from, subject, customer…"
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
          />
          {/* Inline "Include sent items" toggle — only visible while a
              query is active. Sits between the input and the clear ✕,
              right side of the search bar. The earlier funnel-icon +
              popover proved unnecessary; a plain checkbox in the bar
              is what the user actually wanted. */}
          {searchQuery && !isOutgoingFolder(selectedFolder) && (
            <label
              className={styles.searchSentToggle}
              title="Untick to hide your own sent emails from the results"
              onMouseDown={e => e.preventDefault()}
            >
              Include sent items
              <input
                type="checkbox"
                checked={!excludeSent}
                onChange={e => {
                  const next = !e.target.checked;
                  setExcludeSent(next);
                  handleSearch(searchQuery, { excludeSent: next });
                }}
              />
            </label>
          )}
          {searchQuery && (
            <button className={styles.searchClear} onClick={() => handleSearch('')}>✕</button>
          )}
          {searchFocused && !searchQuery && searchHistory.length > 0 && (
            <div className={styles.searchHistory} onMouseDown={e => e.preventDefault()}>
              <div className={styles.searchHistoryHead}>
                <span>Recent searches</span>
                <button className={styles.searchHistoryClear} onClick={clearSearchHistory}>
                  Clear
                </button>
              </div>
              {searchHistory.map((q, i) => (
                <button
                  key={i}
                  className={styles.searchHistoryItem}
                  onClick={() => handleSearch(q)}
                >
                  <Icon name="search" size={11} />
                  <span className={styles.searchHistoryText}>{q}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Thread pill — sits inside .searchAndThread so it lines up at
            the right edge of the search bar within the middle column's
            footprint instead of bleeding into the reading pane. Always
            visible when an email is open; loads every email sharing the
            open message's subject + counterpart domain OR its Microsoft
            conversation_id (one indexed SELECT against mail_search_index).
            Disabled while a fetch is in flight. */}
        {selectedMsg && (
          <button
            type="button"
            onClick={() => loadThread(selectedMsg)}
            disabled={threadLoading}
            className={`${styles.rulesBtn} ${styles.desktopOnly}`}
            style={{ marginLeft: 8, flex: '0 0 auto' }}
            title="Load every email about this subject from the same customer / Outlook thread"
          >
            <Icon name="thread" size={13} stroke={2.2} />
            {threadLoading ? 'Loading…' : 'Thread'}
          </button>
        )}
        </div>

        {/* "Add Sample" shows whenever the email in the reading pane is
            labelled Samples, regardless of which sidebar filter got you
            there. Sits immediately after the search bar so it reads as a
            context-action on the current email, not part of the global
            settings cluster on the right. Uses the shared .rulesBtn
            pill style — see CLAUDE.md "UI conventions → top-bar pill
            buttons". Hidden on mobile (≤768px): on the go, Add Sample
            is admin work that doesn't fit the small-screen email-only
            scope. */}
        {selectedMsg && (selectedMsg.label === 'Samples' || labelMap[selectedMsg.id] === 'Samples') && (
          <button
            className={`${styles.rulesBtn} ${styles.desktopOnly}`}
            style={{ marginLeft: 8 }}
            onClick={() => startSample(selectedMsg)}
            title="Add a sample to OnPoint for the customer this email is from"
          >
            <Icon name="plus" size={13} stroke={2.2} /> Add Sample
          </button>
        )}

        {/* View Order — opens OnPoint's order page in a new tab when the
            email's subject references a known order or customer-order
            reference. Visibility is driven by orderLookupMap (populated
            by the effect above); the button appears only after the
            lookup returns a hit, so it never flickers in then out. */}
        {selectedMsg && orderLookupMap[selectedMsg.id]?.orderNumber && (
          <button
            type="button"
            onClick={() => {
              const hit = orderLookupMap[selectedMsg.id];
              setOrderDrawerMatches(Array.isArray(hit?.matches) ? hit.matches : []);
              setOrderDrawerNumber(hit.orderNumber);
            }}
            className={`${styles.rulesBtn} ${styles.desktopOnly}`}
            style={{ marginLeft: 8 }}
            title={`View OnPoint order ${orderLookupMap[selectedMsg.id].orderNumber}`}
          >
            <Icon name="folder" size={13} stroke={2.2} /> View Order
          </button>
        )}

        {/* Open Quote — when the subject is "<anything> Factory <5–6 digits>"
            the digits are an OnPoint cost-sheet (quote) id, not an order.
            Derive the link directly from the subject; no DB lookup needed.
            Order-lookup deliberately excludes Factory-prefixed numbers
            (orders.js → NEGATIVE_PRECEDING) so this button is the only
            one that fires for these subjects.

            Fallback path: the bot injects a hidden `[onpoint:cs:<id>]`
            marker into the body of every cost-sheet email it sends, so
            customer replies (which quote the original) carry the
            reference even when the customer drops the "Factory <id>"
            tail from the subject. */}
        {(() => {
          let quoteId = null;
          const subj = selectedMsg?.subject || '';
          const subjHit = subj.match(/\bfactory\s+(\d{5,6})\b/i);
          if (subjHit) quoteId = subjHit[1];
          // OnPoint sometimes ships cost-sheet emails with subjects like
          // "Sourcing Quote - Plush Keyring - 39829" (no "Factory"
          // prefix). The trailing number is still the cost-sheet id, so
          // pick it up when the subject opens with "Sourcing Quote".
          if (!quoteId) {
            const sourcingHit = subj.match(/^\s*(?:re:\s*|fwd?:\s*)*sourcing\s+quote\b.*?(\d{5,6})\s*$/i);
            if (sourcingHit) quoteId = sourcingHit[1];
          }
          // Supplier cost-sheet sends: "Cost Sheet 44299 — Metal Keyring".
          // Optional "#" accepted for emails sent before OnPoint dropped it
          // from the default subject. orders.js suppresses View Order for
          // the same shape, so this pill is the only one that fires.
          if (!quoteId) {
            const csHit = subj.match(/^\s*(?:re:\s*|fwd?:\s*)*cost\s*sheet\s*#?\s*(\d{4,6})\b/i);
            if (csHit) quoteId = csHit[1];
          }
          if (!quoteId) {
            const body = selectedMsg?.bodyHtml || selectedMsg?.bodyText || '';
            const bodyHit = body.match(/\[onpoint:cs:(\d+)\]/i);
            if (bodyHit) quoteId = bodyHit[1];
          }
          if (!quoteId) return null;
          return (
            <button
              type="button"
              onClick={() => setCostSheetOpenId(quoteId)}
              className={`${styles.rulesBtn} ${styles.desktopOnly}`}
              style={{ marginLeft: 8 }}
              title={`Open cost sheet ${quoteId}`}
            >
              <Icon name="folder" size={13} stroke={2.2} /> Open Quote
            </button>
          );
        })()}

        {/* New-contact / new-customer / update-contact pill. Only
            renders when the currently-viewed email is from someone
            OnPoint doesn't have recorded under this exact address.
            When the sender's NAME near-matches an existing contact
            whose email has changed, it becomes "Update Contact"
            instead. Click → the matching modal (handled by ReadingPane).
            The "Existing Contact" (green tick) state is intentionally
            NOT shown — it's reassurance noise; if you recognise the
            customer in the sender row, you don't need a pill. */}
        {selectedMsg && senderInfo && senderInfo.matchType !== 'contact' &&
         !/@pinpoint\.promo$/i.test(selectedMsg.from || '') && (
          senderInfo.updateCandidate ? (
            <button
              className={`${styles.rulesBtn} ${styles.desktopOnly} ${styles.newContactBtn}`}
              style={{ marginLeft: 8 }}
              onClick={() => setUpdatingContact(true)}
              title={`${senderInfo.updateCandidate.contactName} is already in OnPoint on a different email — click to review and update`}
            >
              <Icon name="refresh" size={13} stroke={2.2} />
              Update Contact
            </button>
          ) : (
            <button
              className={`${styles.rulesBtn} ${styles.desktopOnly} ${styles.newContactBtn}`}
              style={{ marginLeft: 8 }}
              onClick={() => setAddingContact(true)}
              title={
                senderInfo.matchType === 'customer'
                  ? `New contact at ${senderInfo.customer?.customerName || 'this customer'} — click to add to OnPoint`
                  : 'Sender isn\'t in OnPoint yet — click to add'
              }
            >
              <Icon name="plus" size={13} stroke={2.2} />
              {senderInfo.matchType === 'customer' ? 'New Contact' : 'New Customer'}
            </button>
          )
        )}

        <div className={styles.topbarRight}>
          {/* Top bar is now just the search bar + context actions. Bot
              Rules, sign-out, and the per-pane "Show external content"
              toggle all live in the bottom-left account popup
              (FolderTree). Keeps chrome out of the way for power
              users who already know where their tools are. */}
          {/* Body-load Debug (hidden on /V2). Click when a preview white-screens to see why:
              client fetch time, server/Graph time (Server-Timing), HTTP
              status, and any error/timeout. Badges red when the most recent
              load failed or is still pending. */}
          {!isV2() && (() => {
            const latest = bodyDiag[0];
            const bad = latest && (latest.phase === 'error' || latest.phase === 'timeout' || latest.phase === 'pending');
            return (
              <button
                className={styles.rulesBtn}
                style={{ marginLeft: 8, ...(bad ? { color: '#b91c1c', borderColor: '#fca5a5' } : {}) }}
                onClick={() => setShowBodyDebug(true)}
                title="Diagnose slow / blank email previews"
              >
                <Icon name="bug" size={13} stroke={2} />
                Debug{bad ? ' !' : ''}
              </button>
            );
          })()}
          {sendErrorCount > 0 && (
            <button
              className={styles.rulesBtn}
              style={{ marginLeft: 8 }}
              onClick={() => setShowSendErrors(true)}
              title={`${sendErrorCount} send failure${sendErrorCount === 1 ? '' : 's'} logged — click to view`}
            >
              <Icon name="junk" size={13} stroke={2.2} />
              {sendErrorCount} log{sendErrorCount === 1 ? '' : 's'}
            </button>
          )}
          {appErrorCount > 0 && (
            <button
              className={styles.rulesBtn}
              style={{ marginLeft: 8, color: '#b91c1c', borderColor: '#fca5a5' }}
              onClick={() => setShowAppErrors(true)}
              title={`${appErrorCount} distinct error${appErrorCount === 1 ? '' : 's'} logged across all users — click to view`}
            >
              <Icon name="bug" size={13} stroke={2.2} />
              {appErrorCount} error{appErrorCount === 1 ? '' : 's'}
            </button>
          )}
          {/* Per-email actions in the top bar — only render when an
              email is selected. ViewAccountButton internally probes
              /api/onpoint/customer-match and renders nothing if the
              sender isn't a known OnPoint customer (or is an
              internal @pinpoint.promo address). UploadInvoiceButton
              is gated on the message being labelled 'Accounts'. */}
          {selectedMsg?.from && !/@pinpoint\.promo$/i.test(String(selectedMsg.from)) && (
            <span style={{ marginLeft: 8 }}>
              <ViewAccountButton
                from={selectedMsg.from}
                onOpenAccount={(email) => setAccountDrawerEmail(email)}
                className={styles.rulesBtn}
              />
            </span>
          )}
          {selectedMsg && (selectedMsg.label || labelMap[selectedMsg.id]) === 'Accounts' && (
            <span style={{ marginLeft: 8 }}>
              <UploadInvoiceButton
                message={selectedMsg}
                pdfs={(selectedMsg.attachments || []).filter(a =>
                  /pdf/i.test(a.contentType || '') || /\.pdf$/i.test(a.name || '')
                )}
                className={styles.rulesBtn}
                onRequestUpload={src => setInvoiceUploadSource(src)}
              />
            </span>
          )}
          {/* Build Order button — gated on the 'Orders' label, same pattern
              as UploadInvoiceButton above. Opens the BuildOrderDrawer
              (Phase 1.2) which lets the user select related emails +
              attachments, rename them, and send the package to OnPoint
              as a pending raise-order. Drawer state lives at App level
              so it survives navigation between emails while open. */}
          {selectedMsg && (selectedMsg.label || labelMap[selectedMsg.id]) === 'Orders' && (
            <span style={{ marginLeft: 8 }}>
              <BuildOrderButton
                className={styles.rulesBtn}
                onClick={() => openBuildOrder(selectedMsg)}
              />
            </span>
          )}
        </div>
      </header>

      {showBodyDebug && (
        <BodyLoadDebugModal
          records={bodyDiag}
          onClose={() => setShowBodyDebug(false)}
          onClear={() => setBodyDiag([])}
          onRetry={selectedMsg ? () => hydrateBody(selectedMsg, { reason: 'retry' }) : null}
        />
      )}

      {showAppErrors && (
        <AppErrorsModal
          onClose={() => {
            setShowAppErrors(false);
            fetch('/api/app-errors/count').then(r => r.json()).then(d => setAppErrorCount(d.count || 0)).catch(() => {});
          }}
        />
      )}

      {showSendErrors && (
        <SendErrorsModal
          onClose={() => { setShowSendErrors(false); /* recount on close */
            fetch('/api/send-failures').then(r => r.json()).then(d => setSendErrorCount((d.failures || []).length)).catch(() => {});
          }}
        />
      )}

      {replyOpenersDialog && (
        <ReplyOpenersDialog
          onClose={() => setReplyOpenersDialog(null)}
          openers={replyOpeners}
          onChange={setReplyOpeners}
          seedPhrase={replyOpenersDialog.seed}
          message={selectedMsg}
        />
      )}

      {showAddLabel && (
        <AddLabelDialog
          onClose={() => setShowAddLabel(false)}
          onCreated={() => setLabelsRefreshKey(k => k + 1)}
        />
      )}

      {/* AI Quote panel — always-mounted fixed overlay covering the reading
          pane (same keep-mounted pattern as BuildOrderDrawer). When minimised
          it goes display:none so the draft edits survive, and the "Quote · …"
          tab in the strip brings it back. */}
      {aiQuoteMsg && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          left: SIDEBAR_W + listW + RESIZER_W,
          zIndex: 9300, background: '#fff',
          borderLeft: '1px solid #e5e7ef',
          boxShadow: '-8px 0 24px rgba(15,26,74,0.18)',
          display: aiQuoteMinimised ? 'none' : 'block',
        }}>
          <ErrorBoundary label="AiQuotePanel" resetKey={aiQuoteMsg.id}>
            <AiQuotePanel
              message={aiQuoteMsg}
              onMinimise={() => setAiQuoteMinimised(true)}
              onClose={() => { setAiQuoteMsg(null); setAiQuoteMinimised(false); }}
              onSent={() => {
                setAiQuoteMsg(null);
                setAiQuoteMinimised(false);
                // Refresh folder counts so the just-handled email moves
                // between buckets without a manual reload.
                fetch('/api/mail/folders', { cache: 'no-store' })
                  .then(r => r.json())
                  .then(d => Array.isArray(d) && setFolders(d))
                  .catch(() => {});
              }}
            />
          </ErrorBoundary>
        </div>
      )}

      {mountDrawers && (
      <ErrorBoundary label="Cost sheet drawer" resetKey={costSheetOpenId}>
        <CostSheetDrawer
          open={!!costSheetOpenId}
          sheetId={costSheetOpenId}
          onClose={() => setCostSheetOpenId(null)}
        />
      </ErrorBoundary>
      )}

      {/* BuildOrderDrawer — one MOUNTED instance per open tab. Only
          the activeBuildOrderKey one is VISIBLE; the rest are
          display:none so their state survives the user flipping
          between tabs in the draft strip. Discard from the tab
          unmounts the entry entirely. */}
      {buildOrders.map(order => (
        <ErrorBoundary key={order.key} label={`Build Order drawer ${order.key}`} resetKey={order.key}>
          <BuildOrderDrawer
            anchorMessageId={order.anchorMessageId}
            anchorFallback={order.anchorFallback}
            // Drawer covers exactly the reading pane — wide, but the email
            // list (sidebar + list + resizer) stays visible for ticking.
            paneLeftPx={SIDEBAR_W + listW + RESIZER_W}
            isVisible={activeBuildOrderKey === order.key}
            multiTypeEnabled={!!currentUser?.buildOrderMultiTypeEnabled}
            onClose={closeActiveBuildOrder}
            onDiscard={() => discardBuildOrder(order.key)}
            // Selection state lifted to App so MessageList ticks and
            // drawer ticks share one source of truth per order.
            tickedEmails={order.tickedEmails || {}}
            onToggleEmailTick={(messageId, name = null) => {
              setBuildOrders(prev => prev.map(o => {
                if (o.key !== order.key) return o;
                const current = o.tickedEmails || {};
                if (current[messageId]) {
                  const { [messageId]: _drop, ...rest } = current;
                  return { ...o, tickedEmails: rest, updatedAt: Date.now() };
                }
                return {
                  ...o,
                  tickedEmails: {
                    ...current,
                    [messageId]: { name: name || '', tickedAt: Date.now() },
                  },
                  updatedAt: Date.now(),
                };
              }));
            }}
            onRenameEmailTick={(messageId, newName) => {
              setBuildOrders(prev => prev.map(o => {
                if (o.key !== order.key) return o;
                const current = o.tickedEmails || {};
                if (!current[messageId]) return o;
                return {
                  ...o,
                  tickedEmails: {
                    ...current,
                    [messageId]: { ...current[messageId], name: newName },
                  },
                  updatedAt: Date.now(),
                };
              }));
            }}
          />
        </ErrorBoundary>
      ))}

      {mountDrawers && (
      <ErrorBoundary label="Order drawer" resetKey={orderDrawerNumber}>
        <OrderDrawer
          open={!!orderDrawerNumber}
          orderNumber={orderDrawerNumber}
          matches={orderDrawerMatches}
          onSwitchOrder={(n) => setOrderDrawerNumber(n)}
          onClose={() => { setOrderDrawerNumber(null); setOrderDrawerMatches([]); setTopDrawer(null); }}
          zIndex={topDrawer === 'order' ? 9301 : 9200}
          onRaise={() => setTopDrawer('order')}
          onOpenAccount={({ customerId, email } = {}) => {
            // Keep the OrderDrawer mounted underneath so closing the
            // account drawer (back / X) returns the user to the order
            // they came from. Account drawer rises to the top by
            // default; clicking the visible portion of the order
            // drawer behind swaps the stack via onMouseDownCapture →
            // onRaise → setTopDrawer('order').
            setTopDrawer('account');
            if (customerId) {
              setAccountDrawerEmail(null);
              setAccountDrawerCustomerId(customerId);
            } else if (email) {
              setAccountDrawerCustomerId(null);
              setAccountDrawerEmail(email);
            }
          }}
        />
      </ErrorBoundary>
      )}

      {mountDrawers && (
      <ErrorBoundary label="Account drawer" resetKey={accountDrawerEmail || `cid-${accountDrawerCustomerId}`}>
      <AccountDrawer
        open={!!(accountDrawerEmail || accountDrawerCustomerId)}
        email={accountDrawerEmail}
        customerId={accountDrawerCustomerId}
        zIndex={topDrawer === 'order' ? 9100 : 9301}
        onRaise={() => setTopDrawer('account')}
        onClose={() => { setAccountDrawerEmail(null); setAccountDrawerCustomerId(null); setTopDrawer(null); }}
        onAddSample={() => setAddingSample(true)}
        onOpenOrder={n => { setTopDrawer('order'); setOrderDrawerNumber(n); }}
        onOpenCostSheet={id => setCostSheetOpenId(id)}
        onEditSample={async (sampleId) => {
          // Pull the sample's current state from OnPoint, drop it into
          // the AddSampleModal as `initial` (the same prop the
          // resume-from-tab path uses), and open the drawer in edit
          // mode. AddSampleModal detects initial.editSampleId and
          // routes its submit to PUT /api/onpoint/samples/:id.
          try {
            const r = await fetch(`/api/onpoint/samples/${encodeURIComponent(sampleId)}`);
            const d = await r.json();
            if (!r.ok || !d?.sample) throw new Error(d.error || `HTTP ${r.status}`);
            const key = `edit-sample-${sampleId}`;
            saveSample(key, { ...d.sample, sourceMsgId: null, sourceFrom: '', sourceSubject: '', createdAt: Date.now() });
            setActiveSampleKey(key);
            setAddingSample(true);
            // Close the account drawer so the sample drawer is visible.
            setAccountDrawerEmail(null); setAccountDrawerCustomerId(null);
          } catch (err) {
            // eslint-disable-next-line no-alert
            alert('Could not load sample for editing: ' + err.message);
          }
        }}
        onEmailInvoice={async (inv, customer) => {
          // Fetch the invoice PDF, base64-encode, hand to ComposeModal as
          // a pre-attached file so the user can edit subject/body and send
          // through the existing Graph send pipeline.
          try {
            const r = await fetch(`/api/xero/invoice/${encodeURIComponent(inv.id)}/pdf`);
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const buf = await blob.arrayBuffer();
            const bin = new Uint8Array(buf);
            let s = '';
            for (let i = 0; i < bin.byteLength; i++) s += String.fromCharCode(bin[i]);
            const contentBytes = btoa(s);
            const fileName = `${inv.number || inv.id}.pdf`;
            const contactEmail = customer?.accountEmail || customer?.email || '';
            const contactName  = customer?.accountContact || customer?.name || '';
            const greet = (contactName || 'there').split(/\s+/)[0];
            setComposeDefaults({
              mode: 'new',
              to: contactEmail,
              subject: `Invoice ${inv.number || inv.id}`,
              body: `Hi ${greet},\n\nPlease find attached invoice ${inv.number || inv.id}.\n\nThanks`,
              attachments: [{
                name: fileName,
                contentType: 'application/pdf',
                size: blob.size,
                contentBytes,
              }],
            });
            setComposing(true);
            // ComposeModal renders inside the email view's reading-pane
            // overlay slot — it sits BEHIND the AccountDrawer aside.
            // Without closing the drawer the user clicks Email and sees
            // nothing change. Drop them back to the email view so the
            // pre-filled compose is actually visible.
            setAccountDrawerEmail(null); setAccountDrawerCustomerId(null);
          } catch (err) {
            // eslint-disable-next-line no-alert
            alert(`Couldn't load invoice PDF: ${err.message}`);
          }
        }}
      />
      </ErrorBoundary>
      )}

      {mountDrawers && (
      <ErrorBoundary label="Invoice upload" resetKey={invoiceUploadSource && JSON.stringify(invoiceUploadSource)}>
        <InvoiceUploadDrawer
          open={!!invoiceUploadSource}
          source={invoiceUploadSource}
          onClose={() => setInvoiceUploadSource(null)}
        />
      </ErrorBoundary>
      )}

      {/* AddSample drawer — slides in from the right as an overlay
          on top of the reading pane. The pane stays visible underneath
          so the user can still see the email they're filing a sample
          for. Click the backdrop or press Esc to preserve-close (snapshot
          to a tab in the bottom strip). The two-column tab strip in
          ReadingPane drives "switch back to email" / "switch to draft"
          via the existing requestClose() handle. */}
      <div
        className={`${styles.sampleDrawerBackdrop} ${addingSample ? styles.sampleDrawerBackdropOpen : ''}`}
        onClick={() => sampleRef.current?.requestClose?.()}
        aria-hidden={!addingSample}
      />
      <aside
        className={`${styles.sampleDrawer} ${addingSample ? styles.sampleDrawerOpen : ''}`}
        aria-hidden={!addingSample}
      >
        {addingSample && (
          <ErrorBoundary label="Add sample" resetKey={activeSampleKey}>
          <AddSampleModal
            ref={sampleRef}
            message={selectedMsg}
            initial={activeSampleKey ? samples[activeSampleKey] : null}
            onClose={(info) => {
              setAddingSample(false);
              const key = activeSampleKey || sampleKeyFor(selectedMsg?.id);
              if (info?.created || info?.discard) {
                discardSample(key);
              } else if (info?.snapshot) {
                saveSample(key, {
                  ...info.snapshot,
                  sourceMsgId:   selectedMsg?.id || null,
                  sourceFrom:    selectedMsg?.from || '',
                  sourceSubject: selectedMsg?.subject || '',
                  createdAt:     samples[key]?.createdAt || Date.now(),
                });
              }
              setActiveSampleKey(null);
            }}
            onCustomerResolved={(cust) => {
              const key = activeSampleKey || sampleKeyFor(selectedMsg?.id);
              setSamples(prev => {
                const existing = prev[key] || {};
                return {
                  ...prev,
                  [key]: {
                    ...existing,
                    customer:     { id: cust.id, customerName: cust.customerName },
                    customerName: cust.customerName,
                    updatedAt:    Date.now(),
                  },
                };
              });
            }}
          />
          </ErrorBoundary>
        )}
      </aside>

      {/* 3-panel grid on desktop; single-pane + drawer on mobile. The
          data-pane / data-sidebar attributes drive the @media (max-width:768px)
          rules in App.module.css. */}
      {sidebarOpen && (
        <div
          className={styles.mobileBackdrop}
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}
      <div
        className={styles.main}
        data-pane={mobilePane}
        data-sidebar={sidebarOpen ? 'open' : 'closed'}
      >
        <FolderTree
          folders={effectiveFolders}
          archiveFolders={archiveFolders}
          loading={loadingFolders}
          selected={selectedFolder}
          onSelect={f => {
            // Clicking a folder in the sidebar is a "take me to a fresh
            // view of this folder" gesture. Reset any active filter so
            // Inbox / Sent / etc. open in their default all-messages view.
            // Also exit search mode if a query was active — otherwise
            // clicking Inbox while searching looks like a no-op
            // (search results stay on screen, the search bar still
            // shows the old query, and selectedFolder doesn't change
            // so the load-on-folder-change effect never fires).
            const wasSearching = !!searchQuery;
            const sameFolder   = f?.id === selectedFolder?.id;
            if (wasSearching) {
              setSearchQuery('');
              setSearchPreview(false);
              setResolvedContacts([]);
              setSearching(false);
              setExcludeSent(false); // every new search starts "Include sent items" TICKED
              clearTimeout(searchTimer.current);
              if (searchAbortRef.current) {
                try { searchAbortRef.current.abort(); } catch {}
                searchAbortRef.current = null;
              }
              searchQueryRef.current = '';
            }
            setSelectedFolder(f); setSelectedCustomer(null); setSelectedLabel(null);
            setMsgFilter('all');
            setSidebarOpen(false); setMobilePane('list');
            // Clicking the same folder you were in before searching
            // doesn't change selectedFolder, so the useEffect that
            // loads on folder change won't fire — call loadMessages
            // explicitly to wipe the search results.
            if (wasSearching && sameFolder) loadMessages(f);
          }}
          onCompose={() => {
            setComposeDefaults({ mode: 'new' }); setActiveDraftKey(null); setComposing(true);
            setSidebarOpen(false);
          }}
          customers={customers}
          loadingCustomers={loadingCustomers}
          /* Right-click on Junk / Deleted Items in the sidebar opens a
             tiny menu with "Empty Folder". Wires straight into the
             same confirm + delete flow the message-list-header
             button uses. */
          onEmptyFolder={handleEmptyFolder}
          selectedCustomer={selectedCustomer}
          onSelectCustomer={c => {
            setSelectedCustomer(c); setSelectedFolder(null); setSelectedLabel(null);
            setMsgFilter('all');
            loadCustomerMessages(c);
            setSidebarOpen(false); setMobilePane('list');
          }}
          selectedLabel={selectedLabel}
          onAddLabel={() => setShowAddLabel(true)}
          labelsRefreshKey={labelsRefreshKey}
          customLabels={customLabels}
          flaggedMessages={flaggedList}
          flaggedMore={flaggedMore}
          flaggedLoaded={flaggedLoaded}
          selectedMsgId={selectedMsg?.id || null}
          onOpenFlagged={handleOpenFlagged}
          onUnflagFlagged={m => handleFlag(m.id, false)}
          onDoneFlagged={m => handleMarkDone(m.id, 'set')}
          onSelectLabel={label => {
            setSelectedLabel(prev => prev === label ? null : label);
            setMsgFilter('all');
            // Labels filter whatever's currently loaded (Inbox by default).
            // If nothing is selected yet, default to Inbox.
            if (!selectedFolder && !selectedCustomer) {
              const inbox = folders.find(f => (f.displayName || '').toLowerCase().replace(/\s/g, '') === 'inbox');
              if (inbox) setSelectedFolder(inbox);
            }
            setSidebarOpen(false); setMobilePane('list');
          }}
          labelCounts={(() => {
            // Count every message per label that still needs attention:
            // unread OR flagged-but-not-completed. Once an email is both
            // read AND flag-cleared (or never flagged), it drops out.
            // This way a flagged-for-follow-up email stays on the count
            // after you've read it — surfaces that it still needs doing.
            const c = {};
            for (const m of decoratedMessages) {
              if (!m.label) continue;
              const pending = !m.isRead || (m.flagged && !m.completed);
              if (pending) c[m.label] = (c[m.label] || 0) + 1;
            }
            return c;
          })()}
          // Account popup at the bottom of the drawer. Used to be a
          // single Sign-out row; now it opens a menu hosting Bot
          // Rules, the per-pane "Show external content" toggle, and
          // Sign out — same chrome that was previously in the top
          // right, consolidated into one place.
          currentUser={currentUser}
          onLogout={onLogout}
          onOpenRules={() => setRulesOpen(true)}
          showExternalContent={showExternalContent}
          onToggleExternalContent={() => setShowExternalContent(v => !v)}
          onOpenAnotherMailbox={() => setOpenMailboxDlg(true)}
        />

        <div
          className={`${styles.resizer} ${dragging ? styles.resizerActive : ''}`}
          onMouseDown={() => setDragging(true)}
          onDoubleClick={() => { setListW(520); persistListW(520); }}
          title="Drag to resize (double-click to reset)"
        />
        {/* Transparent full-viewport shield while resizing. Sits in front
            of the email-body iframe so pointer events stay in our window
            (iframes swallow mousemove/up). Without this the drag can
            "stick" — cursor stays col-resize and clicks no longer land
            on buttons. See useEffect on `dragging` for the matching
            event listeners. */}
        {dragging && <div className={styles.dragShield} aria-hidden="true" />}

        {/* Boundary around the list: browser extensions (translate /
            grammar checkers) mutate row DOM under React's feet, and the
            next poll-tick reconcile then throws "insertBefore … is not
            a child of this node" — which used to unmount the WHOLE app
            (top-level boundary, full-page "Something went wrong").
            Scoped here, the crash shows an inline "Try again" that
            remounts just the list with clean DOM; navigating to another
            folder / search / customer auto-recovers via resetKey. */}
        <ErrorBoundary
          label="Message list"
          resetKey={`${selectedFolder?.id || ''}·${searchQuery || ''}·${selectedCustomer?.id || selectedCustomer?.name || ''}·${threadInfo ? 'thread' : ''}`}
        >
        <MessageList
          messages={decoratedMessages}
          /* When the active search came from the local FTS index,
             surface a "Search older mail" footer button so the user
             can extend into Graph for anything beyond the LOCAL_INDEX_DAYS
             window. Hidden once they've extended (source flips to
             'graph') so the button doesn't loop. */
          onSearchOlder={searchQuery && searchSource === 'local' ? searchOlder : null}
          searchingOlder={searchingOlder}
          /* Only show the full-pane loader on a fresh folder/customer load.
             Search keeps the local-cache rows visible and surfaces a tiny
             spinner in the search chrome instead — see handleSearch. */
          loading={loadingMsgs}
          selectedId={selectedMsg?.id}
          /* Thread-mode banner — slotted in via the existing `headerSlot`
             prop on MessageList. Shows what we're filtering by and gives
             the user a one-click escape back to the folder view. Folder
             load / new search clears threadInfo (see loadMessages /
             handleSearch) so this never lingers. */
          headerSlot={threadInfo ? (
            <div style={{
              padding: '6px 10px', background: '#f6f9ff',
              borderBottom: '1px solid #d9e3f5', fontSize: 12,
              color: '#1f2937', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Icon name="thread" size={12} stroke={2} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {threadInfo.unavailable
                  ? <>No related emails found <span style={{ color: '#5b6678' }}>(neither the subject nor the conversation matched any indexed mail in the last {LOCAL_INDEX_DAYS} days)</span></>
                  : <>
                      Thread · <strong>{threadInfo.counterpartDomain || '—'}</strong> ·{' '}
                      <span style={{ color: '#5b6678' }}>{threadInfo.messageCount} email{threadInfo.messageCount === 1 ? '' : 's'}</span>
                      {threadInfo.distinctDomains > 3 && (
                        // Cross-customer warning: a generic subject
                        // ("Quote", "Re: Order Confirmation") pulled
                        // matches from N+ unrelated customers. The
                        // user might be looking at one customer's
                        // email but the list mixes others — make it
                        // visible before they reply on the wrong row.
                        <span style={{
                          marginLeft: 10, padding: '1px 6px',
                          borderRadius: 4, background: '#fef3c7',
                          color: '#92400e', fontSize: 11, fontWeight: 600,
                        }} title={`This thread's subject is generic enough to match ${threadInfo.distinctDomains} unrelated sender domains. Double-check the From column before replying.`}>
                          ⚠ spans {threadInfo.distinctDomains} customers
                        </span>
                      )}
                    </>}
              </span>
              <button
                type="button"
                onClick={() => {
                  setThreadInfo(null);
                  // Restore the user's pre-thread filter (snapshotted
                  // in loadThread) so Flagged / Unread / etc. don't
                  // silently get reset to 'all' just because they
                  // dipped into a thread. loadMessages below will fire
                  // its own setMsgFilter('all') only if it sees an
                  // active filter — but we want to set it BEFORE
                  // loadMessages reads msgFilter so the first folder
                  // fetch uses the right value.
                  if (priorMsgFilterRef.current != null) {
                    setMsgFilter(priorMsgFilterRef.current);
                    priorMsgFilterRef.current = null;
                  }
                  if (selectedCustomer) loadCustomerMessages(selectedCustomer);
                  else if (selectedFolder) loadMessages(selectedFolder);
                }}
                style={{
                  marginLeft: 'auto', height: 22, padding: '0 8px',
                  borderRadius: 4, border: '1px solid #c5cee0', background: '#fff',
                  color: '#0f1a4a', cursor: 'pointer', fontSize: 11,
                }}
                title="Exit thread view and reload the current folder"
              >Clear</button>
            </div>
          ) : (searchPreview ? (
            // Progressive-search hint: the local IndexedDB scan has painted
            // instant results while the debounced server/Graph search is still
            // in flight. Distinct from a hard replace — tells the user more is
            // coming rather than that these are the final results. Cleared when
            // the server response lands (or the search errors).
            <div style={{
              padding: '6px 10px', background: '#fffbeb',
              borderBottom: '1px solid #fde68a', fontSize: 12,
              color: '#92400e', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span>Showing local results — refining…</span>
            </div>
          ) : null)}
          folderName={
            threadInfo
              ? `Thread: ${(threadInfo.subject || '').replace(/^((re|fw[d]?):\s*)+/i, '') || '(no subject)'}`
              : searchQuery
              ? `Search: "${searchQuery}"`
              : [
                  selectedCustomer ? selectedCustomer.name : null,
                  selectedLabel ? `Label: ${selectedLabel}` : null,
                  !selectedCustomer && !selectedLabel ? (selectedFolder?.displayName || '') : null,
                ].filter(Boolean).join(' · ')
          }
          showTo={!selectedCustomer && /sent|outbox|drafts/i.test(selectedFolder?.displayName || '')}
          /* mixedFolders kicks in when the list spans both inbound +
             outbound mail and the user can't infer direction from folder
             context: search results (across all folders), and customer
             threads (every email between us and that customer). Each
             row then renders a Sent pill + "To: …" prefix on outbound
             entries so the eye can scan replies vs new messages at a
             glance. */
          mixedFolders={!!searchQuery || !!selectedCustomer}
          onSelect={handleSelectMsg}
          onPrefetch={prefetchBody}
          onContext={openContext}
          onFlag={handleFlagCycle}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          disableAutoLoad={!!searchQuery}
          todayCount={todayCount}
          mailbox={mailbox}
          // While in thread mode, suppress the sidebar's label filter
          // so MessageList doesn't trim the thread results down to
          // just rows that happen to carry the currently-selected
          // label. The user clicked Thread to see EVERY email about
          // this topic — they don't want the Orders-label filter
          // shadowing it. selectedLabel state itself is left alone
          // so clicking Clear on the thread banner restores the
          // label view they were in. (loadThread already resets the
          // dropdown msgFilter for the same reason; this is the
          // sibling fix for the sidebar.)
          labelFilter={threadInfo ? null : selectedLabel}
          onDelete={handleDelete}
          // Mobile-only swipe gestures: right → mark unread, left → delete.
          // We reuse the existing handlers (no new logic), so swipe is a
          // shortcut for actions that are also reachable via context menu.
          onSwipeUnread={handleMarkUnread}
          onSwipeDelete={handleDelete}
          filter={msgFilter}
          onFilterChange={setMsgFilter}
          onEmptyFolder={
            selectedFolder && /junk/i.test(selectedFolder.displayName || '')
              ? handleEmptyFolder
              : null
          }
          // Only show "Sweep orphans" when the user is currently
          // viewing the Drafts folder. Match by displayName so it
          // works for both Graph's well-known "Drafts" and any
          // localised variant ("Brouillons", etc.) that contains it.
          onSweepOrphanDrafts={
            selectedFolder && /draft/i.test(selectedFolder.displayName || '')
              ? () => setOrphanModalOpen(true)
              : null
          }
          // Build Order — when a Build Order tab is active, MessageList
          // renders a checkbox column + light-orange tint on ticked
          // rows + the user-chosen name replacing the subject. Hidden
          // entirely when no order is active.
          buildOrderActive={!!activeBuildOrderKey}
          buildOrderTickedEmails={activeOrder?.tickedEmails || {}}
          onToggleBuildOrderTick={toggleActiveBuildOrderTick}
          onRenameBuildOrderTick={renameActiveBuildOrderTick}
        />
        </ErrorBoundary>

        {/* Same containment for the reading pane — the other big
            unguarded shell region; renders per-email content, so a
            crash resets on the next message open via resetKey. */}
        <ErrorBoundary label="Reading pane" resetKey={selectedMsg?.id || 'none'}>
        <ReadingPane
          message={selectedMsg}
          /* senderInfo is owned by App.js (same lookup powers the
             topbar's New Contact / New Customer pill). ReadingPane
             reads it for the Add-to-OnPoint affordance and the
             AddContactModal slot. */
          senderInfo={senderInfo}
          designPointEnabled={!!currentUser?.designPointEnabled}
          showExternalContent={showExternalContent}
          addingContact={addingContact}
          onCloseAddContact={() => setAddingContact(false)}
          onContactAdded={() => { setAddingContact(false); refreshSenderInfo(); }}
          updatingContact={updatingContact}
          onCloseUpdateContact={() => setUpdatingContact(false)}
          onContactUpdated={() => { setUpdatingContact(false); refreshSenderInfo(); }}
          onSwitchToAddContact={() => { setUpdatingContact(false); setAddingContact(true); }}
          onBack={() => setMobilePane('list')}
          onReply={msg => openReply(msg, false)}
          onReplyAll={msg => openReply(msg, true)}
          onForward={openForward}
          onManageReplyOpeners={(seed) => setReplyOpenersDialog({ seed: seed || '' })}
          onDelete={handleDelete}
          onFlag={handleFlag}
          onFlagCycle={handleFlagCycle}
          onMarkUnread={handleMarkUnread}
          onAiReply={handleAiReply}
          tenantDomain={(mailbox || '').split('@')[1] || ''}
          onReactToMessage={handleReactToMessage}
          onOpenAccount={(email) => setAccountDrawerEmail(email)}
          composeOverlay={
            // AiQuotePanel moved OUT of this slot (2026-07-14) — it now
            // lives in an always-mounted fixed overlay below, so it can
            // minimise to a tab without losing its draft state.
            //
            // Compose only — AddSampleModal lives in its own slot below
            // (addSampleSlot) so the draft + sample tab strip stays
            // visible behind it. composeOverlay positions itself absolute
            // via .backdropFull and covers the whole pane.
            composing ? (
              <ErrorBoundary label="Compose" resetKey={activeDraftKey || composeDefaults.replyToId || composeDefaults.forwardId || 'new'}>
              <ComposeModal
                ref={composeRef}
                defaults={composeDefaults}
                onDiagnostic={recordComposeDiag}
                spellcheckEnabled={!!currentUser?.spellcheckEnabled}
                onSend={async (payload) => {
                  // Optimistic: kicks off the send in the background and
                  // returns immediately so ComposeModal closes without
                  // waiting on Graph. The toast (bottom-right) reports
                  // sent / failed. On failure handleSend re-saves the
                  // draft so the user can retry.
                  handleSend(payload);
                  if (activeDraftKey) discardDraft(activeDraftKey);
                  if (composeDefaults.replyToId) {
                    discardDraft(`reply-${composeDefaults.replyToId}`);
                    discardDraft(`replyAll-${composeDefaults.replyToId}`);
                  }
                  if (composeDefaults.forwardId) discardDraft(`forward-${composeDefaults.forwardId}`);
                }}
                onClose={(info) => {
                  setComposing(false);
                  if (info?.discard && activeDraftKey) {
                    discardDraft(activeDraftKey);
                  }
                  setActiveDraftKey(null);
                  // Release focus from any contentEditable / input inside
                  // the now-unmounting compose so global shortcuts like
                  // Ctrl+D fire on the next press without the user
                  // having to click the page first. Tracks user report
                  // "Ctrl+D after sending doesn't always work".
                  try { document.activeElement?.blur && document.activeElement.blur(); } catch {}
                }}
                onSaveDraft={(snap) => {
                  const key = activeDraftKey || draftKeyFor(snap);
                  const existing = drafts[key];
                  saveDraft(key, {
                    ...snap,
                    msgSubject: existing?.msgSubject || snap.subject || (selectedMsg?.subject || ''),
                    msgFrom: existing?.msgFrom || selectedMsg?.fromName || selectedMsg?.from || '',
                    createdAt: existing?.createdAt || Date.now(),
                  });
                }}
                fullScreen
              />
              </ErrorBoundary>
            ) : null
          }
          // AddSampleModal lives at App level now (sample drawer),
          // not in the reading-pane slot. ReadingPane just keeps its
          // normal toolbar/body/quick-reply visible underneath.
          tabs={tabList}
          /* Drives the blue "you're viewing this" highlight on the
             tabs strip — exactly one of these is non-null at a time. */
          activeTabKey={
            composing ? activeDraftKey
            : addingSample ? activeSampleKey
            : activeBuildOrderKey
                ? activeBuildOrderKey
            : (aiQuoteMsg && !aiQuoteMinimised) ? `quote-${aiQuoteMsg.id}`
            : null
          }
          onOpenDraft={openDraft}
          onDiscardDraft={discardDraft}
          onOpenSample={openSample}
          onDiscardSample={discardSample}
          onOpenBuildOrder={openBuildOrderTab}
          onDiscardBuildOrder={discardBuildOrder}
          // Quote tab — click restores the minimised AI Quote panel; ×
          // closes the quote entirely (the panel is stateless server-side,
          // so reopening via AI Quote re-fetches the same approval row).
          onOpenQuote={() => setAiQuoteMinimised(false)}
          onDiscardQuote={() => { setAiQuoteMsg(null); setAiQuoteMinimised(false); }}
          // Email-tab click — asks whichever modal is open to snapshot
          // itself + close, leaving the modal's tab in the strip so the
          // user can come back. Drops the user on the email read view.
          // Both modals expose `requestClose()` via useImperativeHandle.
          onOpenEmail={() => {
            if (composing) {
              composeRef.current?.requestClose?.();
              return;
            }
            if (addingSample) {
              sampleRef.current?.requestClose?.();
            }
          }}
          // Bottom-strip action buttons. Sit on the right of the tabs
          // when AddSample is open. The modal itself has no internal
          // footer anymore — Cancel + Submit live here so the user has
          // one canonical bottom strip for "in-progress work + actions
          // on it". Buttons drive the modal via the sampleRef
          // imperative handles.
          tabBarRight={addingSample ? (
            <>
              <button
                type="button"
                onClick={() => sampleRef.current?.requestDiscard?.()}
                title="Cancel — discards the in-progress sample"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => sampleRef.current?.submit?.()}
                title="Submit the sample to OnPoint"
                style={{
                  background: 'var(--accent)',
                  borderColor: 'var(--accent)',
                  color: '#fff',
                }}
              >
                Submit
              </button>
            </>
          ) : null}
        />
        </ErrorBoundary>
      </div>

      {sendToasts.length > 0 && (
        <div className={styles.sendToastStack}>
          {sendToasts.map(t => (
            <div
              key={t.id}
              className={`${styles.sendToast} ${t.status === 'sent' ? styles.sendToast_sent : t.status === 'failed' ? styles.sendToast_failed : ''}`}
              onClick={() => {
                if (t.status === 'failed' && t.draftKey) openDraft(t.draftKey);
                dismissToast(t.id);
              }}
              title={t.status === 'failed' ? 'Click to reopen draft and retry' : ''}
            >
              {t.status === 'sending' && <span className={styles.aiToastSpinner} />}
              <span className={styles.sendToastText}>
                {t.status === 'sending' && `Sending — ${t.subject}`}
                {t.status === 'sent'    && `Sent — ${t.subject}`}
                {t.status === 'failed'  && `Failed to send — ${t.err || 'unknown error'}. Click to retry.`}
              </span>
              <button
                type="button"
                className={styles.sendToastClose}
                onClick={(e) => { e.stopPropagation(); dismissToast(t.id); }}
                aria-label="Dismiss"
              ><Icon name="x" size={11} /></button>
            </div>
          ))}
        </div>
      )}

      {aiToast && (
        <div className={styles.aiToast}>
          <span className={styles.aiToastSpinner} />
          Generating AI reply using OnPoint CRM context…
        </div>
      )}

      {emptyConfirm && (
        <ConfirmDialog
          title={`Empty "${emptyConfirm.displayName}"?`}
          message={`This will permanently delete every message in ${emptyConfirm.displayName}. This cannot be undone.`}
          onCancel={() => !emptying && setEmptyConfirm(null)}
          buttons={[
            { label: 'Cancel', variant: 'subtle', disabled: emptying, onClick: () => setEmptyConfirm(null) },
            { label: emptying ? 'Emptying…' : 'Empty folder', variant: 'danger', disabled: emptying, autoFocus: true, onClick: confirmEmptyFolder },
          ]}
        />
      )}

      {orphanModalOpen && (
        <OrphanDraftsModal
          onClose={() => setOrphanModalOpen(false)}
          onDeleted={(n) => {
            // After a successful sweep, reload the Drafts folder so
            // the deleted rows vanish from the message list
            // immediately rather than waiting on the next poll tick.
            // n=0 (user reviewed but deleted nothing) is also fine —
            // reloading is cheap and keeps state honest.
            if (n >= 0 && selectedFolder) loadMessages(selectedFolder);
          }}
        />
      )}

      {actionError && (
        <div className={styles.errToast}>
          <span>{actionError}</span>
          <button
            className={styles.errToastClose}
            onClick={() => setActionError(null)}
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          msg={ctxMenu.msg}
          onClose={() => setCtxMenu(null)}
          onReply={(m) => openReply(m, false)}
          onReplyAll={(m) => openReply(m, true)}
          onForward={openForward}
          onManageReplyOpeners={(seed) => setReplyOpenersDialog({ seed: seed || '' })}
          onRead={handleMarkRead}
          onMarkUnread={handleMarkUnread}
          onFlag={handleFlag}
          onMarkDone={handleMarkDone}
          onDelete={handleDelete}
          customLabels={customLabels}
          onSetLabel={(msg, target) => {
            if (msg.label === target) return;
            setLabelCorrection({ msg, targetLabel: target });
          }}
        />
      )}

      {labelCorrection && (
        <LabelCorrectionDialog
          message={labelCorrection.msg}
          targetLabel={labelCorrection.targetLabel}
          onClose={() => setLabelCorrection(null)}
          onSaved={({ label }) => {
            // Mirror the correction locally so the UI updates immediately
            // without waiting for the label API re-fetch.
            setLabelMap(prev => ({ ...prev, [labelCorrection.msg.id]: label }));
          }}
          onContextLabel={(serverLabel) => {
            // localStorage's labelMap is the row-pill source of truth and
            // never expires. If the server's stored label differs from
            // the cached value (rule fired later, correction made
            // elsewhere, thread inheritance shifted), update the cache so
            // the row no longer disagrees with the dialog's "WAS" pill.
            const id = labelCorrection.msg.id;
            setLabelMap(prev => (prev[id] === serverLabel ? prev : { ...prev, [id]: serverLabel }));
          }}
        />
      )}

      {rulesOpen && (
        <div className={styles.rulesOverlay} onClick={() => setRulesOpen(false)}>
          <div className={styles.rulesPanel} onClick={e => e.stopPropagation()}>
            <button
              className={styles.rulesClose}
              onClick={() => setRulesOpen(false)}
              title="Close"
            >
              <Icon name="x" size={14} />
            </button>
            <Rules />
          </div>
        </div>
      )}

      {openMailboxDlg && (
        <OpenMailboxDialog
          currentMailbox={currentUser?.mailbox || ''}
          onClose={() => setOpenMailboxDlg(false)}
        />
      )}
    </div>
  );
}
